'use client';

import { BRAND, withBase, getCountry, getCountryName, getCountryCode, getMapDefaults, getCountryBounds } from './lib/brand';
import { getHomeContent } from './lib/homeContent';
import React, { useState, useEffect, useCallback, useRef, Suspense, useMemo } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Spin } from 'antd';
import {
  EnvironmentOutlined,
  CompassOutlined,
  CarOutlined,
  DollarOutlined,
  StarFilled,
  AimOutlined,
  CloseOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Entity } from './types';
import type { TripLeg, TripStop } from './types/trip';
import type { AssistantMessage, AssistantRole } from './types/assistant';
import { tripDirectionsUrl } from './components/TripPlannerLadder';
import AddStopByPlace from './components/AddStopByPlace';
import MapToast, { mapToastLifetime } from './components/planner/MapToast';
import SuggestedAlongRoute from './components/planner/SuggestedAlongRoute';
import { placementInfo } from './utils/tripInsert';
import TripPlannerPanel, { tripSummaryLine } from './components/planner/TripPlannerPanel';
import PlannerBottomSheet from './components/planner/PlannerBottomSheet';
import { EMPTY_PEEK_HEIGHT, PEEK_HEIGHT, type SheetSnap } from './components/planner/sheetSnap';
import TripActions, { TripStatusToast, buildExportItems, corridorSearchArgs } from './components/planner/TripActions';
import { heroPhotoUrl, useStopPhotos } from './components/planner/stopPhotos';
import RoleLegend from './components/planner/RoleLegend';
import { stopDayIndices } from './utils/tripDayColors';
import StopList, { itineraryDays } from './components/planner/StopList';
import { getOptimizedEntityImage } from './utils/entityImage';
import PlannerEmptyState, { type StartPlan } from './components/planner/PlannerEmptyState';
import {
  buildStartStops,
  dedupeByPhoto,
  distanceFromStartLabel,
  hasPhotoUrl,
  isUnstartedLoop,
  nearTripPlaces,
  pickOvernightIndexes,
  routeTripFacts,
  tripPlaceCount,
} from './components/planner/plannerStart';
import { readTakenFrom, readTakenRoute, restorePreviousDraft, type TakenRoute } from './components/planner/takeTrip';
import TakenRouteCover from './components/planner/TakenRouteCover';
import TakenNotice from './components/planner/TakenNotice';
import PopularTripsPicker from './components/planner/PopularTripsPicker';
import { SaveStatusChip, TripNameMenu, signInNudgeCopy } from './components/planner/TripSummaryHeader';
import { daysNightsLabel, longDaySummary, tripStats } from './components/planner/tripStats';
import TripMeta, { VEHICLE_OPTIONS } from './components/planner/TripMeta';
import { formatKm, stopsSignature } from './components/planner/geo';
import { ROLE_BADGE, ROLE_HEX, dayHex, roleAt, stopLetter } from './components/planner/roles';
import { addCostIsHeavy, addCostLabel } from './components/planner/tripDays';
import { IconArrowLeft, IconCheck, IconChevronRight, IconList, IconMap, IconPinPlus, IconPlus, IconRoute, IconSearch, IconSparkle, IconSplit, IconTent, IconX } from './components/planner/icons';
import CampsNearStops from './components/planner/CampsNearStops';
import PickOnMapButton from './components/planner/PickOnMap';
import StopDetailSheet from './components/planner/StopDetailSheet';
import { createTripStopFromName, useNightPlan, useRoutePending, useTripLegs, useTripPlanner } from './components/planner/useTripPlanner';
import { buildTripPrintHtml } from './utils/tripPrint';
import ReviewerLoginPrompt from './components/ReviewerLoginPrompt';
import { useReviewerAuth } from './contexts/ReviewerAuthContext';
import { buildTripLegs, dateForTripDay, segmentIntoDays } from './utils/tripPlanner';
import { apiStopToTripStop, deleteTrip, loadSavedTrips, localOnlyTrips, migrateLocalTrips, syncTripsFromServer, upsertTrip } from './utils/tripStorage';
import { mergeTripStopIntoList } from './utils/tripInsert';
import { kindFromApi, nightsFromApi, normaliseTripStops, totalNights } from './utils/tripRoles';
import { LEG_DISCOVERY_MIN_MINUTES, LegCampsLink, LegDiscoveryPanel, type LegCampsState } from './components/planner/LegDiscovery';
import CampSwapSheet, { fromStopLabel, type NightCampStatus } from './components/planner/CampSwapSheet';
import {
  buildLegLines,
  campFromCandidate,
  campFromEntity,
  campsAlongLine,
  dedupeCamps,
  isCampType,
  lineKm,
  mergeCampOptions,
  rankNearby,
  trimLegEnds,
  type CampOnLine,
  type CampOption,
} from './utils/campsAlong';
import { fetchEntitiesAlong, fetchEntitiesNear } from './utils/campsAlongApi';
import { fetchRoadLegs, fetchRoadRoute } from './utils/googleRoad';
import { PENDING_ADD_KEY, takePendingAdd, writeTripSummary } from './utils/tripNightShare';
import { dayForMinute, planNights, suggestedSlotLabel, type NightSlot } from './utils/nightPlanner';
import { figureSetFromLegs, rigPaceFactor, splitRouteIntoLegs } from './utils/routeLegs';
import { haversineDistanceKm as haversineKm, nearestLegIndex, pointToSegmentKm } from './utils/tripPlanner';
import { apiListTrips, type ApiTripStop } from './utils/tripApi';
import type { StoredTrip } from './utils/tripStorage';
import { search, type EnhancedSearchParams, getEnhancedLocationSuggestions, fetchAllMapPoints } from './lib/searchEntities';
import { matchesMapType, mapTypeNoun } from './lib/mapTypeFilter';
import { planRoute, fetchSuggestions, getDefaultVehicle, type Vehicle, type LatLng } from './utils/routingApi';
import { fetchPopularRoutes, fetchSystemTrip, type PopularRouteSummary } from './utils/popularTripsApi';
import { getUserToken } from './utils/reviewerApi';
import { trackSearch, trackSelectItem, trackAddToTrip, trackFilterChange, trackGenerateLead } from './lib/analytics';
import { trackActivity } from './lib/activity';
import HeroSearch from './components/HeroSearch';
import EntityCard from './components/EntityCard';
import VanHirePromo from './components/VanHirePromo';
import dynamic from 'next/dynamic';
import { anchorPathEnds, shouldShowAllPoints, resolveResultsCount, resolveMatchingTotal, truncatedCountDisplay, isResultsListLoading, mapCountSummary } from './lib/mapPoints';
import { computeFitView, fitTopAnchored, getDefaultFrameBounds, getVisibleMapSize, isSameView, phoneCountryView, pointToLatLng, shiftCenterForInset, type BoundsLiteral, type MapView } from './lib/mapFrame';
import { PIN_AIM_BANNER, PIN_AIM_TOP, pinAimPoint, pinGhost as pinGhostFor } from './components/planner/pinAim';
/**
 * Phones: the strip above the sheet kept clear of the trip when it is
 * framed: Google's logo plus the add/undo toast that docks there (36px up,
 * ~56px tall), so a toast never sits on a pin or the road line.
 */
const PHONE_TOAST_LANE = 104;
import {
  shouldActivateExplorerMap,
  hasMapIntentInUrl,
  useNearViewport,
  usePageSettled,
  useMediaQuery,
} from './lib/explorerMapGate';
import { getGoogleMapsLoader } from './lib/googleMapsLoader';
import { useCaPreview } from './lib/caPreview';
import { CRUMB_TAIL_EVENT, CRUMB_BACK_EVENT } from './components/Breadcrumbs';
import ExplorerMore from './components/store/ExplorerMore';
import { AU_MAP_WIDE, AU_MAP_TALL } from './lib/placeMap';
import MapToolbar from './components/MapToolbar';
import {
  createAgentSession,
  sendAgentMessage,
  summariseTripForAssistant,
  inferAssistantTaskType,
} from './utils/agentAssistant';
import { rememberLegFigures } from './utils/legFigures';

/** The phone planner sheet around its children, or just the children. */
const MaybeSheet: React.FC<{
  on: boolean;
  snap: SheetSnap;
  onSnap: (s: SheetSnap) => void;
  onInsetChange: (h: number) => void;
  peekHeight?: number;
  children: React.ReactNode;
}> = ({ on, snap, onSnap, onInsetChange, peekHeight, children }) =>
  on ? (
    <PlannerBottomSheet snap={snap} onSnap={onSnap} onInsetChange={onInsetChange} peekHeight={peekHeight}>
      {children}
    </PlannerBottomSheet>
  ) : (
    <>{children}</>
  );

/**
 * Same-size stand-in for the map until it is allowed to load (see
 * lib/explorerMapGate). Fills its absolutely-positioned parent, so swapping in
 * the real map causes no layout shift. Clicking it loads the map at once.
 */
const MapPlaceholder: React.FC<{ onActivate?: () => void }> = ({ onActivate }) => {
  // ca_preview: a real map picture of Australia (our own tile server) while
  // the live map loads, never a dotted grey box.
  const preview = useCaPreview();
  return (
  <div
    className="tp-map-placeholder absolute inset-0 flex items-center justify-center bg-[#e7edf4]"
    style={{
      backgroundImage:
        'radial-gradient(circle at 1px 1px, rgba(22,35,58,.10) 1px, transparent 0)',
      backgroundSize: '18px 18px',
    }}
  >
    {preview && (
      <picture>
        <source media="(max-width: 767px)" srcSet={withBase(AU_MAP_TALL)} />
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={withBase(AU_MAP_WIDE)}
          alt=""
          aria-hidden="true"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
      </picture>
    )}
    <button
      type="button"
      onClick={onActivate}
      className={`ca-map-loading inline-flex items-center gap-2 rounded-full border border-[#16233a]/15 bg-white/95 px-4 py-2 text-xs font-semibold text-[#16233a] shadow-sm transition hover:border-[#1558b0] hover:text-[#1558b0]${preview ? ' is-preview' : ''}`}
      aria-label="Load the map"
    >
      <EnvironmentOutlined className="text-[12px]" />
      Loading map…
    </button>
  </div>
  );
};

// Google Maps + the map component (supercluster, overlay layers…) are split
// out of the explorer's first-load bundle and fetched only when the map is
// about to be used — see explorerMapGate. ssr:false: the component is
// client-only anyway (it bails out early on the server).
const GoogleMapComponent = dynamic(() => import('./components/GoogleMapComponent'), {
  ssr: false,
  loading: () => <MapPlaceholder />,
});

const uniqueId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `trip-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

/** Device-wide copy of the trip being planned (see tripNightShare). */
const TRIP_DRAFT_SHARED_KEY = 'trip-planner.draftTrip';
/** "Find camps" on one leg: this far either side of the road, on every screen. */
const LEG_WIDTH_KM = 8;
/** Only if Directions can't route the leg at all: a wider band round a straight line. */
const LEG_WIDTH_STRAIGHT_KM = 20;
/** A night's camps: this far from the overnight stop. */
const NIGHT_RADIUS_KM = 25;
const legKeyOf = (fromId: string, toId: string) => `${fromId}>${toId}`;
/** Hero photo handed over by "Take this trip" (planner/takeTrip). */
const TAKEN_COVER_KEY = 'tp-taken-cover';

/** Today as YYYY-MM-DD in local time. */
const todayIso = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const computeTripRouteKey = (stops: TripStop[]): string =>
  stops
    .map((stop) => stop.name?.trim().toLowerCase())
    .filter(Boolean)
    .join('|');

// Transient toast messages were removed from the explorer — they flashed on
// every search and duplicated status the UI already shows inline (the results
// count, the "no results" empty state, etc.). This no-op shim neutralises the
// old `message.*` calls in one place without touching each call site; errors
// are still logged to the console for debugging. If a status genuinely needs
// surfacing, add it inline in the page, not as a toast.
const message = {
  success: (..._args: unknown[]) => {},
  info: (..._args: unknown[]) => {},
  warning: (..._args: unknown[]) => {},
  loading: (..._args: unknown[]) => {},
  error: (msg?: unknown, ..._args: unknown[]) => {
    if (msg) console.error('[explorer]', msg);
  },
};

interface ParsedRouteQuery {
  start: string;
  end: string;
  waypoints: string[];
}

const parseRouteQuery = (raw: string): ParsedRouteQuery | null => {
  if (!raw) return null;
  const [first, ...restParts] = raw.split(/\s+to\s+/i);
  if (!first || restParts.length === 0) return null;
  const start = first.trim();
  const remainder = restParts.join(' to ').trim();
  if (!start || !remainder) return null;

  const viaSegments = remainder.split(/\s+via\s+/i);
  const end = viaSegments[0]?.trim();
  if (!end) return null;

  const waypoints = viaSegments
    .slice(1)
    .flatMap((segment) =>
      segment
        .split(/,|;|\band\b/i)
        .map((token) => token.trim())
        .filter(Boolean)
    );

  return { start, end, waypoints };
};

type GeocodeCoordinate = {
  lat: number;
  lng: number;
  boundingBox?: [number, number, number, number];
  type?: string;
  class?: string;
};

const NOMINATIM_ENDPOINT = 'https://nominatim.openstreetmap.org/search';

const haversineDistanceKm = (a: GeocodeCoordinate, b: GeocodeCoordinate): number => {
  const R = 6371; // Earth radius in km
  const toRad = (value: number) => (value * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const sinLat = Math.sin(dLat / 2);
  const sinLon = Math.sin(dLon / 2);
  const h =
    sinLat * sinLat +
    sinLon * sinLon * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return R * c;
};

const fetchCoordinatesForPlace = async (place: string): Promise<GeocodeCoordinate | null> => {
  const trimmed = place.trim();
  if (!trimmed) {
    return null;
  }

  const url = `${NOMINATIM_ENDPOINT}?format=json&q=${encodeURIComponent(
    `${trimmed}, ${getCountryName()}`
  )}&countrycodes=${getCountryCode()}&limit=1`;

  const response = await fetch(url, {
    headers: {
      'User-Agent': 'CampsiteExplorer/1.0',
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Geocode request failed: ${response.status}`);
  }

  const data = await response.json();
  const entry = Array.isArray(data) && data.length > 0 ? data[0] : null;
  if (!entry) {
    return null;
  }

  const lat = parseFloat(entry.lat);
  const lng = parseFloat(entry.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  const boundingBox =
    Array.isArray(entry.boundingbox) && entry.boundingbox.length === 4
      ? (entry.boundingbox.map((value: string) => parseFloat(value)) as [
          number,
          number,
          number,
          number
        ])
      : undefined;

  return {
    lat,
    lng,
    boundingBox,
    type: entry.type,
    class: entry.class,
  };
};

const toFiniteNumber = (value?: string | number): number | undefined => {
  if (value === undefined || value === null) return undefined;
  const parsed = Number.parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const createTripStopFromEntity = (entity: Entity): TripStop => {
  const address = entity.addresses?.[0];
  const lat =
    toFiniteNumber(address?.gps_latitude) ?? toFiniteNumber(address?.waypoint_latitude);
  const lng =
    toFiniteNumber(address?.gps_longitude) ??
    toFiniteNumber(address?.waypoint_longitude);

  const tags =
    entity.features
      ?.filter((feature) => feature.desc)
      .map((feature) => feature.desc!.toLowerCase())
      .slice(0, 6) ?? [];

  return {
    id: `stop-${entity.id}-${uniqueId()}`,
    name: entity.name ?? 'Campsite',
    kind: 'camp',
    entityId: entity.id,
    lat,
    lng,
    tags,
    constraints: [],
  };
};

const convertTripStopsToEntities = (stops: TripStop[]): Entity[] =>
  stops
    .map((stop, index) => {
      const hasCoords =
        typeof stop.lat === 'number' && typeof stop.lng === 'number';

      if (!hasCoords) return null;

      return {
        id: stop.entityId ?? -(index + 1),
        name: stop.name,
        short_desc: stop.kind,
        desc: undefined,
        image_src: undefined,
        display_src: undefined,
        gallery_images: [],
        rating: undefined,
        addresses: [
          {
            gps_latitude: String(stop.lat),
            gps_longitude: String(stop.lng),
            town: undefined,
            state: undefined,
          },
        ],
        features:
          stop.tags?.map((tag, idx) => ({
            id: idx,
            desc: tag,
            feature_type: 'TEXT',
          })) ?? [],
      } as unknown as Entity;
    })
    .filter((entity): entity is Entity => Boolean(entity));

function HomePageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const searchParamsKey = searchParams?.toString();

  // Auth — so the trip planner can offer an in-panel "Sign in" (saving,
  // syncing and GPX/KML export all need an account). Same context the header
  // uses, so state stays in lock-step.
  const { isLoggedIn } = useReviewerAuth();
  const [tripSignInOpen, setTripSignInOpen] = useState(false);

  // STATE
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedType, setSelectedTypeRaw] = useState<string>('All');
  const setSelectedType = useCallback((type: string) => {
    setSelectedTypeRaw(type);
    trackFilterChange('tab', type);
  }, []);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  // Filters + Search with no place typed = a nationwide, feature-filtered
  // search. While its results show, the map leaves the all-points browse view.
  const [featureSearch, setFeatureSearch] = useState(false);
  useEffect(() => {
    if (selectedFeatures.length === 0) setFeatureSearch(false);
  }, [selectedFeatures.length]);
  const [radius, setRadius] = useState<number>(50);
  // View modes: 'split' = map+list side by side (default), 'list' = list only, 'map' = fullscreen map
  const [viewMode, setViewMode] = useState<'split' | 'list' | 'map'>('split');
  const [searchAsMove, setSearchAsMove] = useState(false); // bounds-based loading toggle
  const [mapBoundsEntities, setMapBoundsEntities] = useState<Entity[]>([]);
  const [checkInDate, setCheckInDate] = useState('');
  const [checkOutDate, setCheckOutDate] = useState('');
  const [availabilityData, setAvailabilityData] = useState<Record<string, { status: string; from_price: number | null; booking_url: string; has_booking: boolean }>>({});
  const [options, setOptions] = useState<{ value: string }[]>([]);
  
  const [entities, setEntities] = useState<Entity[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  // Country-aware initial view (AU: shifted south to include Tasmania)
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>(() => getMapDefaults().center);
  const [mapZoom, setMapZoom] = useState<number>(() => getMapDefaults().zoom);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isLoadingFeatured, setIsLoadingFeatured] = useState<boolean>(true);
  
  // Route search state
  const [isRouteSearch, setIsRouteSearch] = useState<boolean>(false);
  const [startPoint, setStartPoint] = useState<string>('');
  const [endPoint, setEndPoint] = useState<string>('');
  const [routeName, setRouteName] = useState<string>('');
  const [routeCorridorWidth, setRouteCorridorWidth] = useState<number>(60);
  const [tripRouteKey, setTripRouteKey] = useState<string | null>(null);
  const [mapViewMode, setMapViewMode] = useState<'search' | 'trip'>('search');
  // The live map (declared before useTripPlanner: a dropped pin reads its zoom).
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  // The road line the trip is drawn with right now (see tripRoutePath).
  const tripRoutePathRef = useRef<{ lat: number; lng: number }[] | null>(null);
  // The trip itself + the handlers that edit it (components/planner).
  const {
    tripStops,
    setTripStops,
    tripStopsRef,
    activeTripId,
    setActiveTripId,
    tripStatusMessage,
    setTripStatusMessage,
    addWaypointMode,
    setAddWaypointMode,
    reverseGeocodeName,
    handleAddWaypointAt,
    handleAddPlaceStop,
    handleInsertPlaceStop,
    highlightStopId,
    fitAfterAddRef,
    mapToast,
    pinPending,
    handleChangeNights,
    handleTripReorder,
    handleTripRemove,
    handleTripEditNote,
    handleTripEditName,
  } = useTripPlanner({ setMapViewMode, routePathRef: tripRoutePathRef, mapRef: mapInstanceRef });
  // ── Route figures: ONE source of truth (see utils/routeLegs) ─────────────
  // The vehicle-aware route (GraphHopper, via /api/routing/plan) split across
  // the legs; Google's per-leg figures only until it lands; a flagged
  // straight-line estimate as the last resort. Header = sum of the legs.
  const [vehicleRoute, setVehicleRoute] = useState<{
    key: string;
    polyline: LatLng[];
    legs: { distanceKm: number; driveMinutes: number }[];
    vertexIndex: number[];
    cumulativeKm: number[];
  } | null>(null);
  // Road legs of the Google route the map drew, keyed to the stop ids the
  // route was drawn for (GoogleMapComponent onRouteLegs).
  const [googleLegs, setGoogleLegs] = useState<{
    key: string;
    legs: { distanceKm: number; driveMinutes: number; via?: string }[];
    /** The road Google drew for each leg (leg camp searches follow it). */
    paths?: LatLng[][];
  } | null>(null);
  // List <-> map linking: the stop picked in the list (its pin grows, the map
  // pans), the stop under the pointer on either side, and the leg hovered in
  // the list (by the stop it drives to).
  const [selectedStopId, setSelectedStopId] = useState<string | null>(null);
  const [hoveredStopId, setHoveredStopId] = useState<string | null>(null);
  const [hoveredLegToId, setHoveredLegToId] = useState<string | null>(null);
  const stopIdsKeyRef = useRef('');
  // The Google Directions line the map last drew, keyed to the stops it was
  // for: dropped pins snap onto it, search rows say how far off it they are.
  const [googlePath, setGooglePath] = useState<{ key: string; path: { lat: number; lng: number }[] } | null>(null);
  // The road per leg from that same drawing (leg camp searches follow it).
  const [googleLegPaths, setGoogleLegPaths] = useState<{ key: string; paths: LatLng[][] } | null>(null);
  // Nightly camps: every overnight's nearby camps, loaded for guests too,
  // shown when "Find a camp" opens that night's picks (campPicksStopId).
  const [campPicksStopId, setCampPicksStopId] = useState<string | null>(null);
  const [nightCamps, setNightCamps] = useState<Record<string, { status: NightCampStatus; options: CampOption[] }>>({});
  const [nightCampsRetry, setNightCampsRetry] = useState(0);
  // Every camp we've shown, so a freshly picked camp renders before its
  // night's list reloads around the new stop.
  const campCacheRef = useRef(new Map<number, CampOption>());
  // "Find camps on this leg": which leg is open, and what each leg found.
  const [openLegKey, setOpenLegKey] = useState<string | null>(null);
  const [legCamps, setLegCamps] = useState<Record<string, LegCampsState & { road?: boolean }>>({});
  // The open leg's rows on screen (numbered): the map pins exactly these.
  const [legPins, setLegPins] = useState<{ camp: CampOnLine; n: number }[]>([]);
  // A night's Swap sheet: its rows, numbered, on the live map beside it.
  const [swapPins, setSwapPins] = useState<{ camp: CampOption; n: number }[]>([]);
  const [hoveredLegCampId, setHoveredLegCampId] = useState<number | null>(null);
  // Picked on the map or by clicking a row: sticky, unlike hover.
  const [selectedLegCampId, setSelectedLegCampId] = useState<number | null>(null);
  const [slotNames, setSlotNames] = useState<Record<string, string>>({});
  // The guests' account pitch rides on their first save, once a session.
  const guestPitchShownRef = useRef(false);
  // Campsite waiting for the "which night?" chooser.
  const [nightChooserEntity, setNightChooserEntity] = useState<Entity | null>(null);
  // Region area outline (region:<name> search)
  const [regionBoundary, setRegionBoundary] = useState<GeoJSON.Polygon | GeoJSON.MultiPolygon | null>(null);
  const [regionBoundarySource, setRegionBoundarySource] = useState<string | undefined>(undefined);
  // Vehicle-aware routing (GraphHopper) — mirrors the iOS trip planner.
  const [routePathOverride, setRoutePathOverride] = useState<LatLng[] | null>(null);
  const [routeAnchors, setRouteAnchors] = useState<{ lat: number; lng: number; day: number }[]>([]);
  const [routeVehicle, setRouteVehicle] = useState<Vehicle | null>(null);
  // Departure date (YYYY-MM-DD) — powers day dates + per-night weather in the
  // ladder. Kept client-side (draft-persisted); server trips don't store it yet.
  const [tripStartDate, setTripStartDate] = useState<string | null>(null);
  // Daily driving budget (hours) — drives the ladder's day segmentation AND
  // the GraphHopper overnight-anchor planning. Draft-persisted.
  const [maxDailyDriveHours, setMaxDailyDriveHours] = useState<number>(6);
  // What the user is towing/driving — shapes the route (caravans route as a
  // small truck) and which campsites suit each night. Saved on the trip as
  // vehicle_type so the apps plan with the same rig.
  const [tripVehicleType, setTripVehicleType] = useState<string>('car');
  const tripVehicleTypeRef = useRef('car');
  tripVehicleTypeRef.current = tripVehicleType;
  // Seeded popular-trip picker (Great Ocean Road, Red Centre Way, …) — loads
  // a catalogue system trip into the ladder as an editable draft.
  const [popularPickerOpen, setPopularPickerOpen] = useState(false);
  const [popularRoutes, setPopularRoutes] = useState<PopularRouteSummary[] | null>(null);
  const [popularLoadingId, setPopularLoadingId] = useState<string | null>(null);
  const [savedTrips, setSavedTrips] = useState<StoredTrip[]>([]);
  const [isEditingTripName, setIsEditingTripName] = useState(false);
  const [tripNameInput, setTripNameInput] = useState('');
  const [mapReady, setMapReady] = useState(false);
  useEffect(() => () => setMapReady(false), []);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantHasUnread, setAssistantHasUnread] = useState(false);
  const [assistantMessages, setAssistantMessages] = useState<AssistantMessage[]>(() => [
    {
      id: uniqueId(),
      role: 'assistant',
      content:
        'Hi there! I can tweak your trip planner. Ask me to add or remove stops, adjust the search radius, or widen the corridor and I will update the ladder for you.',
      createdAt: Date.now(),
    },
  ]);
  const [assistantIsProcessing, setAssistantIsProcessing] = useState(false);
  const [assistantBooting, setAssistantBooting] = useState(true);
  const [agentSessionId, setAgentSessionId] = useState<string | null>(null);
  const [agentHandoff, setAgentHandoff] = useState<string | null>(null);
  const [, setAgentContext] = useState<Record<string, string>>({});
  // Trip planner is always shown above the listings but COLLAPSED by default —
  // the expanded panel dominated the mobile viewport and pushed the map/results
  // below the fold. It auto-expands the moment the user engages (adds a stop,
  // loads a route/popular trip, or restores an in-progress trip — see the
  // stop-count effect below), and the header stays tappable to open manually.
  const [isTripSectionOpen, setIsTripSectionOpen] = useState(false);
  // Someone asked for the planner (?plan=1 or "Plan a road trip"): phones
  // show the map peek above it even before the first stop.
  const [plannerIntent, setPlannerIntent] = useState(false);
  const [visibleCardCount, setVisibleCardCount] = useState(48);
  const [showBackToTop, setShowBackToTop] = useState(false);

  // REFS
  // Camera frame a search wants but couldn't apply because no map instance
  // was live at the time — on mobile the split map never renders (hidden
  // md:block), so a region search's fitBounds went to a null/stale ref and
  // the fullscreen map then re-fit to Australia on mount, dumping the user
  // at continent zoom. Both onMapReady handlers consume this instead.
  const pendingRegionBoundsRef = useRef<google.maps.LatLngBoundsLiteral | null>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  // Results scroll here while the trip planner fills the column (see the
  // sidebar markup); otherwise this div doesn't scroll and the sidebar does.
  const resultsScrollRef = useRef<HTMLDivElement>(null);
  const scrollResultsTop = useCallback(() => {
    sidebarRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    resultsScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  // The split map+list row. It's exactly one viewport tall but sits below the
  // hero, so at most scroll positions the lower half of the map canvas — where
  // fitBounds centres content — is clipped below the fold. When a trip starts
  // we align the row with the viewport so the user actually sees their pin.
  const mapSectionRef = useRef<HTMLDivElement>(null);

  // ── Initial map framing ────────────────────────────────────────────────
  // Explicit "load the map now" requests (deep links, Map + List, Plan a road
  // trip, clicking the placeholder). Derived intent is folded in further down.
  const [mapRequested, setMapRequested] = useState(false);
  // Mirrors for the map's mount-time onMapReady, which GoogleMapComponent
  // captures once (stale closure) — refs always read the live values.
  const mapViewStateRef = useRef<MapView>({ center: mapCenter, zoom: mapZoom });
  mapViewStateRef.current = { center: mapCenter, zoom: mapZoom };
  const routeOrTripActiveRef = useRef(false);
  routeOrTripActiveRef.current = isRouteSearch || tripStops.length > 0;
  // The country frame we last applied, so a later map mount (split → Full
  // Map) still counts it as "nothing chosen yet" and frames its own size.
  const defaultFrameRef = useRef<MapView | null>(null);

  /**
   * Frame a freshly mounted map: a stashed region search wins; otherwise, if
   * the camera is still on the untouched default (no search, route or trip
   * has moved it), fit mainland Australia + Tasmania (or the active escapeto
   * country) to the map's real pixel size.
   *
   * The camera is computed (lib/mapFrame) and written into mapCenter/mapZoom
   * as well as the map, because GoogleMapComponent re-applies those props just
   * after mount and would otherwise undo a plain fitBounds().
   */
  const plannerOpenRef = useRef(false);
  plannerOpenRef.current = isTripSectionOpen && plannerIntent;
  // px of the map covered by the phone planner sheet (0 elsewhere): frames
  // fit the part of the map you can see.
  const mapBottomInsetRef = useRef(0);
  const applyInitialMapFrame = useCallback((map: google.maps.Map, padding: number) => {
    const pending = pendingRegionBoundsRef.current;
    let bounds: BoundsLiteral;
    if (pending) {
      bounds = pending;
    } else {
      if (routeOrTripActiveRef.current) return;
      const current = mapViewStateRef.current;
      const untouched =
        isSameView(current, getMapDefaults()) || isSameView(current, defaultFrameRef.current);
      if (!untouched) return;
      bounds = getDefaultFrameBounds();
    }
    const inset = mapBottomInsetRef.current;
    const visible = getVisibleMapSize(map.getDiv());
    // Phones under the sheet: no integer zoom fits Australia in 390px
    // without either cropping it or showing half of Asia. Zoom 4 with Cape
    // York tucked under the chips: the continent fills the width, the sea
    // above it is gone, and the east coast (most trips) stays in view.
    if (!pending && inset && plannerOpenRef.current && getCountry() === 'AU' && visible.width > 0 && visible.width < 480) {
      const view = phoneCountryView(map.getDiv().clientHeight, 60);
      map.setCenter(view.center);
      map.setZoom(view.zoom);
      defaultFrameRef.current = view;
      setMapCenter(view.center);
      setMapZoom(view.zoom);
      return;
    }
    const fitted = computeFitView(
      bounds,
      // Fit what the layout actually shows of the map (its box clipped by
      // overflow-hidden ancestors, less the sheet over it), not the raw
      // element size.
      { width: visible.width, height: Math.max(0, visible.height - inset) },
      {
        // Region frames keep the roomier 60px they always had.
        padding: pending ? Math.max(padding, 60) : padding,
        // minZoom mirrors the map's own floor (GoogleMapComponent: 4, or 3
        // on phones so the whole country fits a 390px map).
        minZoom: typeof window !== 'undefined' && window.innerWidth < 768 ? 3 : 4,
        maxZoom: pending ? 15 : undefined,
        // The explorer map renders raster tiles, which snap fractional zoom to
        // the NEAREST level (4.58 → 5 cropped Perth and the east coast).
        integerZoom: true,
        // The empty planner is about Australian road trips: frame the country
        // a level closer when it nearly fits, not the region around it.
        // Phones under the sheet: a level down left Australia a quarter of
        // the screen under half of Asia; a level up trims only the far edges
        // of WA and Cape York.
        roundUpFrom: !pending && plannerOpenRef.current ? (inset ? 0.5 : 0.75) : undefined,
      }
    );
    const view = fitted ? shiftCenterForInset(fitted, inset) : null;
    if (!view) {
      // Zero-size container — let Google do it whenever it gets a size.
      map.fitBounds(bounds, padding);
      return;
    }
    map.setCenter(view.center);
    map.setZoom(view.zoom);
    if (!pending) defaultFrameRef.current = view;
    setMapCenter(view.center);
    setMapZoom(view.zoom);
  }, []);

  // Bring the trip planner (top of the results column) on screen. Same
  // scroll target the "trip started" flows use (mapSectionRef).
  // Offsets by the sticky results bar so it doesn't sit over the top of the
  // planner card and the map.
  const resultsBarRef = useRef<HTMLDivElement>(null);
  const scrollPlannerIntoView = useCallback(() => {
    sidebarRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    const section = mapSectionRef.current;
    if (!section || typeof window === 'undefined') return;
    const barHeight = resultsBarRef.current?.offsetHeight ?? 0;
    const top = section.getBoundingClientRect().top + window.scrollY - barHeight;
    window.scrollTo({ top: Math.max(0, Math.ceil(top)), behavior: 'smooth' }); // ceil: a fractional scroll left the footer 0.1px below the fold
  }, []);
  const scrollPlannerIntoViewRef = useRef(scrollPlannerIntoView);
  scrollPlannerIntoViewRef.current = scrollPlannerIntoView;

  const entityRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const prevTripStopCountRef = useRef(tripStops.length);
  const geocodeCacheRef = useRef<Map<string, GeocodeCoordinate>>(new Map());
  const geocodeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Scroll tracking for back-to-top button
  useEffect(() => {
    const scrollers = [sidebarRef.current, resultsScrollRef.current].filter(
      (el): el is HTMLDivElement => !!el
    );
    if (!scrollers.length) return;
    const onScroll = () => {
      setShowBackToTop(scrollers.some((el) => el.scrollTop > 600));
    };
    scrollers.forEach((el) => el.addEventListener('scroll', onScroll, { passive: true }));
    return () => scrollers.forEach((el) => el.removeEventListener('scroll', onScroll));
  }, []);

  const scrollToTop = scrollResultsTop;

  // CONSTANTS
  const features = [
    // Essentials & Amenities
    'Powered Sites',
    'Unpowered Sites',
    'Toilets',
    'Hot Showers',
    'Showers',
    'Drinking Water',
    'Camp Kitchen',
    'Laundry',
    'Dump Point',
    'Public Dump Point',
    'Accessible Facilities',
    'Campfires Allowed',
    // Comfort & Services
    'Cabins',
    'Glamping',
    'WiFi',
    'BBQ',
    'Playground',
    'Swimming Pool',
    'Family Friendly',
    'Pet Friendly',
    'On-site Cafe',
    'Firewood Available',
    // Outdoor & Adventure
    'River Access',
    'Beach Access',
    'Lake Access',
    'Boat Ramp',
    'Fishing',
    'Hiking Trails',
    'Cycling Trails',
    '4WD Access',
    'Horse Riding',
    'Stargazing',
    // Points of Interest
    'Lookout',
    'Waterfall',
    'Scenic Drive',
    'National Park Visitor Centre',
    'Wildlife Park',
    'Botanic Gardens',
    'Historic Site',
    'Heritage Walk',
    'Museum',
    'Art Gallery',
    'Winery',
    'Brewery',
    'Distillery',
    'Farm Gate',
    'Local Market',
  ];

  const toggleAssistant = useCallback(() => {
    setAssistantOpen((prev) => {
      const next = !prev;
      if (next) {
        setAssistantHasUnread(false);
      }
      return next;
    });
  }, []);

  const assistantSuggestions = useMemo(() => {
    // Per-country example prompts (AU strings unchanged — see homeContent.ts).
    const content = getHomeContent();
    return isRouteSearch ? content.assistantRouteSuggestions : content.assistantSuggestions;
  }, [isRouteSearch]);

  useEffect(() => {
    let cancelled = false;

    const bootstrapSession = async () => {
      try {
        const session = await createAgentSession({
          itinerary_summary: 'No trip stops yet.',
        });
        if (!cancelled) {
          setAgentSessionId(session.session_id);
          setAgentContext(session.context || {});
        }
      } catch (error) {
        console.error('Failed to initialise agent session:', error);
        if (!cancelled) {
          message.warning('Trip assistant is waking up, falling back to local actions for now.');
        }
      } finally {
        if (!cancelled) {
          setAssistantBooting(false);
        }
      }
    };

    bootstrapSession();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load featured campsites on mount
  useEffect(() => {
    const fetchFeaturedCampsites = async () => {
      try {
        const response = await search({
          location: getCountryName(),
          radius: 5000,
          per_page: 200,
          sort: 'featured',
          search_type: 'location'
        });
        setEntities(response.entities || []);
      } catch (error) {
        console.error('Failed to fetch featured campsites:', error);
      } finally {
        setIsLoadingFeatured(false);
      }
    };

    if (!isRouteSearch) {
      fetchFeaturedCampsites();
    }
  }, [isRouteSearch]);

  // Check for route parameters on mount
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handler = (event: Event) => {
      const custom = event as CustomEvent<{ location: string; radius?: number }>;
      if (!custom.detail?.location) {
        return;
      }
      handleSearchRef.current?.({
        location: custom.detail.location,
        radius: custom.detail.radius,
      });
    };

    window.addEventListener('global-location-search', handler);
    return () => window.removeEventListener('global-location-search', handler);
  }, []);

  useEffect(() => {
    const trips = loadSavedTrips();
    setSavedTrips(trips);
  }, []);

  // Live refresh when the AI chat ("Trippy") edits the user's saved trip.
  // TripChat dispatches 'trip-updated' with the trip id after the agent calls a
  // trip tool; pull the fresh trip from the backend and, if it's the trip we
  // have open (or the user has none open yet), load it straight into the ladder
  // + map so the chat and planner stay in lock-step.
  useEffect(() => {
    // Same mapping the saved-trips list uses (kinds + nights per contract).
    const toTripStop = (s: ApiTripStop): TripStop => apiStopToTripStop(s);

    const onTripUpdated = async (event: Event) => {
      const updatedId = (event as CustomEvent<{ trip_id?: string }>).detail?.trip_id;
      try {
        const trips = await apiListTrips();
        if (!trips.length) return;
        const target =
          (updatedId && trips.find((t) => t.id === updatedId)) ||
          (activeTripId && trips.find((t) => t.id === activeTripId)) ||
          null;
        if (!target) return;

        // Only take over the planner view when it's the trip already open, or
        // the user has nothing open yet — never clobber a different active trip.
        if (target.id === activeTripId || !activeTripId) {
          const stops = [...target.stops]
            .sort((a, b) => a.order_index - b.order_index)
            .map(toTripStop);
          setTripStops(stops);
          setActiveTripId(target.id);
          if (stops.length >= 2) setMapViewMode('trip');
          setTripStatusMessage({ message: `Trippy updated “${target.name}”`, tone: 'success' });
        } else {
          setTripStatusMessage({ message: `Trippy updated “${target.name}”`, tone: 'success' });
        }
      } catch {
        /* best-effort live refresh */
      }
    };

    window.addEventListener('trip-updated', onTripUpdated);
    return () => window.removeEventListener('trip-updated', onTripUpdated);
  }, [activeTripId]);

  useEffect(() => {
    if (!tripStatusMessage) return;
    // Messages with an action (Undo) get a longer window to react.
    const phone = typeof window !== 'undefined' && window.innerWidth < 768;
    const timer = window.setTimeout(
      () => setTripStatusMessage(null),
      tripStatusMessage.ttlMs ?? (tripStatusMessage.action ? (phone ? 5000 : 7000) : 4000)
    );
    return () => window.clearTimeout(timer);
  }, [tripStatusMessage]);

  useEffect(() => {
    if (tripStops.length < 2 && isEditingTripName) {
      setIsEditingTripName(false);
    }
  }, [tripStops.length, isEditingTripName]);

  const focusMapOnStopsRef = useRef<(stops: TripStop[]) => boolean>(() => false);
  // A trip with somewhere to go is on the map: it frames the camera itself.
  const tripOnMapRef = useRef(false);
  const geocodeAndZoomToLocationRef = useRef<(location: string) => Promise<void>>(async () => {});

  const lookupCoordinates = useCallback(
    async (place: string): Promise<GeocodeCoordinate | null> => {
      const normalized = place.trim().toLowerCase();
      if (!normalized) {
        return null;
      }

      if (geocodeCacheRef.current.has(normalized)) {
        return geocodeCacheRef.current.get(normalized)!;
      }

      try {
        const coords = await fetchCoordinatesForPlace(place);
        if (coords) {
          geocodeCacheRef.current.set(normalized, coords);
          return coords;
        }
      } catch (error) {
        console.warn('[Geocode] Lookup failed for', place, error);
      }

      return null;
    },
    []
  );

  const estimateRouteDistance = useCallback(
    async (startLocation: string, endLocation: string): Promise<number | null> => {
      if (!startLocation || !endLocation) {
        return null;
      }
      const [startCoords, endCoords] = await Promise.all([
        lookupCoordinates(startLocation),
        lookupCoordinates(endLocation),
      ]);
      if (!startCoords || !endCoords) {
        return null;
      }
      return haversineDistanceKm(startCoords, endCoords);
    },
    [lookupCoordinates]
  );

  focusMapOnStopsRef.current = (stops: TripStop[]): boolean => {
    if (!mapInstanceRef.current || stops.length === 0) return false;
    if (typeof window === 'undefined' || !window.google) return false;

    const bounds = new google.maps.LatLngBounds();
    let hasCoords = false;

    stops.forEach((stop) => {
      if (typeof stop.lat === 'number' && typeof stop.lng === 'number') {
        bounds.extend({ lat: stop.lat, lng: stop.lng });
        hasCoords = true;
      }
    });

    if (!hasCoords) return false;

    mapInstanceRef.current.fitBounds(bounds, { top: 80, right: 80, bottom: 80, left: 80 });

    const newCenter = bounds.getCenter();
    const fittedZoom = mapInstanceRef.current.getZoom() ?? mapZoom;
    const zoomCap = stops.length <= 2 ? Math.min(fittedZoom, 8) : Math.min(fittedZoom, 11);

    if (zoomCap !== fittedZoom) {
      mapInstanceRef.current.setZoom(zoomCap);
    }

    setMapCenter({ lat: newCenter.lat(), lng: newCenter.lng() });
    setMapZoom(mapInstanceRef.current.getZoom() ?? zoomCap);

    return true;
  };

  const focusMapOnStops = useCallback(
    (stops: TripStop[]): boolean => {
      return focusMapOnStopsRef.current(stops);
    },
    []
  );

  const geocodeAndZoomToLocation = useCallback(
    async (location: string) => geocodeAndZoomToLocationRef.current(location),
    []
  );

  useEffect(() => {
    if (tripStops.length >= 2) {
      setTripRouteKey(computeTripRouteKey(tripStops));
    } else {
      setTripRouteKey(null);
    }
  }, [tripStops]);

  const toggleFeature = (feature: string) => {
    setSelectedFeatures(prev => 
      prev.includes(feature) 
        ? prev.filter(f => f !== feature)
        : [...prev, feature]
    );
  };

  const clearFilters = () => {
    setSelectedFeatures([]);
    setSelectedType('All');
    setRadius(50);
  };

  const clearVehicleRoute = () => {
    setRoutePathOverride(null);
    setRouteAnchors([]);
    setRouteVehicle(null);
    setVehicleRoute(null);
  };
  // Set further down (planVehicleAwareRouteFromStops is a useCallback declared
  // after the route-search handler that needs it).
  const planFromStopsRef = useRef<(stops: TripStop[]) => Promise<void>>(async () => {});

  // Geocode a place string to coords with the Google Geocoder. Maps JS is no
  // longer loaded eagerly (the map mounts lazily), so a route deep link or a
  // mobile visitor (split map never mounts there) can get here before anything
  // loaded it — load it on demand rather than silently skipping the
  // vehicle-aware route. The loader is a singleton, so this is free once the
  // map is up.
  const geocodeToLatLng = async (address: string): Promise<LatLng | null> => {
    if (typeof window === 'undefined') return null;
    if (!window.google?.maps?.Geocoder) {
      try {
        await getGoogleMapsLoader().load();
      } catch {
        return null;
      }
    }
    return new Promise((resolve) => {
      if (!window.google?.maps) { resolve(null); return; }
      try {
        new google.maps.Geocoder().geocode(
          { address: `${address}, ${getCountryName()}`, region: getCountryCode() },
          (results, status) => {
            if (status === google.maps.GeocoderStatus.OK && results?.[0]?.geometry?.location) {
              const loc = results[0].geometry.location;
              resolve({ lat: loc.lat(), lng: loc.lng() });
            } else resolve(null);
          });
      } catch { resolve(null); }
    });
  };


  const handleRouteSearch = async (
    start: string,
    end: string,
    name: string = '',
    waypoints: string[] = [],
    presetStops?: TripStop[],
    options: { preserveActiveTrip?: boolean } = {}
  ) => {
    setIsSearching(true);
    setIsRouteSearch(true);
    // Clear bounds entities so displayEntities shows route results, not stale map data
    setMapBoundsEntities([]);
    setStartPoint(start);
    setEndPoint(end);
    const readableName =
      name ||
      `${start} to ${end}${waypoints.length ? ` via ${waypoints.join(', ')}` : ''}`;
    const trimmedRouteName = readableName.trim();
    setRouteName(trimmedRouteName);
    if (trimmedRouteName) {
      setSearchQuery(trimmedRouteName);
      setOptions([{ value: trimmedRouteName }]);
    }
    // Default to Search mode so users immediately see campsites along the corridor.
    setMapViewMode('search');

    const initialStops =
      presetStops && presetStops.length >= 2
        ? presetStops
        : [
            createTripStopFromName(start, 'start', { locked: true }),
            ...waypoints.map((wp) =>
              createTripStopFromName(wp, 'via', { locked: false })
            ),
            createTripStopFromName(end, 'end', { locked: true }),
          ];

    setTripStops(initialStops);
    setTripRouteKey(computeTripRouteKey(initialStops));
    if (!options.preserveActiveTrip) {
      setActiveTripId(null);
    }

    try {
      const baseCorridorWidth = Math.max(radius, 15);
      let corridorWidthKm = baseCorridorWidth;
      let estimatedDistanceKm: number | null = null;

      try {
        estimatedDistanceKm = await estimateRouteDistance(start, end);
        if (estimatedDistanceKm) {
          const dynamicWidth = Math.min(
            30,
            Math.max(15, Math.round(estimatedDistanceKm / 60))
          );
          corridorWidthKm = dynamicWidth;
        }
      } catch (distanceError) {
        console.warn('[Route search] Unable to estimate distance:', distanceError);
      }

      setRouteCorridorWidth(corridorWidthKm);

      // The backend corridor query collects ALL matches then slices, so the
      // expensive distance scan runs in full on every page request. Paginating
      // therefore re-runs the whole scan (and re-derives the route) per page —
      // fetch one big page instead so the scan runs exactly once.
      const perPage = 500;

      const params: EnhancedSearchParams = {
        search_type: 'route',
        startPoint: start,
        endPoint: end,
        radius: corridorWidthKm,
        max_deviation: corridorWidthKm,
        corridor_width: corridorWidthKm,
        page: 1,
        per_page: perPage,
        viaPoints: waypoints,
      };

      // Apply tab filter to route searches
      if (selectedType === 'Accommodation') {
        params.entity_type = 'accom';
      } else if (selectedType === 'Activities') {
        params.entity_type = 'tours';
      } else if (selectedType !== 'All') {
        params.type = selectedType;
      }

      if (selectedFeatures.length > 0) {
        params.features = selectedFeatures;
      }

      const response = await search(params);
      const searchResults = response.entities || [];

      setEntities(searchResults);

      // Vehicle-aware route line (guests too) — runs in the background so the
      // corridor results render immediately. Same planner as every other
      // stop edit, so the totals come from one place.
      clearVehicleRoute();
      void planFromStopsRef.current(initialStops);
      
      scrollResultsTop();

      const resultLabel = selectedType === 'Accommodation' ? 'accommodation' : selectedType !== 'All' ? selectedType.toLowerCase() : 'results';
      setSearchTotal(response.total || 0);
      trackActivity('route_search', {
        term: trimmedRouteName,
        resultCount: response.total || 0,
        meta: { start, end, waypoints: waypoints.length, type: selectedType },
      });
      const routeFailed =
        (response as { search_metadata?: { error?: string } }).search_metadata?.error === 'route_unavailable' ||
        (response.total ?? 0) === 0;
      if (routeFailed) {
        // The route geocode/routing step occasionally fails. Don't pretend it
        // worked (and never show the scattered all-Australia fallback) — ask
        // the user to retry.
        message.warning(`Couldn't map ${trimmedRouteName || 'that route'} just now — please tap Search to try again.`);
        // If this search was launched FROM a trip ("Camps along route"),
        // land the user back on their trip instead of stranding them in an
        // empty search view with the camera reset to the continent default.
        if (presetStops && presetStops.length >= 2) {
          setMapViewMode('trip');
        }
      } else {
        message.success(`Found ${response.total} ${resultLabel} along route`);
      }
    } catch (error) {
      console.error('Route search error:', error);
      message.error('Failed to search along route. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleRouteSearchRef = useRef<typeof handleRouteSearch>();
  handleRouteSearchRef.current = handleRouteSearch;

  geocodeAndZoomToLocationRef.current = async (location: string) => {
    if (!location || location.length < 3) return;
    
    try {
      const coords = await lookupCoordinates(location);
      if (!coords) {
        return;
      }

      const { lat, lng, boundingBox, type: placeType, class: placeClass } = coords;
      let zoom = 12;
      
      if (boundingBox) {
        const [minLat, maxLat, minLon, maxLon] = boundingBox;
        const latDiff = Math.abs(maxLat - minLat);
        const lonDiff = Math.abs(maxLon - minLon);
        const avgDiff = (latDiff + lonDiff) / 2;
        
        if (avgDiff > 10) zoom = 5;
        else if (avgDiff > 3) zoom = 7;
        else if (avgDiff > 1) zoom = 9;
        else if (avgDiff > 0.3) zoom = 11;
        else if (avgDiff > 0.05) zoom = 13;
        else zoom = 14;
      } else if (placeType === 'country' || placeClass === 'boundary') {
        zoom = 5;
      } else if (placeType === 'state' || placeType === 'administrative') {
        zoom = 7;
      } else if (placeType === 'city' || placeType === 'town') {
        zoom = 11;
      } else if (placeType === 'suburb' || placeType === 'locality') {
        zoom = 13;
      }

      setMapCenter({ lat, lng });
      setMapZoom(zoom);
    } catch (error) {
      console.error('Geocoding error:', error);
    }
  };
  
  useEffect(() => {
    if (!mapReady) return;
    if (!isRouteSearch || tripStops.length < 2) return;
    if (mapViewMode !== 'trip') return;
    const focused = focusMapOnStops(tripStops);
    if (!focused && startPoint) {
      geocodeAndZoomToLocation(startPoint);
    }
  }, [mapReady, isRouteSearch, tripStops, mapViewMode, focusMapOnStops, startPoint, geocodeAndZoomToLocation]);
  
  const handleLocationSearch = useCallback(async (value: string) => {

    // Check for route search format
    if (value.includes(' to ')) {
      const [from, to] = value.split(' to ').map(s => s.trim());
      if (from && to) {
        setOptions([{ value: value }]);
      }
      return;
    }

    // Fetch autocomplete suggestions
    if (value.length > 2) {
      try {        
        const suggestions = await getEnhancedLocationSuggestions(value);
        setOptions(suggestions.map((item) => ({ value: item.value })));
        
        if (geocodeTimeoutRef.current) {
          clearTimeout(geocodeTimeoutRef.current);
        }

        if (value.length >= 5) {
          geocodeTimeoutRef.current = setTimeout(() => {
            geocodeAndZoomToLocation(value);
          }, 1500);
        }
      } catch (error) {
        console.error('Error fetching location suggestions:', error);
        setOptions([]);
      }
    } else {
      setOptions([]);
    }
  }, [geocodeAndZoomToLocation]);

  const handleLocationSelect = useCallback((value: string) => {
    setSearchQuery(value);
    
    // Cancel any pending auto-geocode
    if (geocodeTimeoutRef.current) {
      clearTimeout(geocodeTimeoutRef.current);
    }
    
    // Only geocode if not a route search
    if (!value.includes(' to ')) {
      geocodeAndZoomToLocation(value);
    }
  }, [geocodeAndZoomToLocation]);

  const getLocationInputValue = useCallback((input: unknown): string => {
    if (typeof input === 'string') {
      return input;
    }
    if (input && typeof input === 'object') {
      const candidate = input as { value?: unknown; label?: unknown };
      if (typeof candidate.value === 'string') {
        return candidate.value;
      }
      if (typeof candidate.label === 'string') {
        return candidate.label;
      }
      if (Array.isArray(input)) {
        return input.filter(Boolean).join(' ');
      }
    }
    // A bare number/boolean is never a valid location string. Stringifying
    // here (String(0) === "0") produced spurious "0" location searches that
    // returned nothing and polluted search analytics — treat as no input.
    return '';
  }, []);

  // region:<name> — outline the region area and pin every campsite inside it.
  const handleRegionSearch = async (rawRegion: string) => {
    const slug = rawRegion
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    if (!slug) return;

    setIsRouteSearch(false);
    setTripStops([]);
    setActiveTripId(null);
    setMapViewMode('search');
    setIsSearching(true);
    setMapBoundsEntities([]);

    try {
      const resp = await fetch(`https://app.trip-planner.com.au/api/regions/${slug}/map`);
      if (!resp.ok) {
        trackActivity('search', { term: `region:${rawRegion}`, resultCount: 0, meta: { scope: 'region' } });
        message.info(`No region matching "${rawRegion}"`);
        return;
      }
      const data = await resp.json();
      const ents = (data.entities || [])
        .filter((e: any) => e && e.lat != null && e.lng != null)
        .map((e: any) => ({
          id: e.id,
          entityId: e.id,
          name: e.name,
          entityType: e.entity_type,
          // Marker/chip code reads the snake_case field — carry both so
          // region pins get their typed glyphs like search results do.
          entity_type: e.entity_type,
          imageSrc: e.image_src,
          addresses: [{ gps_latitude: String(e.lat), gps_longitude: String(e.lng) }],
        }));

      setEntities(ents as any);
      setSearchTotal(ents.length);
      trackActivity('search', { term: `region:${data.name || rawRegion}`, resultCount: ents.length, meta: { scope: 'region' } });
      setRegionBoundary(data.boundary || null);
      setRegionBoundarySource(data.boundary_source);
      if (ents.length === 0) {
        message.info(`No campsites found in ${data.name || rawRegion}`);
      } else {
        message.success(`${data.name}: ${ents.length} campsite${ents.length === 1 ? '' : 's'}`);
      }

      // Frame the region: prefer the boundary extent, else the campsites.
      // Plain numeric bounds — no google.maps dependency, and stashed in
      // pendingRegionBoundsRef so a map that mounts LATER (mobile fullscreen)
      // still frames the region instead of its Australia-wide default.
      let south = Infinity, west = Infinity, north = -Infinity, east = -Infinity;
      const addCoord = (lng: number, lat: number) => {
        if (typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng)) {
          south = Math.min(south, lat); north = Math.max(north, lat);
          west = Math.min(west, lng); east = Math.max(east, lng);
        }
      };
      const geom = data.boundary;
      if (geom?.type === 'Polygon') {
        geom.coordinates.forEach((ring: number[][]) => ring.forEach((c) => addCoord(c[0], c[1])));
      } else if (geom?.type === 'MultiPolygon') {
        geom.coordinates.forEach((poly: number[][][]) =>
          poly.forEach((ring) => ring.forEach((c) => addCoord(c[0], c[1]))));
      }
      if (!isFinite(south)) {
        ents.forEach((e: any) => addCoord(
          parseFloat(e.addresses[0].gps_longitude),
          parseFloat(e.addresses[0].gps_latitude)));
      }
      if (isFinite(south)) {
        const literal = { south, west, north, east };
        pendingRegionBoundsRef.current = literal;
        if (mapInstanceRef.current) {
          mapInstanceRef.current.fitBounds(literal, { top: 60, right: 60, bottom: 60, left: 60 });
        }
      }
    } catch (err) {
      console.error('Region search error:', err);
      message.error('Failed to load region');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = async (
    override?: { location?: string; radius?: number }
  ) => {
    const rawLocationInput = override?.location ?? searchQuery;
    let targetLocation = getLocationInputValue(rawLocationInput).trim();
    // Guard against junk all-zero locations (a stray "0" from a malformed
    // ?search=0 / ?location=0 link or a campsite with town "0"). Never a
    // real place; was logged ~192× as zero-result searches. Postcodes like
    // "2481" are not all-zeros, so they still search normally.
    if (/^0+$/.test(targetLocation)) {
      targetLocation = '';
    }
    const targetRadius = override?.radius ?? radius;

    // Clear any prior region outline; a region:<name> search re-sets it below.
    setRegionBoundary(null);
    // A plain location/region search isn't a route — drop any vehicle route line.
    clearVehicleRoute();

    if (!targetLocation) {
      // A type tab (e.g., Free Camping) or feature filters (Filters panel, no
      // place typed) → a nationwide bounds search. Feature filters used to be
      // ignored here, so Search with no location looked like it did nothing.
      const withFeatures = selectedFeatures.length > 0;
      if (selectedType !== 'All' || withFeatures) {
        const what = selectedType !== 'All' ? selectedType.toLowerCase() : 'campsites';
        setFeatureSearch(withFeatures);
        setIsRouteSearch(false);
        setTripStops([]);
        setActiveTripId(null);
        setMapViewMode('search');
        setIsSearching(true);
        setMapBoundsEntities([]);

        try {
          // Use bounds-search covering all of Australia instead of geocoding
          const { searchEntitiesByBounds } = await import('@/app/lib/searchEntities');
          const filters: Record<string, any> = {};
          if (selectedType === 'Accommodation') {
            filters.entity_type = 'accom';
          } else if (selectedType !== 'All') {
            filters.campsite_type = selectedType;
          }
          if (withFeatures) {
            filters.features = selectedFeatures;
          }
          const response = await searchEntitiesByBounds({
            bounds: getCountryBounds(), // whole active country (AU default)
            zoom: 5,
            limit: 200,
            filters,
          });
          const searchResults = response.entities || [];
          setEntities(searchResults);
          if (searchResults.length === 0) {
            setSearchTotal(0);
            message.info(`No ${what} found${withFeatures ? ' with those filters' : ''}`);
          } else {
            setSearchTotal(response.total || 0, response.truncated ? response.total_matching : undefined);
            message.success(`Found ${(response.truncated && response.total_matching ? response.total_matching : response.total || 0).toLocaleString('en-AU')} ${what}${withFeatures ? ' matching your filters' : ' spots'} across ${getCountryName()}`);
          }
        } catch (error) {
          console.error('Type search error:', error);
          message.error('Failed to search');
        } finally {
          setIsSearching(false);
        }
        return;
      }
      // Empty query + All types = "show me everything". Previously this just
      // warned and returned, which left any lingering route-search state in
      // place — after a failed corridor search the map sat EMPTY (corridor
      // results were 0 and the all-points browse layer is suppressed while
      // isRouteSearch is true). Reset to the browse view instead.
      setIsRouteSearch(false);
      setFeatureSearch(false);
      setSearchQuery('');
      setEntities([]);
      setSearchTotal(0);
      setMapBoundsEntities([]);
      setMapViewMode('search');
      message.success('Showing all campsites');
      return;
    }

    setFeatureSearch(false);
    if (override?.location) {
      setSearchQuery(override.location);
    }
    if (override?.radius) {
      setRadius(override.radius);
    }

    // region:<name> keyword — outline the region + pin its campsites.
    const regionMatch = targetLocation.match(/^region:\s*(.+)$/i);
    if (regionMatch) {
      await handleRegionSearch(regionMatch[1].trim());
      return;
    }
    // Any other search supersedes a stashed region frame — a later map
    // mount should not snap back to a region the user has moved past.
    pendingRegionBoundsRef.current = null;

    const parsedRoute = parseRouteQuery(targetLocation);
    if (parsedRoute) {
      await handleRouteSearch(
        parsedRoute.start,
        parsedRoute.end,
        '',
        parsedRoute.waypoints
      );
      return;
    }

    // Route-mode reliability: when the user arrives from a "popular route"
    // link the search box shows the route's NAME, which usually has no
    // " to " — so parseRouteQuery above returns null. If the box still
    // reflects the active route, a Search click must re-run the STRUCTURED
    // route search using the known start/end points, NOT fall through to a
    // plain location geocode of the route name (which "doesn't find the
    // route"). Preserve any waypoints already on the trip.
    if (
      isRouteSearch &&
      startPoint &&
      endPoint &&
      (targetLocation === routeName.trim() ||
        targetLocation === `${startPoint} to ${endPoint}`)
    ) {
      const activeWaypoints = tripStops
        .filter((s) => s.kind === 'waypoint' && s.name)
        .map((s) => s.name as string);
      await handleRouteSearch(startPoint, endPoint, routeName, activeWaypoints);
      return;
    }

    // Broad geographic search detection — use bounds-search for country/state level queries
    const broadSearchBounds: Record<string, { north: number; south: number; east: number; west: number }> = {
      // Country
      'australia': { north: -10, south: -45, east: 155, west: 112 },
      'aus': { north: -10, south: -45, east: 155, west: 112 },
      // States & territories
      'queensland': { north: -10, south: -29.2, east: 153.6, west: 138 },
      'qld': { north: -10, south: -29.2, east: 153.6, west: 138 },
      'new south wales': { north: -28.1, south: -37.5, east: 153.7, west: 141 },
      'nsw': { north: -28.1, south: -37.5, east: 153.7, west: 141 },
      'victoria': { north: -34, south: -39.2, east: 150, west: 141 },
      'vic': { north: -34, south: -39.2, east: 150, west: 141 },
      'south australia': { north: -26, south: -38.1, east: 141, west: 129 },
      'sa': { north: -26, south: -38.1, east: 141, west: 129 },
      'western australia': { north: -13.7, south: -35.2, east: 129, west: 112.9 },
      'wa': { north: -13.7, south: -35.2, east: 129, west: 112.9 },
      'tasmania': { north: -39.5, south: -43.7, east: 148.5, west: 144 },
      'tas': { north: -39.5, south: -43.7, east: 148.5, west: 144 },
      'northern territory': { north: -10.9, south: -26.1, east: 138, west: 129 },
      'nt': { north: -10.9, south: -26.1, east: 138, west: 129 },
      'act': { north: -35.1, south: -35.9, east: 149.4, west: 148.7 },
      'australian capital territory': { north: -35.1, south: -35.9, east: 149.4, west: 148.7 },
    };

    const broadBounds = broadSearchBounds[targetLocation.toLowerCase()];
    if (broadBounds) {
      setIsRouteSearch(false);
      setTripStops([]);
      setActiveTripId(null);
      setMapViewMode('search');
      setIsSearching(true);
      setMapBoundsEntities([]);

      try {
        const { searchEntitiesByBounds } = await import('@/app/lib/searchEntities');
        const filters: Record<string, any> = {};
        if (selectedType === 'Accommodation') {
          filters.entity_type = 'accom';
        } else if (selectedType !== 'All') {
          filters.campsite_type = selectedType;
        }
        if (selectedFeatures.length > 0) {
          filters.features = selectedFeatures;
        }
        const response = await searchEntitiesByBounds({
          bounds: broadBounds,
          zoom: 5,
          limit: 500,
          filters,
        });
        const searchResults = response.entities || [];
        setEntities(searchResults);
        trackActivity('search', {
          term: targetLocation,
          resultCount: response.total || searchResults.length,
          meta: { scope: 'broad', type: selectedType, features: selectedFeatures.length > 0 },
        });
        if (searchResults.length === 0) {
          message.info(`No campsites found in ${targetLocation}`);
        } else {
          setSearchTotal(response.total || 0, response.truncated ? response.total_matching : undefined);
          message.success(`Found ${(response.truncated && response.total_matching ? response.total_matching : response.total || 0).toLocaleString('en-AU')} campsites across ${targetLocation}`);
        }

        // Fit map to the search bounds
        const centerLat = (broadBounds.north + broadBounds.south) / 2;
        const centerLng = (broadBounds.east + broadBounds.west) / 2;
        const latSpan = broadBounds.north - broadBounds.south;
        const lngSpan = broadBounds.east - broadBounds.west;
        const span = Math.max(latSpan, lngSpan);
        let zoom = 5;
        if (span < 5) zoom = 7;
        else if (span < 10) zoom = 6;
        setMapCenter({ lat: centerLat, lng: centerLng });
        setMapZoom(zoom);

        if (mapInstanceRef.current) {
          const gBounds = new google.maps.LatLngBounds(
            { lat: broadBounds.south, lng: broadBounds.west },
            { lat: broadBounds.north, lng: broadBounds.east }
          );
          mapInstanceRef.current.fitBounds(gBounds, { top: 40, right: 40, bottom: 40, left: 40 });
        }
      } catch (error) {
        console.error('Broad search error:', error);
        message.error('Failed to search');
      } finally {
        setIsSearching(false);
      }
      return;
    }

    const hasActiveTrip = tripStops.length > 0;

    setIsRouteSearch(false);
    if (!hasActiveTrip) {
      setTripStops([]);
      setActiveTripId(null);
      setMapViewMode('search');
    }
    setIsSearching(true);
    // Clear bounds entities so displayEntities shows search results, not stale map data
    setMapBoundsEntities([]);

    try {
      const params: EnhancedSearchParams = {
        location: targetLocation,
        radius: targetRadius,
        page: 1,
        per_page: 200,
        search_type: 'location'
      };

      if (selectedType === 'Accommodation') {
        params.entity_type = 'accom';
      } else if (selectedType !== 'All') {
        params.type = selectedType;
      }

      if (selectedFeatures.length > 0) {
        params.features = selectedFeatures;
      }

      // Run location search and name search in parallel
      const [locationResponse, nameResponse] = await Promise.all([
        search(params),
        search({ ...params, search_type: 'name' }),
      ]);

      // Merge results: name matches first, then location results (deduped)
      const nameResults = nameResponse.entities || [];
      const locationResults = locationResponse.entities || [];
      const nameIds = new Set(nameResults.map((e: any) => e.id || e.entityId || e.name));
      const dedupedLocation = locationResults.filter((e: any) => !nameIds.has(e.id || e.entityId || e.name));
      const mergedResults = [...nameResults, ...dedupedLocation];
      const response = mergedResults.length > 0
        ? { entities: mergedResults, total: mergedResults.length }
        : locationResponse;

      if (response.entities?.length === 0) {
        const resultLabel = selectedType === 'Accommodation' ? 'accommodation' : selectedType !== 'All' ? selectedType.toLowerCase() : 'results';
        message.info(`No ${resultLabel} found near ${targetLocation}`);
      }

      const searchResults = response.entities || [];
      setEntities(searchResults);

      // GA4: Track search event
      trackSearch({
        searchTerm: targetLocation,
        typeFilter: selectedType,
        resultCount: response.total || searchResults.length,
        radius: targetRadius,
        hasFeatureFilters: selectedFeatures.length > 0,
      });

      await new Promise(resolve => setTimeout(resolve, 200));
      
      if (searchResults.length > 0) {
        const coords: { lat: number; lng: number }[] = [];

        searchResults.forEach(entity => {
          if (entity.addresses && entity.addresses[0]) {
            const latStr = entity.addresses[0].gps_latitude;
            const lngStr = entity.addresses[0].gps_longitude;

            if (latStr && lngStr) {
              const lat = parseFloat(latStr);
              const lng = parseFloat(lngStr);

              if (!isNaN(lat) && !isNaN(lng)) {
                coords.push({ lat, lng });
              }
            }
          }
        });

        if (coords.length > 0) {
          // Always update map state so it works in both list and map views
          const lats = coords.map(c => c.lat);
          const lngs = coords.map(c => c.lng);
          const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
          const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
          const latSpan = Math.max(...lats) - Math.min(...lats);
          const lngSpan = Math.max(...lngs) - Math.min(...lngs);
          const span = Math.max(latSpan, lngSpan);
          let zoom = 13;
          if (span > 10) zoom = 5;
          else if (span > 5) zoom = 7;
          else if (span > 2) zoom = 8;
          else if (span > 1) zoom = 9;
          else if (span > 0.5) zoom = 10;
          else if (span > 0.2) zoom = 11;
          else if (span > 0.05) zoom = 12;

          setMapCenter({ lat: centerLat, lng: centerLng });
          setMapZoom(zoom);

          // If map instance is available, also use fitBounds for smooth animation.
          // Not while a trip is on the map: taking a popular trip names the
          // search after it ("Great Ocean Road"), and fitting to those results
          // zoomed the just-framed Melbourne → Adelaide trip in on its middle.
          if (mapInstanceRef.current && !tripOnMapRef.current) {
            const bounds = new google.maps.LatLngBounds();
            coords.forEach(c => bounds.extend(c));
            mapInstanceRef.current.fitBounds(bounds, {
              top: 80, right: 80, bottom: 80, left: 80
            });

            setTimeout(() => {
              const currentZoom = mapInstanceRef.current?.getZoom();
              if (currentZoom && currentZoom > 13) {
                mapInstanceRef.current?.setZoom(13);
              }
            }, 300);
          }
        } else {
          await geocodeAndZoomToLocation(targetLocation);
        }
      } else {
        await geocodeAndZoomToLocation(targetLocation);
      }
      
      scrollResultsTop();

      const resultLabel2 = selectedType === 'Accommodation' ? 'accommodation' : selectedType !== 'All' ? selectedType.toLowerCase() : 'results';
      setSearchTotal(response.total || 0);
      message.success(`Found ${response.total} ${resultLabel2}`);
    } catch (error) {
      console.error('Search error:', error);
      message.error('Failed to search. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };
  const handleSearchRef = useRef<typeof handleSearch>();
  handleSearchRef.current = handleSearch;

  useEffect(() => {
    if (typeof window === 'undefined' || !searchParams) {
      return;
    }

    // ?plan=1 opens the trip panel on arrival. This is where /trip-planner
    // now lands: that page is retired, and dropping people on the explorer
    // with the planner collapsed would look like the link was broken.
    // Every map-framing deep link (plan / region / route / location) loads the
    // map straight away instead of waiting for it to scroll into view.
    if (hasMapIntentInUrl(searchParams)) {
      setMapRequested(true);
    }
    if (searchParams.get('plan') === '1' || searchParams.get('night')) {
      setIsTripSectionOpen(true);
      setPlannerIntent(true);
      // The planner lives at the top of the results column, under the hero —
      // open it AND bring it on screen, or the link still looks broken. The
      // hero keeps settling (fonts, images, lazy chips) for a second or so,
      // which left the first scroll short at 1400×900 — so aim again once it
      // has, unless the user has scrolled somewhere themselves in between.
      let userScrolled = false;
      let ours = false;
      const onUserScroll = () => {
        if (!ours) userScrolled = true;
      };
      const aim = () => {
        if (userScrolled) return;
        ours = true;
        scrollPlannerIntoViewRef.current?.();
        setTimeout(() => {
          ours = false;
        }, 900);
      };
      window.addEventListener('wheel', onUserScroll, { passive: true });
      window.addEventListener('touchmove', onUserScroll, { passive: true });
      window.addEventListener('keydown', onUserScroll);
      setTimeout(aim, 350);
      setTimeout(aim, 1400);
      setTimeout(() => {
        window.removeEventListener('wheel', onUserScroll);
        window.removeEventListener('touchmove', onUserScroll);
        window.removeEventListener('keydown', onUserScroll);
      }, 2500);
    }

    // Map URL tab slugs to selectedType values
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const tabMapping: Record<string, string> = {
        'caravan-parks': 'Caravan Parks',
        'camping-sites': 'Camping Sites',
        'free-camping': 'Free Camping',
        'activities': 'Activities',
        'accommodation': 'Accommodation',
      };
      const mappedType = tabMapping[tabParam.toLowerCase()];
      if (mappedType) {
        setSelectedType(mappedType);
      }
    }

    // ?region=<slug> — region pages link here to outline the area + show camps.
    const regionParam = searchParams.get('region');
    if (regionParam) {
      const q = `region:${regionParam.replace(/-/g, ' ')}`;
      setSearchQuery(q);
      setTimeout(() => {
        handleSearchRef.current?.({ location: q });
      }, 300);
      return;
    }

    const startPointParam = searchParams.get('startPoint');
    const endPointParam = searchParams.get('endPoint');
    const routeNameParam = searchParams.get('routeName');
    const isRoute = searchParams.get('isRouteSearch') === 'true';
    const rerunLocation = searchParams.get('rerun');

    if (isRoute && startPointParam && endPointParam) {
      const viaParam = searchParams.get('via');
      const viaList = viaParam
        ? viaParam
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean)
        : [];

      const queryString = viaList.length
        ? `${startPointParam} to ${endPointParam} via ${viaList.join(', ')}`
        : `${startPointParam} to ${endPointParam}`;

      setSearchQuery(queryString);

      setTimeout(() => {
        handleRouteSearchRef.current?.(
          startPointParam,
          endPointParam,
          routeNameParam || `${startPointParam} to ${endPointParam}`,
          viaList
        );
      }, 300);
      return;
    }

    // Support both ?location= and ?search= params
    const location = rerunLocation || searchParams.get('location') || searchParams.get('search');
    if (location) {
      const locationRadius = searchParams.get('radius');
      const parsedRadius = locationRadius ? parseInt(locationRadius, 10) : undefined;

      handleSearchRef.current?.({
        location,
        radius: parsedRadius && !Number.isNaN(parsedRadius) ? parsedRadius : undefined,
      });
    }
  }, [searchParamsKey]);

  // Auto-search after user stops typing
  useEffect(() => {
    if (
      !isRouteSearch &&
      searchQuery &&
      searchQuery.length >= 3 &&
      !searchQuery.includes(' to ')
    ) {
      const timer = setTimeout(() => {
        handleSearch();
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [searchQuery, isRouteSearch]);

  const handleMapChange = (bounds: google.maps.LatLngBounds | null) => {
    // Optional: Add bounds change handling
  };

  const handleBoundsEntitiesLoaded = useCallback((loaded: Entity[], matching?: number) => {
    setMapBoundsEntities(loaded);
    setBoundsMatching(matching && matching > loaded.length ? matching : 0);
  }, []);

  // Fetch bulk availability when entities load and dates are set
  const availabilityActive = !!(checkInDate && checkOutDate);
  const fetchAvailabilityRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Bookeasy availability is an AU (camping-australia) surface only.
    if (!BRAND.showBookeasy) return;
    if (!availabilityActive) {
      setAvailabilityData({});
      return;
    }

    const entitiesToCheck = mapBoundsEntities.length > 0 ? mapBoundsEntities : entities;
    if (entitiesToCheck.length === 0) return;

    // Abort any in-flight request
    fetchAvailabilityRef.current?.abort();
    const controller = new AbortController();
    fetchAvailabilityRef.current = controller;

    const entityIds = entitiesToCheck.map(e => e.id).slice(0, 200);

    fetch('https://app.trip-planner.com.au/api/entities/bulk-availability-status', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        entity_ids: entityIds,
        check_in: checkInDate,
        check_out: checkOutDate,
      }),
      signal: controller.signal,
    })
      .then(res => res.json())
      .then(data => {
        if (!controller.signal.aborted && data.availability) {
          setAvailabilityData(prev => ({ ...prev, ...data.availability }));
        }
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          console.warn('Bulk availability fetch failed:', err);
        }
      });

    return () => controller.abort();
  }, [mapBoundsEntities, entities, checkInDate, checkOutDate, availabilityActive]);

  const handleDateChange = useCallback((checkIn: string, checkOut: string) => {
    setCheckInDate(checkIn);
    setCheckOutDate(checkOut);
  }, []);

  const focusEntityInList = useCallback((entity: Entity) => {
    setSelectedEntity(entity);
    const entityElement = entityRefs.current.get(entity.id);
    if (entityElement && sidebarRef.current) {
      requestAnimationFrame(() => {
        entityElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      });
    }
  }, []);

  const handleRouteDrawn = useCallback((result: google.maps.DirectionsResult) => {
    if (!result?.routes?.length) return;

    const [primaryRoute] = result.routes;
    const legs = primaryRoute.legs ?? [];
    if (!legs.length) return;

    // The road line itself, for snapping dropped pins onto the route and for
    // "3 km off route" on search rows.
    const overview = primaryRoute.overview_path ?? [];
    setGooglePath(
      overview.length >= 2
        ? { key: stopIdsKeyRef.current, path: overview.map((pt) => ({ lat: pt.lat(), lng: pt.lng() })) }
        : null
    );

    setMapDrewKey(stopIdsKeyRef.current);
    // (Google's per-leg figures arrive via onRouteLegs -> handleRouteLegs,
    // keyed by the stop ids the route was drawn for, with each leg's roads.)
    // The road itself, per leg: leg camp searches follow it. Thinned: a
    // long leg has thousands of points. Only a result covering the whole
    // trip counts (a chunked route can't be matched leg for leg).
    if (legs.length === stopIdsKeyRef.current.split('|').length - 1) {
      setGoogleLegPaths({
        key: stopIdsKeyRef.current,
        paths: legs.map((leg) => {
          const pts: LatLng[] = [];
          (leg.steps ?? []).forEach((step) =>
            (step.path ?? []).forEach((pt) => pts.push({ lat: pt.lat(), lng: pt.lng() }))
          );
          const step = Math.max(1, Math.ceil(pts.length / 600));
          const thin = pts.filter((_, i) => i % step === 0);
          if (pts.length && thin[thin.length - 1] !== pts[pts.length - 1]) thin.push(pts[pts.length - 1]);
          return thin;
        }),
      });
    }

    // Sync stop coordinates with the routed path so markers reflect every stop.
    setTripStops((prev) => {
      if (!prev.length) return prev;
      const moved = legs.some((leg, index) => {
        const a = leg.start_location;
        const b = leg.end_location;
        const s = prev[index];
        const e = prev[index + 1];
        const off = (p: google.maps.LatLng | undefined, st: TripStop | undefined) =>
          !!p && !!st && (st.lat !== p.lat() || st.lng !== p.lng());
        return off(a, s) || off(b, e);
      });
      if (!moved) return prev;
      const updated = prev.map((stop) => ({ ...stop }));

      legs.forEach((leg, index) => {
        const startLocation = leg.start_location;
        const endLocation = leg.end_location;
        if (startLocation && updated[index]) {
          updated[index] = {
            ...updated[index],
            lat: startLocation.lat(),
            lng: startLocation.lng(),
          };
        }
        const endIndex = index + 1;
        if (endLocation && updated[endIndex]) {
          updated[endIndex] = {
            ...updated[endIndex],
            lat: endLocation.lat(),
            lng: endLocation.lng(),
          };
        }
      });

      return updated;
    });
  }, []);

  const handleEntityClick = (entity: Entity) => {
    setSelectedEntity(entity);
    focusEntityInList(entity);

    // GA4: Track campsite card click
    trackSelectItem({
      entityId: entity.id,
      entityName: entity.name,
      entityType: entity.entity_type,
      listName: 'search_results',
    });

    // Center map on the selected entity
    const lat = parseFloat(entity.addresses?.[0]?.gps_latitude || '');
    const lng = parseFloat(entity.addresses?.[0]?.gps_longitude || '');
    if (!isNaN(lat) && !isNaN(lng)) {
      setMapCenter({ lat, lng });
      if (mapZoom < 10) setMapZoom(12);
    }

    // Also open details in a new tab
    handleViewEntityDetails(entity);
  };

  const handleViewEntityDetails = (entity: Entity) => {
    const safeName = entity.name || 'campsite';
    const slug = safeName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-+/g, '-');

    const currentParams = new URLSearchParams(window.location.search);
    if (searchQuery) currentParams.set('from', searchQuery);
    if (isRouteSearch && startPoint && endPoint) {
      currentParams.set('route', `${startPoint}|${endPoint}`);
    }

    const paramsString = currentParams.toString();
    const url = withBase(`/camp-sites/${encodeURIComponent(slug)}${paramsString ? `?${paramsString}` : ''}`);

    window.open(url, '_blank', 'noopener');
  };

  const handleFreeCampingClick = async () => {
    setSearchQuery(getCountryName());
    setSelectedType('Free Camping');
    setSelectedFeatures(['Free Overnight Camp']);
    setIsRouteSearch(false);
    setTripStops([]);
    
    setIsSearching(true);
    try {
      const response = await search({
        location: getCountryName(),
        radius: 500,
        features: ['Free Overnight Camp'],
        type: 'Free Camping',
        page: 1,
        per_page: 50,
        search_type: 'location'
      });
      
      setEntities(response.entities || []);
      
      scrollResultsTop();
      
      setSearchTotal(response.total || 0);
      message.success(`Found ${response.total} free camping spots`);
    } catch (error) {
      console.error('Free camping search error:', error);
      message.error('Failed to search free camping spots');
    } finally {
      setIsSearching(false);
    }
  };

  // Build bounds search filters from selected type/features for map component
  const boundsSearchFilters = useMemo(() => {
    const filters: { campsite_type?: string; entity_type?: string; features?: string[]; [key: string]: any } = {};
    if (selectedType === 'Accommodation') {
      filters.entity_type = 'accom';
    } else if (selectedType !== 'All') {
      filters.campsite_type = selectedType;
    }
    if (selectedFeatures.length > 0) {
      filters.features = selectedFeatures;
    }
    return filters;
  }, [selectedType, selectedFeatures]);

  // Clear stale text-search results when tab/features change so the map
  // only shows the (correctly filtered) bounds-search entities
  const prevTypeRef = useRef(selectedType);
  const prevFeaturesRef = useRef(selectedFeatures);
  useEffect(() => {
    if (prevTypeRef.current !== selectedType || prevFeaturesRef.current !== selectedFeatures) {
      prevTypeRef.current = selectedType;
      prevFeaturesRef.current = selectedFeatures;
      // Clear old text-search entities so map markers only come from bounds search
      setEntities([]);
      setMapBoundsEntities([]);
    }
  }, [selectedType, selectedFeatures]);

  // ── Legs + totals: one source of truth ─────────────────────────────────
  const stopIdsKey = useMemo(() => tripStops.map((s) => s.id).join('|'), [tripStops]);
  const [takenCover, setTakenCover] = useState<{ url: string; name: string; key: string } | null>(null);
  // Just taken from a shared page: the welcome at the top of the plan, and
  // the way back ("Shared trip > Your copy"). Both go once the trip is edited.
  const [takenNotice, setTakenNotice] = useState<{ name: string; key: string } | null>(null);
  const [takenFrom, setTakenFrom] = useState<{ path: string; key: string } | null>(null);
  // The shared page's road, held over the map until the live map draws its own.
  const [takenRoute, setTakenRoute] = useState<{ key: string; route: TakenRoute } | null>(null);
  /** The stops the live map last drew a road for. */
  const [mapDrewKey, setMapDrewKey] = useState<string | null>(null);
  const takenActive = !!takenNotice && takenNotice.key === stopIdsKey;
  const takenTrail = !!takenFrom && takenFrom.key === stopIdsKey;
  stopIdsKeyRef.current = stopIdsKey;
  // Routing is signed-in only (GraphHopper bills per request). Guests plan
  // stops + nights and save, with "≈" straight-line figures and a prompt to
  // sign in for the real route, drive times and night planning.
  const canRoute = isLoggedIn;
  const { tripLegInfo, tripLegs, routeSummary } = useTripLegs({
    tripStops,
    canRoute,
    stopIdsKey,
    vehicleRoute,
    googleLegs,
    vehicleType: tripVehicleType,
    // One set of figures, signed in or not: the road route at the rig's
    // pace. The vehicle route still draws the line (and fills in when
    // there's no road route yet).
    preferRoad: true,
  });

  // /saved shows the planner's own road figures for this exact stop list
  // (utils/legFigures) instead of re-estimating them from straight lines.
  useEffect(() => {
    if (tripLegInfo.source === 'estimate' || tripStops.length < 2) return;
    rememberLegFigures(tripStops, tripLegs, tripLegInfo.source);
  }, [tripLegInfo.source, tripLegs, tripStops]);

  // ── Map <-> list: one trip ────────────────────────────────────────────────
  // Road legs the map computed come back here (they only count while they
  // match the current stop list: pickTripLegs checks the key).
  const handleRouteLegs = useCallback((legs: TripLeg[]) => {
    const set = figureSetFromLegs(legs);
    if (set) setGoogleLegs(set);
  }, []);
  // The on-map chip prints the panel's own totals, so the two always agree.
  const mapRouteSummary = useMemo(
    () => ({ ...routeSummary, estimate: tripLegInfo.source === 'estimate' }),
    [routeSummary, tripLegInfo.source]
  );
  // A removed stop can't stay picked or hovered.
  useEffect(() => {
    if (selectedStopId && !tripStops.some((s) => s.id === selectedStopId)) setSelectedStopId(null);
    if (hoveredStopId && !tripStops.some((s) => s.id === hoveredStopId)) setHoveredStopId(null);
    if (hoveredLegToId && !tripStops.some((s) => s.id === hoveredLegToId)) setHoveredLegToId(null);
  }, [tripStops, selectedStopId, hoveredStopId, hoveredLegToId]);
  // Picking a stop twice lets it go.
  const handleSelectStop = useCallback((id: string | null) => setSelectedStopId((cur) => (cur === id ? null : id)), []);
  // The header's figures wait for the road route (the one that sticks)
  // instead of showing a straight line, then a number that changes a second
  // later. Gives up after a few seconds and shows the best it has.
  const routePending = useRoutePending(
    tripPlaceCount(tripStops) >= 2 ? `${stopIdsKey}|road` : '',
    tripLegInfo.source === 'google'
  );
  // Where the trip already is: place search ranks results near these first.
  const tripAnchors = useMemo(
    () =>
      tripStops
        .filter((s) => typeof s.lat === 'number' && typeof s.lng === 'number')
        .map((s) => ({ lat: s.lat as number, lng: s.lng as number })),
    [tripStops]
  );
  // Routed legs only (a straight-line estimate can't teach a road factor),
  // for the "+100 km" a search row says a pick would add.
  const routedLegsRef = useRef<TripLeg[]>([]);
  routedLegsRef.current = tripLegInfo.source === 'estimate' ? [] : tripLegs;
  // The road line the trip is drawn with right now (vehicle route when signed
  // in, else Google's), or null before one exists. Stale lines are ignored.
  const tripRoutePath = useMemo(() => {
    if (tripStops.length < 2) return null;
    if (canRoute && routePathOverride && routePathOverride.length >= 2) return routePathOverride;
    return googlePath && googlePath.key === stopIdsKey ? googlePath.path : null;
  }, [tripStops.length, canRoute, routePathOverride, googlePath, stopIdsKey]);
  tripRoutePathRef.current = tripRoutePath;
  // "Added Caragabal · +399 km, +4h 34m": an add says what it did to the
  // drive once the new road figures land. The figures before the add are
  // the last complete routed totals for the previous set of stops.
  const lastRoadTotalsRef = useRef<{ key: string; km: number; minutes: number } | null>(null);
  const costBaseRef = useRef<{ key: string; base: { km: number; minutes: number } } | null>(null);
  const roadTotals =
    tripLegInfo.source !== 'estimate' &&
    tripStops.length >= 2 &&
    tripLegs.length === tripStops.length - 1 &&
    tripLegs.every((l) => Number.isFinite(l.distanceKm) && Number.isFinite(l.driveMinutes))
      ? { km: routeSummary?.distanceKm ?? 0, minutes: routeSummary?.driveMinutes ?? 0 }
      : null;
  useEffect(() => {
    const msg = tripStatusMessage;
    if (!msg?.measureCost || msg.cost) return;
    const last = lastRoadTotalsRef.current;
    if (costBaseRef.current?.key === stopIdsKey) return;
    costBaseRef.current = last && last.key !== stopIdsKey ? { key: stopIdsKey, base: last } : null;
  }, [tripStatusMessage, stopIdsKey]);
  useEffect(() => {
    if (!roadTotals) return;
    const base = costBaseRef.current;
    if (base && base.key === stopIdsKey) {
      const cost = addCostLabel(base.base, roadTotals);
      costBaseRef.current = null;
      const costHeavy = addCostIsHeavy(base.base, roadTotals);
      if (cost) setTripStatusMessage((m) => (m && m.measureCost && !m.cost ? { ...m, cost, costHeavy } : m));
    }
    lastRoadTotalsRef.current = { key: stopIdsKey, ...roadTotals };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roadTotals?.km, roadTotals?.minutes, stopIdsKey]);

  // Each row in the "Add a stop" list says what that pick would add to the
  // drive, and the highlighted one says where it would go.
  const topPlacement = useCallback(
    (pt: { lat: number; lng: number }) => placementInfo(tripStopsRef.current, pt, routedLegsRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  // The leg whose inline "add a stop here" box is open: the top box greys out
  // meanwhile, so only one search field is live at a time.
  const [ladderInsertAt, setLadderInsertAt] = useState<number | null>(null);

  // ── Plan my nights ───────────────────────────────────────────────────────
  // Assigned once reverseGeocodeName is declared further down.
  const reverseGeocodeNameRef = useRef<(lat: number, lng: number) => Promise<string>>(async () => '');
  // Day split + campsite picks need the routed drive times → signed in only.
  const { nightPlan } = useNightPlan({
    canRoute,
    tripStops,
    tripLegs,
    maxDailyDriveHours,
    tripStartDate,
    vehicleRoute,
    stopIdsKey,
    slotNames,
  });

  // Numbered night markers on the route for suggested halfway nights (the
  // overnight stops already have their own pins).
  const nightMarkers = useMemo(() => {
    if (!nightPlan) return routeAnchors;
    return nightPlan.slots
      .filter((s) => s.suggested && typeof s.lat === 'number' && typeof s.lng === 'number')
      .map((s) => ({ lat: s.lat!, lng: s.lng!, day: s.night }));
  }, [nightPlan, routeAnchors]);

  // Guests get the same overnights (no drive times, so no halfway
  // suggestions): enough to show a real camp under every night.
  const guestNightPlan = useMemo(() => {
    if (canRoute || tripStops.length < 2) return null;
    return planNights({ stops: tripStops, legs: tripLegs, maxDriveMinutes: 0, startDate: tripStartDate });
  }, [canRoute, tripStops, tripLegs, tripStartDate]);
  const cardPlan = canRoute ? nightPlan : guestNightPlan;

  // The line each leg follows: the routed road when we have it (vehicle
  // route, else the one Google drew), straight between stops otherwise.
  const tripLegLines = useMemo(
    () =>
      buildLegLines(tripStops, {
        stopIdsKey,
        vehicle: vehicleRoute,
        // The map's own drawing first, else the road a phone asked for.
        google: googleLegPaths?.key === stopIdsKey ? googleLegPaths : googleLegs,
      }),
    [tripStops, stopIdsKey, vehicleRoute, googleLegs, googleLegPaths]
  );

  // Camps for every night: one bounds search per overnight (public, so
  // guests get real camps too) plus, signed in, the rig-aware ranking from
  // /routing/suggestions for all nights in one request. Re-runs when the
  // nights, the vehicle or the dates change.
  const slotsSignature = useMemo(
    () =>
      cardPlan
        ? cardPlan.slots
            .filter((s) => typeof s.lat === 'number' && typeof s.lng === 'number')
            .map((s) => `${s.key}@${s.lat!.toFixed(3)},${s.lng!.toFixed(3)}#${s.night}`)
            .join('|') + `|${tripVehicleType}|${tripStartDate ?? ''}|${canRoute ? 'm' : 'g'}|${nightCampsRetry}`
        : '',
    [cardPlan, tripVehicleType, tripStartDate, canRoute, nightCampsRetry]
  );
  const cardPlanRef = useRef(cardPlan);
  cardPlanRef.current = cardPlan;
  useEffect(() => {
    const plan = cardPlanRef.current;
    if (!plan || !slotsSignature) return;
    const slots = plan.slots.filter((s) => typeof s.lat === 'number' && typeof s.lng === 'number');
    if (!slots.length) return;
    let cancelled = false;
    setNightCamps((prev) =>
      Object.fromEntries(slots.map((s) => [s.key, { status: 'loading' as const, options: prev[s.key]?.options ?? [] }]))
    );
    const timer = setTimeout(() => {
      const nearbyP = Promise.all(
        slots.map((s) =>
          fetchEntitiesNear({ lat: s.lat!, lng: s.lng! }, NIGHT_RADIUS_KM)
            .then((list) => list.map(campFromEntity).filter((c): c is CampOption => !!c))
            .catch(() => null)
        )
      );
      const rankedP = canRoute
        ? fetchSuggestions({
            daily_anchors: slots.map((s) => ({ day: s.night, lat: s.lat!, lng: s.lng! })),
            dates: tripStartDate ? { start: tripStartDate } : undefined,
            prefs: { vehicle_type: tripVehicleType },
            radius_km: 30,
            limit: 5,
          }).catch(() => null)
        : Promise.resolve(null);
      void Promise.all([nearbyP, rankedP]).then(([nearby, ranked]) => {
        if (cancelled) return;
        const byNight = new Map((ranked?.per_day || []).map((d) => [d.day, d.candidates || []]));
        const next: Record<string, { status: NightCampStatus; options: CampOption[] }> = {};
        slots.forEach((s, i) => {
          const near = nearby[i];
          const rankedList = (byNight.get(s.night) || []).map(campFromCandidate);
          if (!near && !rankedList.length) {
            next[s.key] = { status: 'error', options: [] };
            return;
          }
          const options = dedupeCamps(
            mergeCampOptions(rankedList, rankNearby(near || [], { lat: s.lat!, lng: s.lng! }, NIGHT_RADIUS_KM)).filter(
              (c) => c.feeVerdict !== 'CLOSED'
            )
          ).slice(0, 12);
          options.forEach((c) => campCacheRef.current.set(c.id, c));
          next[s.key] = { status: 'ready', options };
        });
        setNightCamps(next);
        // Name suggested halfway nights after the town most picks sit in.
        const names: Record<string, string> = {};
        slots
          .filter((s) => s.suggested)
          .forEach((s) => {
            const towns = (next[s.key]?.options || []).map((c) => (c.town || '').trim()).filter(Boolean);
            if (!towns.length) return;
            const counts = new Map<string, number>();
            towns.forEach((t) => counts.set(t, (counts.get(t) ?? 0) + 1));
            names[s.key] = Array.from(counts.entries()).sort((a, b) => b[1] - a[1])[0][0];
          });
        if (Object.keys(names).length) setSlotNames((prev) => ({ ...prev, ...names }));
        // No campsite nearby to name it after: ask the map for the town.
        slots
          .filter((s) => s.suggested && !names[s.key])
          .forEach((s) => {
            void reverseGeocodeNameRef.current(s.lat!, s.lng!).then((town) => {
              if (cancelled || !town || /^custom stop/i.test(town)) return;
              setSlotNames((prev) => ({ ...prev, [s.key]: town.replace(/^Stop near /, '') }));
            });
          });
      });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // slotsSignature captures the slot set; the plan's identity alone churns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotsSignature]);

  // The vehicle changes the route (caravans route as a small truck), so
  // re-plan the drive when it does.
  const lastVehicleRef = useRef(tripVehicleType);
  useEffect(() => {
    if (lastVehicleRef.current === tripVehicleType) return;
    lastVehicleRef.current = tripVehicleType;
    if (tripStops.length >= 2) void planFromStopsRef.current(tripStops);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripVehicleType]);

  // A signed-in user's saved rig is the sensible default for a new trip.
  const vehicleDefaultedRef = useRef(false);
  useEffect(() => {
    if (!isLoggedIn || vehicleDefaultedRef.current) return;
    vehicleDefaultedRef.current = true;
    void getDefaultVehicle().then((v) => {
      const type = v?.type === 'sedan' ? 'car' : v?.type;
      if (type) setTripVehicleType((cur) => (cur === 'car' ? type : cur));
    });
  }, [isLoggedIn]);

  // Signing in (inline, without the OAuth reload) lights the planner up:
  // route the trip that was built as a guest. Signing out drops the routed
  // figures so a guest never sees stale drive times.
  const wasLoggedInRef = useRef(isLoggedIn);
  useEffect(() => {
    const was = wasLoggedInRef.current;
    wasLoggedInRef.current = isLoggedIn;
    if (isLoggedIn && !was && tripStopsRef.current.length >= 2) {
      void planFromStopsRef.current(tripStopsRef.current);
    }
    if (!isLoggedIn && was) {
      setVehicleRoute(null);
      setRoutePathOverride(null);
      setRouteAnchors([]);
      setRouteVehicle(null);
      setNightCamps({});
    }
  }, [isLoggedIn]);
  const tripEntitiesFallback = useMemo(
    () => convertTripStopsToEntities(tripStops),
    [tripStops]
  );
  const tripEntities = useMemo(() => {
    if (!tripStops.length) {
      return [];
    }
    const searchLookup = new Map(entities.map((entity) => [entity.id, entity]));
    return tripStops
      .map((stop, index) => {
        if (stop.entityId && searchLookup.has(stop.entityId)) {
          return searchLookup.get(stop.entityId)!;
        }
        return tripEntitiesFallback[index];
      })
      .filter((entity): entity is Entity => Boolean(entity));
  }, [tripStops, entities, tripEntitiesFallback]);
  const tripStopsWithCoords = useMemo(
    () =>
      tripStops.filter(
        (stop) => typeof stop.lat === 'number' && typeof stop.lng === 'number'
      ),
    [tripStops]
  );
  const tripEntityIds = useMemo(() => {
    const ids = tripStops
      .map((stop) => stop.entityId)
      .filter((id): id is number => typeof id === 'number');
    return new Set(ids);
  }, [tripStops]);

  // ── Find camps on this leg ──────────────────────────────────────────────
  // Camps within a few km of ONE drive (the road when routed, wider around a
  // straight line), in the order you pass them. Add makes it an overnight
  // between the leg's two stops.
  // Roads fetched for legs no map has drawn (phones don't mount the split map).
  const [fetchedLegRoads, setFetchedLegRoads] = useState<Record<string, LatLng[]>>({});
  const loadLegCamps = useCallback(
    async (legKey: string, legIndex: number) => {
      const base = tripLegLines?.[legIndex];
      if (!base) return;
      setLegCamps((prev) => ({ ...prev, [legKey]: { status: 'loading', road: true } }));
      // One method everywhere: the road, LEG_WIDTH_KM either side. When no
      // map has drawn the road (a phone), ask Directions for it rather than
      // search a wider band round a straight line.
      let points: LatLng[] | null = base.road ? base.points : fetchedLegRoads[legKey] ?? null;
      if (!points) {
        const a = base.points[0];
        const b = base.points[base.points.length - 1];
        const roads = await fetchRoadLegs([a, b]);
        points = roads?.[0] && roads[0].length > 1 ? roads[0] : null;
        if (points) setFetchedLegRoads((prev) => ({ ...prev, [legKey]: points as LatLng[] }));
      }
      const road = !!points;
      const line = points ?? base.points;
      const widthKm = road ? LEG_WIDTH_KM : LEG_WIDTH_STRAIGHT_KM;
      try {
        // One quiet retry: a single slow answer from the search shouldn't
        // leave a leg saying it couldn't reach it.
        const found = await fetchEntitiesAlong(line, widthKm + 2).catch(
          () =>
            new Promise<Awaited<ReturnType<typeof fetchEntitiesAlong>>>((res, rej) =>
              window.setTimeout(() => fetchEntitiesAlong(line, widthKm + 2).then(res, rej), 1500)
            )
        );
        const camps = found
          .map(campFromEntity)
          .filter(
            (c): c is CampOption => !!c && isCampType(c.type) && c.feeVerdict !== 'CLOSED' && !tripEntityIds.has(c.id)
          );
        // Camps in the leg's two towns belong to those nights' Swap lists:
        // two overnights a kilometre apart is not a plan. The same place
        // listed twice shows once.
        const items = dedupeCamps(trimLegEnds(campsAlongLine(camps, line, widthKm), lineKm(line)));
        items.forEach((c) => campCacheRef.current.set(c.id, c));
        setLegCamps((prev) => ({ ...prev, [legKey]: { status: 'ready', items, widthKm, road, lineKm: lineKm(line) } }));
      } catch {
        setLegCamps((prev) => ({ ...prev, [legKey]: { status: 'error', road } }));
      }
    },
    [tripLegLines, tripEntityIds, fetchedLegRoads]
  );
  // Load on open, and again once the road arrives for a leg first searched
  // around a straight line.
  useEffect(() => {
    if (!openLegKey || !tripLegLines) return;
    const i = tripLegLines.findIndex((l) => legKeyOf(l.fromId, l.toId) === openLegKey);
    if (i < 0) {
      setOpenLegKey(null);
      return;
    }
    const st = legCamps[openLegKey];
    // Re-run only when a straight-line fallback can now follow the road.
    if (!st || (st.status !== 'loading' && !st.road && tripLegLines[i].road)) void loadLegCamps(openLegKey, i);
    // legCamps is read, not watched: a finished load must not re-trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openLegKey, tripLegLines, loadLegCamps]);

  // Count the camps on a long leg when its "+" box opens, so the box can say
  // "22 camps on this drive" under its ideas.
  useEffect(() => {
    if (ladderInsertAt === null || !tripLegLines) return;
    const l = tripLegLines[ladderInsertAt];
    if (!l) return;
    const mins = tripLegs[ladderInsertAt]?.driveMinutes;
    if (typeof mins === 'number' && mins > 0 && mins < LEG_DISCOVERY_MIN_MINUTES) return;
    const key = legKeyOf(l.fromId, l.toId);
    if (!legCamps[key]) void loadLegCamps(key, ladderInsertAt);
    // legCamps is read, not watched: a finished load must not re-trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ladderInsertAt, tripLegLines, tripLegs, loadLegCamps]);

  /** The night a camp added after `fromId` becomes ("Night 2"). */
  const nightAfterStop = useCallback(
    (fromId: string) => {
      const at = tripStops.findIndex((s) => s.id === fromId);
      if (at < 0) return undefined;
      const before = tripStops.slice(1, at + 1).reduce((n, s) => n + Math.max(0, s.nights ?? 0), 0);
      return `Night ${before + 1}`;
    },
    [tripStops]
  );

  const handleAddLegCamp = useCallback((fromId: string, toId: string, c: CampOnLine) => {
    const stop = createTripStopFromName(c.name, 'camp', { entityId: c.id, lat: c.lat, lng: c.lng, nights: 1 });
    let names: [string, string] | null = null;
    setTripStops((prev) => {
      const at = prev.findIndex((s) => s.id === fromId);
      if (at < 0 || prev[at + 1]?.id !== toId) return prev;
      names = [prev[at].name, prev[at + 1].name];
      return [...prev.slice(0, at + 1), stop, ...prev.slice(at + 1)];
    });
    campCacheRef.current.set(c.id, c);
    setOpenLegKey(null);
    setLadderInsertAt(null);
    setActiveTripId(null);
    const pair = names as [string, string] | null;
    setTripStatusMessage({
      message: pair ? `Added “${c.name}” as a night between ${pair[0]} and ${pair[1]}` : `Added “${c.name}” to your trip`,
      tone: 'success',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Find camps on this leg": the open leg's road + the rows on screen.
  const openLegPath = useMemo(() => {
    if (!openLegKey || !tripLegLines) return null;
    const line = tripLegLines.find((l) => legKeyOf(l.fromId, l.toId) === openLegKey);
    if (!line) return null;
    return line.road ? line.points : fetchedLegRoads[openLegKey] ?? line.points;
  }, [openLegKey, tripLegLines, fetchedLegRoads]);
  const legPinProps = useMemo(
    () => [...legPins, ...swapPins].map(({ camp, n }) => ({ id: camp.id, lat: camp.lat, lng: camp.lng, n, name: camp.name })),
    [legPins, swapPins]
  );
  useEffect(() => {
    if (!openLegKey) {
      setLegPins([]);
      setHoveredLegCampId(null);
    }
  }, [openLegKey]);

  useEffect(() => {
    const prevCount = prevTripStopCountRef.current;
    const hasStops = tripStops.length > 0;

    if (isRouteSearch && tripStops.length >= 2) {
      setIsTripSectionOpen(true);
    } else if (!isRouteSearch && hasStops && prevCount === 0) {
      setIsTripSectionOpen(true);
    }
    // Emptying the trip ("New trip", last stop removed) leaves the panel
    // open: the empty planner is the place to start the next one.

    prevTripStopCountRef.current = tripStops.length;
  }, [isRouteSearch, tripStops.length]);

  const isTripView = mapViewMode === 'trip';
  // Total sites loaded via the all-points map path (reported by GoogleMapComponent),
  // so the "results" count reflects the whole map in the default browse view.
  const [allPointsTotal, setAllPointsTotal] = useState(0);
  // Set when the all-points load fails or never reports, so the count label
  // stops waiting and falls back to the result-page size.
  const [allPointsGaveUp, setAllPointsGaveUp] = useState(false);
  // Total reported by the last search/route (response.total). For a capped
  // bounds search that's the page size (200); `searchMatching` then holds the
  // real total_matching so the bar can say "6,513 results · showing 200".
  // Every search sets both through setSearchTotal, so a later uncapped search
  // never inherits an earlier search's total.
  const [searchTotal, setSearchTotalState] = useState(0);
  const [searchMatching, setSearchMatching] = useState(0);
  const setSearchTotal = useCallback((total: number, matching?: number) => {
    setSearchTotalState(total);
    setSearchMatching(matching && matching > total ? matching : 0);
  }, []);
  // The same for "Update as I move" (the in-view bounds search).
  const [boundsMatching, setBoundsMatching] = useState(0);
  // Show ALL sites on the map only in the default browse view (pure + unit-tested
  // in lib/mapPoints) — never during an active search/type-filter/route.
  const showAllPoints = shouldShowAllPoints({ mapViewMode, isRouteSearch, searchQuery, selectedType, featureSearch });

  useEffect(() => {
    if (tripStops.length === 0 && mapViewMode === 'trip') {
      setMapViewMode('search');
    }
  }, [tripStops.length, mapViewMode, setMapViewMode]);

  // i18n TODO (phase 2): the search-results surface below — result-panel
  // titles/counts, empty states, the trip-planner ladder and the trip
  // assistant — is a deep, string-dense area deferred past phase 1. Phase 1
  // covers the header, footer, hero/search (HeroSearch), campsite cards
  // (EntityCard) and the country/language switchers. Thread useTranslations
  // through HomePageContent here when localising the results experience.
  const searchPanelTitle =
    mapViewMode === 'trip'
      ? tripStops.length >= 2
        ? 'Trip stops'
        : 'Trip ladder'
      : isRouteSearch
        ? `${selectedType === 'Accommodation' ? 'Accommodation' : 'Campsites'} along your trip`
        : searchQuery
          ? `Results for ${searchQuery}`
          : selectedType === 'Accommodation' ? 'Featured accommodation' : 'Featured campsites';
  // Skeleton while the featured first page is still on its way, not just
  // during searches — otherwise the empty list said "No results found" for
  // the first few seconds of every visit.
  const listIsLoading = isResultsListLoading({
    mapViewMode,
    isSearching,
    isLoadingFeatured,
    isRouteSearch,
    searchQuery,
    resultCount: entities.length + mapBoundsEntities.length,
  });
  const tripPanelTitle =
    tripStops.length >= 2 ? 'Your trip itinerary' : 'Trip ladder';

  const searchResultCount =
    mapViewMode === 'trip' ? tripStops.length : isSearching ? 0 : entities.length;
  const hasTripStops = tripStops.length > 0;
  tripOnMapRef.current = mapViewMode === 'trip' && tripPlaceCount(tripStops) >= 2;
  const hasBuiltTrip = isRouteSearch && tripStops.length >= 2;

  // In trip mode the lettered stop pins ARE the trip (GoogleMapComponent
  // draws them). Passing the stops as entities too stacked a default pin on
  // top of every lettered one. Clicking a campsite stop's pin still opens its
  // card (onTripStopClick below).
  const mapEntities = useMemo(() => {
    if (!isTripView) return entities;
    return [] as Entity[];
  }, [isTripView, entities]);
  // A stop up close (its photo in the list, or a town's pin on the map).
  const [detailStopId, setDetailStopId] = useState<string | null>(null);
  const handleTripStopClick = useCallback(
    (stopId: string) => {
      const idx = tripStops.findIndex((s) => s.id === stopId);
      const entityId = idx >= 0 ? tripStops[idx].entityId : undefined;
      if (typeof entityId !== 'number') {
        if (idx >= 0) setDetailStopId(stopId);
        return;
      }
      const entity = tripEntities.find((e) => e.id === entityId);
      if (entity) handleEntityClick(entity);
    },
    [tripStops, tripEntities]
  );
  const visibleEntities = useMemo(
    () => (mapViewMode === 'trip' ? tripEntities : entities),
    [mapViewMode, tripEntities, entities]
  );
  useEffect(() => {
    if (mapViewMode === 'trip') {
      setVisibleCardCount(visibleEntities.length);
    } else {
      setVisibleCardCount(Math.min(48, visibleEntities.length || mapBoundsEntities.length));
    }
  }, [visibleEntities.length, mapBoundsEntities.length, mapViewMode]);

  const waypointNamesForRoute = useMemo(
    () =>
      tripStops
        .slice(1, -1)
        // Coordinates when we have them — Google Directions accepts "lat,lng"
        // waypoints natively, and a dragged via point must route through the
        // exact spot, not wherever its reverse-geocoded NAME resolves to.
        .map((stop) =>
          typeof stop.lat === 'number' && typeof stop.lng === 'number'
            ? `${stop.lat},${stop.lng}`
            : stop.name
        )
        .filter(Boolean),
    [tripStops]
  );

  const ladderRouteConfig = useMemo(() => {
    if (tripStopsWithCoords.length < 2) {
      return null;
    }

    const toWaypointString = (stop: TripStop) => {
      if (typeof stop.lat === 'number' && typeof stop.lng === 'number') {
        return `${stop.lat},${stop.lng}`;
      }
      return stop.name;
    };

    const startStop = tripStopsWithCoords[0];
    const endStop = tripStopsWithCoords[tripStopsWithCoords.length - 1];

    return {
      start: toWaypointString(startStop),
      end: toWaypointString(endStop),
      color: '#1a68d1',
      strokeWeight: 4,
      maxDeviationKm: routeCorridorWidth,
      waypoints: tripStopsWithCoords.slice(1, -1).map(toWaypointString),
      routeKey: tripRouteKey || computeTripRouteKey(tripStopsWithCoords),
    };
  }, [tripStopsWithCoords, routeCorridorWidth, tripRouteKey]);

  const activeRouteConfig = useMemo(() => {
    // The drawn route follows the CURRENT stop order. A route search seeds
    // the stops, but once they are reordered, removed or added to, the
    // search's original start/end strings are stale — drawing from them
    // showed a line that contradicted the list.
    if (ladderRouteConfig && tripStops.length >= 2 && tripStopsWithCoords.length === tripStops.length) {
      return ladderRouteConfig;
    }
    if (isRouteSearch && startPoint && endPoint) {
      return {
        start: startPoint,
        end: endPoint,
        color: '#1a68d1',
        strokeWeight: 4,
        maxDeviationKm: routeCorridorWidth,
        waypoints: waypointNamesForRoute,
        routeKey: tripRouteKey || null,
      };
    }
    return ladderRouteConfig;
  }, [
    isRouteSearch,
    startPoint,
    endPoint,
    routeCorridorWidth,
    waypointNamesForRoute,
    tripRouteKey,
    ladderRouteConfig,
    tripStops.length,
    tripStopsWithCoords.length,
  ]);

  // A round trip that hasn't left home is one place: no route to draw (a
  // Melbourne→Melbourne "route" zoomed the map to street level).
  const shouldShowRouteOnMap =
    !!activeRouteConfig &&
    tripStops.length >= 2 &&
    !isUnstartedLoop(tripStops) &&
    (isRouteSearch || mapViewMode === 'trip');

  // Trip-context deep-link for the van-hire promo: when the trip has dates,
  // send pickup/drop-off through so the fleet search opens pre-filled.
  const hireQuery = useMemo(() => {
    if (!tripStartDate || tripStops.length < 2) return undefined;
    const days = segmentIntoDays(tripStops, tripLegs, maxDailyDriveHours * 60);
    if (!days.length) return undefined;
    // Drop-off the morning after the last night on the road (rest days count).
    const dropOff = dateForTripDay(tripStartDate, days[days.length - 1].dayOffset + 1);
    return dropOff ? `?from=${tripStartDate}&to=${dropOff}` : undefined;
  }, [tripStartDate, tripStops, tripLegs, maxDailyDriveHours]);

  // The driving day each stop is reached on: the map colours its pins and
  // route pieces by day, the same colours as the itinerary's day headers.
  // Only once the list is in days; until then pins keep their role colours,
  // the same as the list's letters.
  const tripStopDays = useMemo(() => {
    const { days, show } = itineraryDays(tripStops, tripLegs, maxDailyDriveHours * 60, tripStartDate);
    return show ? { index: stopDayIndices(tripStops.length, days), days } : undefined;
  }, [tripStops, tripLegs, maxDailyDriveHours, tripStartDate]);
  // Each pin wears its list letter; before days its role colour, once the
  // list is in days the colour of the day it's reached on (and each leg of
  // the route the colour of the day it's driven), with that day's number.
  const tripStopsForMap = useMemo(
    () =>
      tripStops.map((s, i) => {
        const day = tripStopDays?.index[i];
        const dayNumber = typeof day === 'number' ? (tripStopDays?.days[day]?.dayOffset ?? day) + 1 : undefined;
        return { ...s, day, dayNumber, role: roleAt(tripStops, i) };
      }),
    [tripStops, tripStopDays]
  );

  // The map's type chips (All / Campsites / Free camps / Caravan / Stays).
  // They filter the list and the count as well as the pins — filtering only
  // the pins left "39 results" and the same cards, which read as broken.
  const [mapTypeFilter, setMapTypeFilter] = useState<string>('all');
  // When "Update as I move" is active and bounds entities are loaded, show those in the list
  const unfilteredDisplayEntities = useMemo(() => {
    if (mapViewMode === 'trip') {
      // Trip mode keeps SEARCH results in the list — the ladder above is the
      // trip UI, and swapping the list for trip-stop cards hid the
      // "+ Trip on any result below" path right after the first stop.
      if (searchAsMove && mapBoundsEntities.length > 0) return mapBoundsEntities;
      if (entities.length > 0) return entities;
      return visibleEntities;
    }
    if (searchAsMove && mapBoundsEntities.length > 0) return mapBoundsEntities;
    if (visibleEntities.length > 0) return visibleEntities;
    return mapBoundsEntities;
  }, [mapViewMode, searchAsMove, mapBoundsEntities, visibleEntities, entities]);
  const typeFilterActive = mapTypeFilter !== 'all' && mapViewMode !== 'trip';
  const displayEntities = useMemo(
    () => (typeFilterActive ? unfilteredDisplayEntities.filter((e) => matchesMapType(e, mapTypeFilter)) : unfilteredDisplayEntities),
    [typeFilterActive, unfilteredDisplayEntities, mapTypeFilter]
  );
  // Browse view: the count is the whole map (every site), so a chip's count
  // is that same full set, filtered — not the featured page beside it.
  const [typedPointsCount, setTypedPointsCount] = useState<number | null>(null);
  useEffect(() => {
    if (!typeFilterActive || !showAllPoints) {
      setTypedPointsCount(null);
      return;
    }
    let cancelled = false;
    fetchAllMapPoints().then((pts) => {
      if (cancelled) return;
      setTypedPointsCount(pts.length ? pts.filter((p) => matchesMapType(p, mapTypeFilter)).length : null);
    });
    return () => {
      cancelled = true;
    };
  }, [typeFilterActive, showAllPoints, mapTypeFilter]);

  // A trip with no route yet (just a start, or a round trip that hasn't left
  // home): list campsites near its places, nearest first — not whatever the
  // country-wide list led with ("Engoordina Ruins, NT" under a Melbourne trip).
  const tripHasNoRouteYet = hasTripStops && !isRouteSearch && tripPlaceCount(tripStops) < 2;
  // The country-wide list rarely holds anything near the start, so draw on
  // the map's full point set too (the same server-cached list the map loads).
  const [nearbyPool, setNearbyPool] = useState<Entity[] | null>(null);
  useEffect(() => {
    if (!tripHasNoRouteYet || nearbyPool) return;
    let cancelled = false;
    fetchAllMapPoints().then((pts) => {
      if (!cancelled) setNearbyPool(pts);
    });
    return () => {
      cancelled = true;
    };
  }, [tripHasNoRouteYet, nearbyPool]);
  const listEntities = useMemo(() => {
    if (!tripHasNoRouteYet) return displayEntities;
    // Full entities first (they carry town, features and photos); the map's
    // light points only fill in places the list doesn't have.
    const seen = new Set(displayEntities.map((e) => e.id));
    // "Camps near…" means somewhere to stay the night in your rig — not the
    // tours, apartments and points of interest the map also carries.
    const isCamp = (e: Entity) =>
      /camp|caravan|free|rest_area/i.test(String((e as Entity & { entityType?: string }).entityType || e.entity_type || ''));
    // Light points without a photo of their own would all show the same
    // stock fallback, one under the other — leave those to the map.
    // (Some points carry credit text — "Manual", "OSM feed" — in image_src.)
    const hasOwnPhoto = (e: Entity) => hasPhotoUrl(e.image_src);
    const pool = [
      ...displayEntities,
      ...(nearbyPool ?? []).filter((e) => !seen.has(e.id) && hasOwnPhoto(e)),
    ].filter(isCamp);
    const start = tripStops[0];
    const near = nearTripPlaces(
      pool,
      tripStops,
      (e) => {
        const lat = parseFloat(e.addresses?.[0]?.gps_latitude || '');
        const lng = parseFloat(e.addresses?.[0]?.gps_longitude || '');
        return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
      },
      haversineKm
    );
    // One photo, one camp: the same stock campfire twice reads as fake.
    const photoOf = (e: Entity) => e.image_src || null;
    return dedupeByPhoto(near, photoOf)
      .slice(0, 24)
      .map((e) => {
        // The map's light points carry no town: say how far it is instead
        // ("38 km from Melbourne") — the thing a planner wants to know anyway.
        const addr = e.addresses?.[0];
        if (!addr || addr.town || typeof start?.lat !== 'number' || typeof start?.lng !== 'number') return e;
        const km = haversineKm(start.lat, start.lng, parseFloat(addr.gps_latitude || ''), parseFloat(addr.gps_longitude || ''));
        if (!Number.isFinite(km)) return e;
        return { ...e, addresses: [{ ...addr, town: distanceFromStartLabel(km, start.name) }, ...(e.addresses?.slice(1) ?? [])] } as Entity;
      });
  }, [tripHasNoRouteYet, displayEntities, tripStops, nearbyPool]);
  // One place, no route yet: pin the "Camps near …" list on the map too, so
  // the list and the map are visibly the same camps.
  const nearStartPins = useMemo(
    () => (tripHasNoRouteYet ? listEntities.slice(0, 12) : null),
    [tripHasNoRouteYet, listEntities]
  );

  // ── Camps along the route, grouped by day ───────────────────────────────
  // A route search returns hundreds of campsites; a flat list of 300 isn't a
  // plan. Each one is placed on the leg it sits beside, turned into a "trip
  // minute", and filed under the drive day that covers it — with the ones
  // within 25 km of that night's stop first ("near tonight's stop").
  const listSections = useMemo((): {
    key: string;
    title: string | null;
    subtitle?: string;
    entities: Entity[];
    nearTonight: Set<number>;
  }[] => {
    // The empty planner shows campsites as ideas for a first stop: only the
    // ones with a photo (three grey placeholder tiles in a row look broken).
    const plannerEmpty = !hasTripStops && isTripSectionOpen && plannerIntent;
    const flat = (list: Entity[]) => {
      // Same test as the card itself (EntityCard's hasOwnImage).
      const hasPhoto = (e: Entity) => !!getOptimizedEntityImage(e, { width: 720, quality: 78, fallback: '' });
      const shown = plannerEmpty && list.some(hasPhoto) ? list.filter(hasPhoto) : list;
      return [{ key: 'all', title: null, entities: shown.slice(0, visibleCardCount), nearTonight: new Set<number>() }];
    };
    if (tripHasNoRouteYet) {
      return listEntities.length
        ? [{
            key: 'near-trip',
            title: `Camps near ${tripStops[0]?.name ?? 'your start'}`,
            subtitle: 'Nearest first. Tap + Trip to add one as a stop.',
            entities: listEntities,
            nearTonight: new Set<number>(),
          }]
        : [];
    }
    if (!isRouteSearch || mapViewMode === 'trip' || !nightPlan || tripStops.length < 2) {
      // Under a trip, say what the list is — it used to start, unlabelled,
      // straight under the timeline.
      if (hasTripStops && tripPlaceCount(tripStops) >= 2 && displayEntities.length) {
        return [{
          key: 'along',
          title: 'Camps along this route',
          subtitle: 'Tap + Trip to add one as a stop.',
          entities: displayEntities.slice(0, visibleCardCount),
          nearTonight: new Set<number>(),
        }];
      }
      return flat(displayEntities);
    }
    const located = tripStops.every((s) => typeof s.lat === 'number' && typeof s.lng === 'number');
    if (!located) return flat(displayEntities);
    const pts = tripStops as (TripStop & { lat: number; lng: number })[];
    const minutes = nightPlan.stopMinutes;
    const placed = displayEntities.map((e, order) => {
      const lat = parseFloat(e.addresses?.[0]?.gps_latitude || '');
      const lng = parseFloat(e.addresses?.[0]?.gps_longitude || '');
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return { e, order, day: null as number | null, minute: Infinity, near: false };
      const p = { lat, lng };
      const leg = Math.max(0, nearestLegIndex(pts, p));
      const a = pts[leg];
      const b = pts[leg + 1] ?? a;
      const da = haversineKm(a.lat, a.lng, lat, lng);
      const db = haversineKm(lat, lng, b.lat, b.lng);
      const frac = da + db > 0 ? da / (da + db) : 0;
      const minute = (minutes[leg] ?? 0) + frac * ((minutes[leg + 1] ?? minutes[leg] ?? 0) - (minutes[leg] ?? 0));
      const day = dayForMinute(nightPlan, minute);
      const slot = day?.slot;
      const near =
        !!slot && typeof slot.lat === 'number' && typeof slot.lng === 'number' &&
        haversineKm(slot.lat, slot.lng, lat, lng) <= 25;
      // Off-corridor safety: farther than 80 km from the route reads as noise.
      const off = pointToSegmentKm(p, a, b) > 80;
      return { e, order, day: off ? null : day?.index ?? null, minute, near };
    });
    const byDay = new Map<number, typeof placed>();
    const other: typeof placed = [];
    placed.forEach((x) => {
      if (x.day === null) other.push(x);
      else byDay.set(x.day, [...(byDay.get(x.day) ?? []), x]);
    });
    const ordered: { dayIndex: number | null; items: typeof placed }[] = Array.from(byDay.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([dayIndex, items]) => ({
        dayIndex,
        items: items.sort((a, b) => (a.near === b.near ? a.minute - b.minute : a.near ? -1 : 1)),
      }));
    if (other.length) ordered.push({ dayIndex: null, items: other });
    // Share the visible-card budget across the days, so Day 3 isn't hidden
    // behind 48 cards of Day 1; "Show more" raises every day's share.
    const perSection = Math.max(6, Math.ceil(visibleCardCount / Math.max(1, ordered.length)));
    const sections = [];
    for (const g of ordered) {
      const items = g.items.slice(0, perSection);
      const day = g.dayIndex === null ? null : nightPlan.days[g.dayIndex];
      const nearCount = g.items.filter((x) => x.near).length;
      sections.push({
        key: `day-${g.dayIndex ?? 'other'}`,
        title: day
          ? `Day ${day.index + 1} · ${day.fromName} → ${day.toName}`
          : 'Further off the route',
        subtitle: day
          ? `${g.items.length} campsite${g.items.length === 1 ? '' : 's'}${
              day.slot && nearCount ? ` · ${nearCount} near tonight’s stop (${day.slot.name})` : ''
            }`
          : undefined,
        entities: items.map((x) => x.e),
        nearTonight: new Set(items.filter((x) => x.near).map((x) => x.e.id)),
      });
    }
    return sections;
  }, [isRouteSearch, mapViewMode, nightPlan, tripStops, displayEntities, visibleCardCount, hasTripStops, isTripSectionOpen, plannerIntent, listEntities, tripHasNoRouteYet]);

  // null = the browse total isn't known yet → render a neutral label, never a
  // placeholder number (it used to read "0 results", then "200", then 5,184).
  const resultsCount = resolveResultsCount({
    searchAsMove,
    boundsCount: mapBoundsEntities.length,
    showAllPoints,
    allPointsTotal,
    allPointsGaveUp,
    searchTotal,
    mapEntitiesCount: mapEntities.length,
  });
  // One source for every "N trip stops" label (bar, map chip, panel).
  const mapCount = mapCountSummary({
    mapViewMode,
    tripStopCount: tripPlaceCount(tripStops),
    hasBuiltTrip,
    resultsCount,
    selectedType,
  });
  const typedCount = typeFilterActive
    ? showAllPoints && !(searchAsMove && mapBoundsEntities.length > 0)
      ? typedPointsCount
      : displayEntities.length
    : null;
  const activeMapCount = typeFilterActive ? typedCount ?? 0 : mapCount.count ?? 0;
  const mapCountLabel = typeFilterActive && !hasBuiltTrip ? mapTypeNoun(mapTypeFilter, activeMapCount) ?? mapCount.label : mapCount.label;
  const mapCountKnown = typeFilterActive ? typedCount !== null : mapCount.count !== null;
  // A capped bounds search (e.g. all of Australia: 200 of 6,513) says so:
  // "6,513 results · showing 200", or with a type chip on,
  // "34 free camps in the 200 shown". Never in trip view, and never when the
  // typed count comes from the all-points map (that one covers every site).
  const matchingTotal =
    mapViewMode === 'trip' && hasTripStops
      ? null
      : resolveMatchingTotal({
          searchAsMove,
          boundsCount: mapBoundsEntities.length,
          boundsMatching,
          showAllPoints,
          allPointsTotal,
          searchTotal,
          searchMatching,
        });
  const countDisplay = truncatedCountDisplay({
    count: activeMapCount,
    matching: matchingTotal,
    shown: resultsCount ?? 0,
    typeFiltered: typeFilterActive,
  });
  const countNote = countDisplay.note;
  const shownMapCount = countDisplay.count;

  // "+ Trip" on a card or map pin. With planned nights (signed in), ask which
  // night it's for (the chooser below); otherwise it just joins the trip.
  const tripHasRouteRef = useRef(false);
  tripHasRouteRef.current = tripStops.length >= 2 && !!nightPlan && nightPlan.slots.length > 0;
  const handleAddEntityToTrip = useCallback((entity: Entity) => {
    if (tripHasRouteRef.current) {
      setNightChooserEntity(entity);
      return;
    }
    addEntityAsStopRef.current(entity);
  }, []);
  const addEntityAsStopRef = useRef<(entity: Entity) => void>(() => {});

  const addEntityAsStop = useCallback(
    (entity: Entity) => {
      const stop = createTripStopFromEntity(entity);
      let didAdd = false;
      let previousCount = 0;
      let nextStopsSnapshot: TripStop[] = [];

      setTripStops((prev) => {
        previousCount = prev.length;
        const result = mergeTripStopIntoList(prev, stop);
        if (!result.added) {
          return prev;
        }

        didAdd = true;
        nextStopsSnapshot = result.next;
        return result.next;
      });

      if (!didAdd) {
        message.info('That campsite is already in your trip.');
        return;
      }

      // GA4: Track add to trip
      trackAddToTrip({
        entityId: entity.id,
        entityName: entity.name,
        entityType: entity.entity_type,
        tripStopCount: nextStopsSnapshot.length,
      });

      setActiveTripId(null);
      setRouteName((currentName) => {
        const firstStopName = nextStopsSnapshot[0]?.name;
        const defaultFirstName =
          firstStopName && firstStopName.length > 0 ? `${firstStopName} trip` : 'My Trip';

        if (previousCount === 0) {
          return defaultFirstName;
        }

        if (previousCount === 1 && nextStopsSnapshot.length >= 2) {
          const firstName = nextStopsSnapshot[0]?.name ?? entity.name ?? 'Start';
          const lastName =
            nextStopsSnapshot[nextStopsSnapshot.length - 1]?.name ?? entity.name ?? 'Finish';
          const proposedName = `${firstName} to ${lastName}`;

          if (!currentName || currentName === defaultFirstName) {
            return proposedName;
          }
        }

        return currentName;
      });

      // No scrolling here: the click came from a card or pin the user is
      // looking at, and yanking the page moved other cards under the cursor.
      setTripStatusMessage({ message: `Added “${entity.name}” to your trip`, tone: 'success' });
    },
    [setActiveTripId, setRouteName]
  );
  addEntityAsStopRef.current = addEntityAsStop;

  reverseGeocodeNameRef.current = reverseGeocodeName;

  // ── Plan my nights: fill a night with a campsite ─────────────────────────
  // An existing overnight stop BECOMES that campsite (kind camp, same nights —
  // the shared contract); a suggested halfway night is inserted as a new camp
  // stop. New stop ids, so the geometry effect reroutes through the camp.
  const fillNightWithCamp = useCallback(
    (slot: Pick<NightSlot, 'stopId' | 'insertAfterStopId' | 'night' | 'name'>, camp: {
      entityId: number; name: string; lat?: number; lng?: number;
    }) => {
      setTripStops((prev) => {
        if (slot.stopId) {
          const idx = prev.findIndex((s) => s.id === slot.stopId);
          if (idx < 0) return prev;
          const old = prev[idx];
          const replacement: TripStop = {
            ...old,
            id: uniqueId(),
            name: camp.name,
            kind: 'camp',
            entityId: camp.entityId,
            lat: camp.lat ?? old.lat,
            lng: camp.lng ?? old.lng,
            nights: Math.max(1, old.nights ?? 1),
          };
          // Already somewhere else on the trip? Drop that copy.
          const rest = prev.filter((s, i) => i === idx || s.entityId !== camp.entityId);
          return rest.map((s) => (s.id === old.id ? replacement : s));
        }
        const after = prev.findIndex((s) => s.id === slot.insertAfterStopId);
        const stop = createTripStopFromName(camp.name, 'camp', {
          entityId: camp.entityId,
          lat: camp.lat,
          lng: camp.lng,
          nights: 1,
        });
        const at = after >= 0 ? after + 1 : Math.max(1, prev.length - 1);
        return [...prev.slice(0, at), stop, ...prev.slice(at)];
      });
      setActiveTripId(null);
      setTripStatusMessage({
        message: `“${camp.name}” is night ${slot.night}${slot.name && slot.name !== camp.name ? ` (near ${slot.name})` : ''}`,
        tone: 'success',
      });
    },
    []
  );

  const handlePickCampForSlot = useCallback(
    (slot: NightSlot, c: CampOption) => {
      campCacheRef.current.set(c.id, c);
      fillNightWithCamp(slot, { entityId: c.id, name: c.name, lat: c.lat, lng: c.lng });
    },
    [fillNightWithCamp]
  );

  // ── A night's camp picks + a leg's camps, opened from the itinerary ─────
  // "Find a camp" under an overnight (and "Find camps near here" on one)
  // opens that night's picks; the "+" box on a leg links to every camp on
  // that drive. Both are sheets over the planner column, pinned on the map.
  const slotByStopId = useMemo(() => {
    const m = new Map<string, NightSlot>();
    cardPlan?.slots.forEach((s) => {
      if (s.stopId && !s.suggested) m.set(s.stopId, s);
    });
    return m;
  }, [cardPlan]);

  const renderCampPicks = () => {
    const slot = campPicksStopId ? slotByStopId.get(campPicksStopId) : null;
    if (!slot) return null;
    const data = nightCamps[slot.key];
    const options = data?.options ?? [];
    const committed = typeof slot.entityId === 'number';
    let pick: CampOption | null = null;
    if (committed) {
      pick =
        options.find((o) => o.id === slot.entityId) ??
        campCacheRef.current.get(slot.entityId!) ??
        (typeof slot.lat === 'number' && typeof slot.lng === 'number'
          ? {
              id: slot.entityId!, name: slot.name, lat: slot.lat, lng: slot.lng, type: 'campground', image: null,
              feeVerdict: null, minPrice: null, powered: null, bookingUrl: null, town: null, rating: null, feeFeature: false,
            }
          : null);
    }
    const placeName = committed ? pick?.town || slot.name : slot.name.split(',')[0];
    const nightLabel = slot.nights > 1 ? `Nights ${slot.night}–${slot.night + slot.nights - 1}` : `Night ${slot.night}`;
    const dateLabel = slot.date
      ? new Date(`${slot.date}T12:00:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })
      : null;
    const located = typeof slot.lat === 'number' && typeof slot.lng === 'number';
    const close = () => setCampPicksStopId(null);
    return (
      <CampSwapSheet
        key={slot.key}
        kicker={`${nightLabel}${dateLabel ? ` · ${dateLabel}` : ''} · near ${placeName}`}
        placeName={placeName}
        anchor={located ? { lat: slot.lat!, lng: slot.lng! } : null}
        current={pick}
        currentChosen={committed}
        options={committed && pick && !options.some((o) => o.id === pick!.id) ? [pick, ...options] : options}
        loading={!data || data.status === 'loading'}
        error={data?.status === 'error'}
        onRetry={() => setNightCampsRetry((n) => n + 1)}
        distanceFor={(c) => (located ? fromStopLabel(haversineKm(slot.lat!, slot.lng!, c.lat, c.lng), placeName) : null)}
        kmFor={located ? (c) => haversineKm(slot.lat!, slot.lng!, c.lat, c.lng) : undefined}
        guest={!canRoute}
        onPins={setSwapPins}
        hoveredId={hoveredLegCampId}
        onHover={setHoveredLegCampId}
        selectedId={selectedLegCampId}
        onSelect={setSelectedLegCampId}
        onChoose={(c) => {
          close();
          if (!pick || c.id !== pick.id) handlePickCampForSlot(slot, c);
        }}
        onClose={close}
      />
    );
  };

  // Inside a leg's "+" box, under its few ideas: every camp on that drive.
  const renderLegCamps = useCallback(
    (index: number) => {
      const stop = tripStops[index];
      const next = tripStops[index + 1];
      if (!stop || !next || !tripLegLines) return null;
      // Nobody camps between two towns twelve minutes apart.
      const mins = tripLegs[index]?.driveMinutes;
      if (typeof mins === 'number' && mins > 0 && mins < LEG_DISCOVERY_MIN_MINUTES) return null;
      const key = legKeyOf(stop.id, next.id);
      const st = legCamps[key];
      return (
        <LegCampsLink
          toName={next.name}
          state={st ?? { status: 'loading' }}
          onOpen={() => setOpenLegKey(key)}
          onRetry={() => void loadLegCamps(key, index)}
        />
      );
    },
    [tripStops, tripLegLines, tripLegs, legCamps, loadLegCamps]
  );

  const renderLegSheet = () => {
    if (!openLegKey || !tripLegLines) return null;
    const index = tripLegLines.findIndex((l) => legKeyOf(l.fromId, l.toId) === openLegKey);
    const stop = tripStops[index];
    const next = tripStops[index + 1];
    if (index < 0 || !stop || !next) return null;
    const legState = legCamps[openLegKey];
    return (
      <LegDiscoveryPanel
        key={openLegKey}
        id={`leg-camps-${index}`}
        fromName={stop.name}
        toName={next.name}
        state={legState && legState.status !== 'loading' ? legState : { status: 'loading' }}
        onAdd={(c) => handleAddLegCamp(stop.id, next.id, c)}
        onClose={() => setOpenLegKey(null)}
        onRetry={() => void loadLegCamps(openLegKey, index)}
        hoveredId={hoveredLegCampId}
        onHover={setHoveredLegCampId}
        onVisibleChange={setLegPins}
        line={openLegPath}
        addedLabel={nightAfterStop(stop.id)}
        legKm={tripLegs[index]?.distanceKm}
        legMinutes={tripLegs[index]?.driveMinutes}
        lineKm={legState && legState.status === 'ready' ? legState.lineKm : undefined}
        fromShort={stop.name}
        toShort={next.name}
        selectedId={selectedLegCampId}
        onSelect={setSelectedLegCampId}
      />
    );
  };

  // Re-draw the vehicle-aware route through an explicit ordered stop list —
  // used by the on-map route-drag edits. Threads the intermediate stops as
  // GraphHopper `waypoints`, so the dropped point snaps to the nearest road and
  // the pulled route sticks. Endpoints without coords are geocoded from name.
  const resolveStopLatLng = useCallback(
    async (s: TripStop): Promise<{ lat: number; lng: number } | null> => {
      if (typeof s.lat === 'number' && typeof s.lng === 'number') return { lat: s.lat, lng: s.lng };
      if (s.name) return await geocodeToLatLng(s.name);
      return null;
    },
    []
  );

  // The vehicle-aware route is THE source for every distance and drive time
  // the planner shows. Signed in only — each plan costs GraphHopper credits,
  // so guests never call it (they see "≈" estimates + a sign-in prompt). A
  // saved vehicle adds its dimensions when it's the same kind of rig.
  const planSeqRef = useRef(0);
  const planVehicleAwareRouteFromStops = useCallback(
    async (stops: TripStop[]) => {
      if (!getUserToken() || stops.length < 2) return;
      const seq = ++planSeqRef.current;
      try {
        const vehicleType = tripVehicleTypeRef.current || 'car';
        const [start, end, saved] = await Promise.all([
          resolveStopLatLng(stops[0]),
          resolveStopLatLng(stops[stops.length - 1]),
          getUserToken() ? getDefaultVehicle() : Promise.resolve(null),
        ]);
        if (!start || !end) return;
        const middle = await Promise.all(stops.slice(1, -1).map(resolveStopLatLng));
        const waypoints = middle.filter(Boolean) as { lat: number; lng: number }[];
        const savedType = saved?.type === 'sedan' ? 'car' : saved?.type;
        const useSaved = !!saved && savedType === vehicleType;
        const plan = await planRoute({
          start, end, waypoints,
          vehicle_id: useSaved ? saved!.id : undefined,
          vehicle_profile: vehicleType,
          max_daily_hours: maxDailyDriveHours,
        });
        if (seq !== planSeqRef.current) return; // a newer edit superseded this
        if (!plan.polyline?.length) return;
        setRoutePathOverride(plan.polyline);
        // Night markers come from "Plan my nights", not GraphHopper's fixed
        // every-N-hours anchors (those ignored the nights you'd chosen).
        setRouteAnchors([]);
        setRouteVehicle(useSaved ? saved : null);
        const points = [start, ...waypoints, end];
        const split =
          waypoints.length === stops.length - 2
            ? splitRouteIntoLegs(plan.polyline, points, plan.distance_km, plan.duration_minutes)
            : null;
        setVehicleRoute(
          split
            ? {
                key: stops.map((s) => s.id).join('|'),
                polyline: plan.polyline,
                legs: split.legs,
                vertexIndex: split.vertexIndex,
                cumulativeKm: split.cumulativeKm,
              }
            : null
        );
      } catch {
        /* keep the current line on failure — a drag never blanks the route */
      }
    },
    [resolveStopLatLng, maxDailyDriveHours]
  );
  planFromStopsRef.current = planVehicleAwareRouteFromStops;

  // Keep the drawn route honest when the stop LIST changes.
  //
  // The vehicle-aware polyline is cached in `routePathOverride`, and
  // GoogleMapComponent deliberately suppresses the Google line while it exists.
  // So a stale override is not merely out of date — it is the only line drawn.
  // Removing a stop used to leave the map routing through it, because only the
  // two drag handlers called planVehicleAwareRouteFromStops; the panel's own
  // remove / reorder / add actions never did.
  //
  // This lives in one effect rather than in each handler so a future one cannot
  // forget it — six already had.
  //
  // The key is the ORDERED STOP IDS, which is what makes it safe:
  //   • handleRouteDrawn syncs stop COORDINATES back from the drawn route. A
  //     coordinate- or name-based key would retrigger here and loop forever.
  //     Ids are untouched by that sync, so it cannot fire.
  //   • renaming a stop or editing a note leaves ids alone — no wasted reroute.
  //   • add / remove / reorder / undo all change the id list, so all reroute.
  // Dragging changes coordinates but not ids, so the drag handlers keep their
  // own explicit call (they also own the "Rerouting…" status message).
  const tripGeometryKey = useMemo(
    () => tripStops.map((stop) => stop.id).join('|'),
    [tripStops]
  );
  const lastRoutedGeometryRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastRoutedGeometryRef.current === null) {
      // First run: whatever drew the current line (a route search, a loaded
      // trip) already routed for this geometry. Adopt it without a second call.
      lastRoutedGeometryRef.current = tripGeometryKey;
      return;
    }
    if (lastRoutedGeometryRef.current === tripGeometryKey) return;
    lastRoutedGeometryRef.current = tripGeometryKey;

    if (tripStops.length < 2) {
      clearVehicleRoute();
      return;
    }
    // Clear BEFORE rerouting, not after it succeeds. planVehicleAwareRouteFromStops
    // keeps the existing line when the call fails — right for a drag, wrong here,
    // where the retained line runs through a stop the user just deleted. Dropping
    // it first means the worst case is the Google fallback, which follows the
    // stop list.
    clearVehicleRoute();
    void planVehicleAwareRouteFromStops(tripStops);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripGeometryKey]);

  // ── Popular trips (seeded catalogue) ──────────────────────────────────────
  const popularFetchRef = useRef(false);
  const ensurePopularRoutes = useCallback((force = false) => {
    if (popularFetchRef.current && !force) return;
    popularFetchRef.current = true;
    if (force) setPopularRoutes(null);
    fetchPopularRoutes()
      .then(setPopularRoutes)
      .catch(() => setPopularRoutes([]));
  }, []);
  const retryPopularRoutes = useCallback(() => ensurePopularRoutes(true), [ensurePopularRoutes]);

  // The empty planner draws the popular trips on the map as faint lines; the
  // card under the pointer (or keyboard focus) lights its line up. Shapes come
  // from each trip's own stops (public catalogue, fetched once per trip).
  const [previewPaths, setPreviewPaths] = useState<Record<string, { lat: number; lng: number }[]>>({});
  const [previewRouteId, setPreviewRouteId] = useState<string | null>(null);
  const previewFetchRef = useRef<Set<string>>(new Set());

  const openPopularPicker = useCallback(() => {
    setPopularPickerOpen(true);
    setIsTripSectionOpen(true);
    ensurePopularRoutes();
  }, [ensurePopularRoutes]);

  // The empty planner shows the popular-trips shelf: load it once the panel
  // is open with nothing in it.
  useEffect(() => {
    if (isTripSectionOpen && tripStops.length === 0) ensurePopularRoutes();
  }, [isTripSectionOpen, tripStops.length, ensurePopularRoutes]);

  // "Start planning" on the empty planner: start, optional end or loop, and
  // an optional leaving date.
  const handleStartPlanning = useCallback((plan: StartPlan) => {
    const stops = normaliseTripStops(
      buildStartStops({ start: plan.start, end: plan.end, loop: plan.loop }, uniqueId)
    );
    setTripStops(stops);
    setActiveTripId(null);
    setMapViewMode('trip');
    setAddWaypointMode(false);
    if (plan.startDate) setTripStartDate(plan.startDate);
    // The panel header and the ladder already say where the trip starts;
    // a status line saying it a third time was noise.
    setTripStatusMessage(null);
    // Bring the whole planner and map on screen (a click low in the form
    // can leave the page scrolled past the map's top edge).
    requestAnimationFrame(() => scrollPlannerIntoViewRef.current?.());
  }, []);

  const handleLoadPopularTrip = useCallback(
    async (route: PopularRouteSummary) => {
      setPopularLoadingId(route.id);
      const trip = await fetchSystemTrip(route.id);
      setPopularLoadingId(null);
      if (!trip || !trip.stops?.length) {
        setTripStatusMessage({
          message: `Couldn't load “${route.name}” just now — please try again.`,
          tone: 'warning',
        });
        return;
      }
      // The seeder leaves raw route-shaping via-points in some system trips —
      // named "Custom stop (lat, lng)" and occasionally sitting in the ocean
      // (straight-line anchors, not roads). They add nothing for the user:
      // the route re-derives from the real stops on load. Drop them, unless
      // that would leave the trip without a usable route.
      const realStops = trip.stops.filter(
        (s) => !/^custom stop \(/i.test((s.name || '').trim())
      );
      const usableStops = realStops.length >= 2 ? realStops : trip.stops;
      const stops: TripStop[] = usableStops.map((s) => ({
        id: s.id || uniqueId(),
        name: s.name || 'Stop',
        kind: kindFromApi(s.kind),
        nights: nightsFromApi(s.kind, s.nights),
        entityId: typeof s.entity_id === 'number' ? s.entity_id : undefined,
        lat: typeof s.lat === 'number' ? s.lat : undefined,
        lng: typeof s.lng === 'number' ? s.lng : undefined,
        notes: s.notes || undefined,
        tags: [],
        constraints: [],
        locked: false,
      }));
      // The card promised "4 days · 3 nights": if the template carries no
      // overnights of its own, make that many of its middle stops the nights
      // (spread along the drive). CampsNearStops suggests a camp near each.
      const promisedNights = routeTripFacts(route).nights ?? 0;
      const hasOwnNights = stops.slice(1, -1).some((st) => (st.nights ?? 0) > 0);
      if (!hasOwnNights && promisedNights > 0) {
        const picks = pickOvernightIndexes(
          stops.map((st) => (typeof st.lat === 'number' && typeof st.lng === 'number' ? { lat: st.lat, lng: st.lng } : null)),
          promisedNights,
          (a, b) => haversineKm(a.lat, a.lng, b.lat, b.lng)
        );
        picks.forEach((i) => {
          stops[i] = { ...stops[i], kind: stops[i].entityId !== undefined ? 'camp' : 'overnight', nights: 1 };
        });
      }
      const previousStops = tripStops;
      setTripStops(normaliseTripStops(stops));
      setActiveTripId(null);
      const tripName = trip.name || route.name;
      setRouteName(tripName);
      setTripNameInput(tripName);
      setSearchQuery(tripName);
      if (stops[0]?.name) setStartPoint(stops[0].name);
      if (stops[stops.length - 1]?.name) setEndPoint(stops[stops.length - 1].name);
      setMapViewMode('trip');
      setPopularPickerOpen(false);
      setTripStatusMessage(
        previousStops.length >= 1
          ? {
              message: `Swapped in “${tripName}”.`,
              tone: 'success',
              ttlMs: 6000,
              action: {
                label: 'Undo',
                onClick: () => {
                  setTripStops(previousStops);
                  setTripStatusMessage(null);
                },
              },
            }
          : {
              message: `Loaded “${tripName}” — ${stops.length} stops. Make it yours.`,
              tone: 'success',
            }
      );
      void planVehicleAwareRouteFromStops(stops);
      // Not scrollIntoView: that ignores the sticky results bar, leaving the
      // top of the map and the trip header under it and the footer showing.
      scrollPlannerIntoViewRef.current();
    },
    [tripStops, planVehicleAwareRouteFromStops]
  );

  // Dragged an existing stop pin → move that stop, then reroute through the new
  // geometry. Editing turns the trip into an unsaved draft until "Save Trip".
  const handleStopDragged = useCallback(
    async (stopId: string, lat: number, lng: number) => {
      let next: TripStop[] = [];
      setTripStops((prev) => {
        next = prev.map((s) => (s.id === stopId ? { ...s, lat, lng } : s));
        return next;
      });
      setActiveTripId(null);
      setMapViewMode('trip');
      setTripStatusMessage({ message: 'Rerouting…', tone: 'warning' });
      await planVehicleAwareRouteFromStops(next);
      setTripStatusMessage({ message: 'Route updated — Save Trip to keep it', tone: 'success' });
    },
    [planVehicleAwareRouteFromStops]
  );

  // Dragged a leg's via-handle onto a road → insert a new waypoint on that leg
  // (after the leg's start stop), then reroute through it.
  const handleRouteViaDragged = useCallback(
    async (afterStopId: string, lat: number, lng: number) => {
      const name = await reverseGeocodeName(lat, lng);
      const via = createTripStopFromName(name, 'via', { lat, lng, nights: 0 });
      let inserted = false;
      let next: TripStop[] = [];
      setTripStops((prev) => {
        const idx = prev.findIndex((s) => s.id === afterStopId);
        if (idx < 0) { next = prev; return prev; }
        inserted = true;
        next = [...prev.slice(0, idx + 1), via, ...prev.slice(idx + 1)];
        return next;
      });
      if (inserted) {
        setActiveTripId(null);
        setMapViewMode('trip');
        setTripStatusMessage({ message: 'Rerouting…', tone: 'warning' });
        await planVehicleAwareRouteFromStops(next);
        setTripStatusMessage({ message: 'Route updated — Save Trip to keep it', tone: 'success' });
      }
    },
    [planVehicleAwareRouteFromStops, reverseGeocodeName]
  );

  // Escape exits drop-a-pin mode (pairs with the on-map banner's hint).
  useEffect(() => {
    if (!addWaypointMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAddWaypointMode(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [addWaypointMode]);

  // Only attach the map click listener while the mode is on, so normal map
  // interaction (marker clicks, panning) is untouched the rest of the time.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !addWaypointMode || typeof google === 'undefined') return;
    map.setOptions({ draggableCursor: 'crosshair' });
    const listener = map.addListener('click', (event: google.maps.MapMouseEvent) => {
      if (!event.latLng) return;
      // Click point inside the map box, so the result can sit by the click.
      const dom = event.domEvent as MouseEvent | undefined;
      const box = map.getDiv().getBoundingClientRect();
      const at =
        dom && typeof dom.clientX === 'number' ? { x: dom.clientX - box.left, y: dom.clientY - box.top } : undefined;
      void handleAddWaypointAt(event.latLng.lat(), event.latLng.lng(), at);
    });
    return () => {
      google.maps.event.removeListener(listener);
      map.setOptions({ draggableCursor: null });
    };
  }, [addWaypointMode, handleAddWaypointAt, mapReady]);

  const handleStartTripSave = useCallback(() => {
    if (tripStops.length < 2) {
      setTripStatusMessage({
        message: 'Add at least two stops before saving.',
        tone: 'warning',
      });
      return;
    }

    setTripNameInput(
      routeName ||
        (tripStops.length >= 2
          ? `${tripStops[0].name} to ${tripStops[tripStops.length - 1].name}`
          : 'New Trip')
    );
    setTripStatusMessage(null);
    setIsEditingTripName(true);
  }, [routeName, tripStops]);

  // Resolves to the saved trip (null when it couldn't save), so Share and
  // export can save first and carry on.
  // When the open trip was last saved in this session: the chip by its
  // name says "Saved · just now", then "Saved · 4 min ago".
  const [lastSavedAt, setLastSavedAt] = useState<{ id: string; at: number } | null>(null);
  // `quiet`: Share saved it on the way to making a link. The header's chip
  // says "Saved · just now"; the share sheet is the moment, so no toast.
  const handleSaveTripConfirm = useCallback(async (opts?: { quiet?: boolean }): Promise<StoredTrip | null> => {
    if (tripStops.length < 2) {
      setTripStatusMessage({
        message: 'Add at least two stops before saving.',
        tone: 'warning',
      });
      return null;
    }

    // The header's title field is the name. Left blank, the trip is named
    // after its ends ("Melbourne to Sydney").
    const trimmedName =
      tripNameInput.trim() || routeName || `${tripStops[0].name} to ${tripStops[tripStops.length - 1].name}`;

    try {
      // Re-saving the open trip updates it; re-saving the same route before
      // it was opened from the list matches on route key, so one trip ≠ two rows.
      const id =
        activeTripId ||
        (tripRouteKey && savedTrips.find((t) => t.routeKey === tripRouteKey)?.id) ||
        uniqueId();
      const existing = savedTrips.find((t) => t.id === id);
      const storedTrip: StoredTrip = {
        id,
        name: trimmedName,
        createdAt: existing?.createdAt ?? new Date().toISOString(),
        routeName: routeName || trimmedName,
        startPoint,
        endPoint,
        routeKey: tripRouteKey || undefined,
        stops: normaliseTripStops(tripStops),
        startDate: tripStartDate,
        driveHoursPerDay: maxDailyDriveHours,
        vehicleType: tripVehicleType,
        localOnly: existing?.localOnly,
      };

      // Guests save to this device; signed-in users save to their account
      // (trip + stops), which is what the iOS/Android apps read.
      const updatedTrips = await upsertTrip(storedTrip);
      const saved = updatedTrips[0];
      setSavedTrips(updatedTrips);
      setActiveTripId(saved?.id ?? id);
      setLastSavedAt({ id: saved?.id ?? id, at: Date.now() });
      setTripNameInput(trimmedName);
      // Short: the primary turning into "Share trip" says the rest.
      // One confirmation each: a guest's save is shown by the "Saved on this
      // device" badge by the title (and Save turning into Share), so no toast.
      // A guest's first save is the one place an account is pitched: what
      // it adds, once a session, with Sign in right there. After that the
      // "On this device · Back up" chip says it.
      const pitch = saved?.localOnly && !isLoggedIn && !guestPitchShownRef.current;
      if (pitch) guestPitchShownRef.current = true;
      if (!opts?.quiet) setTripStatusMessage(
        saved?.localOnly
          ? isLoggedIn
            ? { message: 'Saved. Syncing to your account.', tone: 'success' }
            : pitch
              ? {
                  message: 'Saved on this device.',
                  detail: signInNudgeCopy(),
                  tone: 'success',
                  action: { label: 'Sign in', onClick: () => setTripSignInOpen(true) },
                }
              : null
          : { message: 'Saved. It’s in the app too.', tone: 'success' }
      );
      setIsEditingTripName(false);
      return saved ?? null;
    } catch (error) {
      console.error('Error saving trip:', error);
      setTripStatusMessage({
        message: 'Couldn’t save the trip. Try again in a sec.',
        tone: 'error',
      });
      return null;
    }
  }, [tripStops, tripNameInput, tripRouteKey, activeTripId, routeName, startPoint, endPoint, savedTrips, tripStartDate, maxDailyDriveHours, tripVehicleType, isLoggedIn]);

  // ── Guest trips follow the user into their account ──────────────────────
  // Same progressive-auth idea as the review form: a guest saves freely; the
  // moment they sign in (OAuth round-trip or inline), their device-only trips
  // are pushed to the server so they appear in the iOS/Android apps.
  const migratingRef = useRef(false);
  useEffect(() => {
    if (!isLoggedIn || migratingRef.current) return;
    if (localOnlyTrips().length === 0) return;
    migratingRef.current = true;
    void migrateLocalTrips()
      .then(({ trips, migrated, idMap }) => {
        setSavedTrips(trips);
        setActiveTripId((cur) => (cur && idMap[cur] ? idMap[cur] : cur));
        if (migrated > 0) {
          setTripStatusMessage({
            message: `Synced ${migrated} saved trip${migrated === 1 ? '' : 's'} to your account — open the app to see ${migrated === 1 ? 'it' : 'them'}.`,
            tone: 'success',
          });
        }
      })
      .catch(() => {
        /* stays local; retried on the next sign-in or visit */
      })
      .finally(() => {
        migratingRef.current = false;
      });
  }, [isLoggedIn]);

  const handleCancelTripSave = useCallback(() => {
    setIsEditingTripName(false);
    setTripStatusMessage(null);
  }, []);

  const handleLoadTrip = useCallback(
    async (tripId: string) => {
      if (!tripId) return;
      const trip = savedTrips.find((item) => item.id === tripId);
      if (!trip) return;
      setActiveTripId(tripId);
      setTripNameInput(trip.name);
      // Trip-level planner settings travel with the trip (and the apps).
      setTripStartDate(trip.startDate ?? null);
      if (typeof trip.driveHoursPerDay === 'number' && trip.driveHoursPerDay > 0) {
        setMaxDailyDriveHours(trip.driveHoursPerDay);
      }
      if (trip.vehicleType) setTripVehicleType(trip.vehicleType);
      const waypoints = trip.stops
        .slice(1, -1)
        .map((stop) => stop.name)
        .filter(Boolean);
      await handleRouteSearch(
        trip.startPoint || trip.stops[0]?.name || '',
        trip.endPoint || trip.stops[trip.stops.length - 1]?.name || '',
        trip.routeName || trip.name,
        waypoints,
        trip.stops,
        { preserveActiveTrip: true }
      );
    },
    [savedTrips, handleRouteSearch]
  );

  // Phones have no room for the results bar's "Ask the camping assistant"
  // link, so the explorer asks TripChat for its store chat control as a
  // floating button on phones (TripChat reads data-chat-fab).
  useEffect(() => {
    document.documentElement.setAttribute('data-chat-fab', '1');
    return () => document.documentElement.removeAttribute('data-chat-fab');
  }, []);

  // Deep-link: /?trip=<id> (the Saved page's "Open in planner" link) loads that
  // itinerary straight into the ladder + map. Fires once, after savedTrips has
  // hydrated from localStorage (the Saved page keeps it in sync with the
  // server). An unknown / not-yet-synced id is simply ignored.
  // With &night=<n>&stop=<stopId> (the trip sheet's "Pick a campsite" link)
  // it also opens Plan my nights on that overnight's campsite picker.
  const consumedTripParam = useRef(false);
  const fetchedTripsForParam = useRef(false);
  const [pendingNightFocus, setPendingNightFocus] = useState<{ stop?: string; night?: number } | null>(null);
  useEffect(() => {
    if (consumedTripParam.current) return;
    const tid = searchParams?.get('trip');
    if (!tid) return;
    if (savedTrips.some((t) => t.id === tid)) {
      consumedTripParam.current = true;
      handleLoadTrip(tid);
      const night = Number(searchParams?.get('night'));
      const stop = searchParams?.get('stop') || undefined;
      if (stop || (Number.isFinite(night) && night > 0)) {
        setPendingNightFocus({ stop, night: Number.isFinite(night) && night > 0 ? night : undefined });
      }
    } else if (isLoggedIn && !fetchedTripsForParam.current) {
      // Not on this device yet (opened from another device / the app) —
      // pull the account's trips once and try again.
      fetchedTripsForParam.current = true;
      void syncTripsFromServer().then(setSavedTrips).catch(() => {});
    }
  }, [searchParams, savedTrips, handleLoadTrip, isLoggedIn]);

  useEffect(() => {
    if (!pendingNightFocus || !cardPlan || !cardPlan.slots.length) return;
    const slot =
      cardPlan.slots.find((s) => pendingNightFocus.stop && s.stopId === pendingNightFocus.stop) ??
      cardPlan.slots.find(
        (s) =>
          typeof pendingNightFocus.night === 'number' &&
          pendingNightFocus.night >= s.night &&
          pendingNightFocus.night < s.night + s.nights
      );
    if (slot) {
      // Opens that night's camp picks.
      if (slot.stopId && !slot.suggested) setCampPicksStopId(slot.stopId);
      setPendingNightFocus(null);
    }
  }, [pendingNightFocus, cardPlan]);

  // ── Preserve an in-progress (unsaved) trip across the OAuth full-page redirect
  // Signing in bounces the browser out to Google/Apple/etc. and back, which
  // reloads the whole SPA and would otherwise wipe the draft route the user just
  // built (their complaint: "logging in cleared all my trip work"). We mirror
  // the pending-review pattern — write the draft through to sessionStorage while
  // it's unsaved, and restore it on the next load. It clears itself once the
  // trip is saved (activeTripId set) or the planner is emptied.
  const TRIP_DRAFT_KEY = 'tripDraftInProgress';
  // Set once the restore effect below has run. The write effect is declared
  // first, so on mount it fires BEFORE the restore effect — without this guard
  // its else-branch removeItem() deleted the saved draft before restore could
  // read it, which is exactly how reloads (and the OAuth return) lost trips.
  const restoredDraftRef = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !restoredDraftRef.current) return;
    try {
      if (tripStops.length >= 2 && !activeTripId) {
        sessionStorage.setItem(
          TRIP_DRAFT_KEY,
          JSON.stringify({ tripStops, routeName, startPoint, endPoint, routePathOverride, routeAnchors, tripStartDate, maxDailyDriveHours, tripVehicleType })
        );
      } else {
        sessionStorage.removeItem(TRIP_DRAFT_KEY);
      }
    } catch {
      /* sessionStorage unavailable (private mode / quota) — non-fatal */
    }
    // A device-wide copy (saved or not) so a campsite page opened in another
    // tab can drop a campsite into one of this trip's nights, and the
    // explorer can pick the trip back up to apply it (see tripNightShare).
    try {
      if (tripStops.length >= 2) {
        localStorage.setItem(
          TRIP_DRAFT_SHARED_KEY,
          JSON.stringify({ tripStops, routeName, startPoint, endPoint, tripStartDate, maxDailyDriveHours, tripVehicleType, activeTripId, savedAt: Date.now() })
        );
      } else if (tripStops.length === 0) {
        localStorage.removeItem(TRIP_DRAFT_SHARED_KEY);
      }
    } catch {
      /* non-fatal */
    }
  }, [tripStops, activeTripId, routeName, startPoint, endPoint, routePathOverride, routeAnchors, tripStartDate, maxDailyDriveHours, tripVehicleType]);

  // Restore the draft once on load — but never over a /?trip= deep-link (a saved
  // itinerary must win) or an already-populated planner.
  useEffect(() => {
    if (typeof window === 'undefined' || restoredDraftRef.current) return;
    restoredDraftRef.current = true;
    if (searchParams?.get('trip')) return;
    if (tripStops.length > 0) return;
    let draft: {
      tripStops?: TripStop[];
      routeName?: string;
      startPoint?: string;
      endPoint?: string;
      routePathOverride?: { lat: number; lng: number }[] | null;
      routeAnchors?: { lat: number; lng: number; day: number }[];
      tripStartDate?: string | null;
      maxDailyDriveHours?: number;
      tripVehicleType?: string;
      activeTripId?: string | null;
    } | null = null;
    try {
      const raw = sessionStorage.getItem(TRIP_DRAFT_KEY);
      draft = raw ? JSON.parse(raw) : null;
    } catch {
      draft = null;
    }
    // New tab (sessionStorage is per tab): pick the device-wide copy back up
    // when the user came here to work on the trip — a campsite page sent a
    // night to fill, or a ?plan=1 link.
    if (!draft) {
      try {
        const wantsTrip =
          !!localStorage.getItem(PENDING_ADD_KEY) || searchParams?.get('plan') === '1';
        const raw = wantsTrip ? localStorage.getItem(TRIP_DRAFT_SHARED_KEY) : null;
        draft = raw ? JSON.parse(raw) : null;
      } catch {
        draft = null;
      }
    }
    if (!draft || !Array.isArray(draft.tripStops) || draft.tripStops.length < 2) return;
    setTripStops(draft.tripStops);
    if (draft.activeTripId) setActiveTripId(draft.activeTripId);
    if (draft.routeName) {
      setRouteName(draft.routeName);
      setSearchQuery(draft.routeName);
    }
    if (draft.startPoint) setStartPoint(draft.startPoint);
    if (draft.endPoint) setEndPoint(draft.endPoint);
    if (draft.routePathOverride) setRoutePathOverride(draft.routePathOverride);
    if (draft.routeAnchors) setRouteAnchors(draft.routeAnchors);
    if (draft.tripStartDate) setTripStartDate(draft.tripStartDate);
    if (typeof draft.maxDailyDriveHours === 'number') setMaxDailyDriveHours(draft.maxDailyDriveHours);
    if (draft.tripVehicleType) setTripVehicleType(draft.tripVehicleType);
    setMapViewMode('trip');
    // "Take this trip" on a shared trip page leaves a marker (planner/takeTrip).
    let taken: string | null = null;
    try {
      taken = sessionStorage.getItem('tp-taken-trip');
      if (taken !== null) sessionStorage.removeItem('tp-taken-trip');
    } catch {
      taken = null;
    }
    // A taken trip keeps its name, ready to save under it (or rename), and
    // the shared page's hero photo comes with it.
    if (taken && draft.routeName) setTripNameInput(draft.routeName);
    if (taken !== null) {
      const key = draft.tripStops.map((s) => s.id).join('|');
      setTakenNotice({ name: draft.routeName || '', key });
      let from: string | null = null;
      try {
        from = readTakenFrom(sessionStorage);
      } catch {
        from = null;
      }
      setTakenFrom({ path: from || '', key });
      // The shared page's road comes too: the same Google legs (so the
      // numbers never jump from a straight-line guess to the road figure)
      // and its lines (held over the map while the live one wakes up).
      let route: TakenRoute | null = null;
      try {
        route = readTakenRoute(sessionStorage, draft.tripStops.length);
      } catch {
        route = null;
      }
      if (route?.legs) setGoogleLegs({ key, legs: route.legs });
      if (route && route.lines.length) {
        setTakenRoute({ key, route });
        setGooglePath({ key, path: route.lines.flatMap((l) => l.path) });
      }
    }
    if (taken !== null) {
      try {
        const cover = JSON.parse(sessionStorage.getItem(TAKEN_COVER_KEY) || 'null');
        if (cover?.url) setTakenCover({ url: String(cover.url), name: String(cover.name || ''), key: draft.tripStops.map((s) => s.id).join('|') });
      } catch {
        /* no cover: the planner picks its own */
      }
    }
    // A taken trip is welcomed at the top of the plan (TakenNotice), not in the footer.
    if (taken === null) setTripStatusMessage({ message: 'Restored your trip in progress', tone: 'success' });
    // Refresh the vehicle-aware line now we may be signed in (no-op if not).
    void planVehicleAwareRouteFromStops(draft.tripStops);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Undo" on a just-taken trip: put back whatever unsaved trip the take
  // replaced, and go back to the shared page it came from. A full page load,
  // so nothing in this planner writes the copy back on the way out.
  const undoTake = useCallback(() => {
    restoredDraftRef.current = false;
    try {
      restorePreviousDraft(sessionStorage, localStorage);
    } catch {
      /* storage blocked: the copy simply stays */
    }
    window.location.assign(withBase(takenFrom?.path || '/?plan=1'));
  }, [takenFrom]);

  // Publish this trip's nights for campsite pages ("Add to trip → Night 2").
  useEffect(() => {
    if (!restoredDraftRef.current) return;
    if (tripStops.length < 2 || !nightPlan) {
      // No planned nights (empty planner, or a guest) → campsite pages show
      // the plain "Plan a road trip" link, never a stale night list.
      if (tripStops.length === 0 || !canRoute) writeTripSummary(null);
      return;
    }
    writeTripSummary({
      updatedAt: Date.now(),
      title: routeName || `${tripStops[0].name} to ${tripStops[tripStops.length - 1].name}`,
      nights: nightPlan.slots.map((s) => ({
        key: s.key,
        night: s.night,
        nights: s.nights,
        date: s.date,
        name: s.name,
        suggested: s.suggested,
        stopId: s.stopId,
        insertAfterStopId: s.insertAfterStopId,
        entityId: s.entityId,
      })),
    });
  }, [nightPlan, tripStops, routeName, canRoute]);

  // Apply a campsite page's "add to night N" — on load, when this tab
  // regains focus, or live from another tab via the storage event.
  const applyPendingAdd = useCallback(() => {
    if (tripStops.length < 2) return;
    const pending = takePendingAdd();
    if (!pending) return;
    const { entity, night } = pending;
    const target = night
      ? night.stopId
        ? tripStops.some((s) => s.id === night.stopId) ? night : null
        : tripStops.some((s) => s.id === night.insertAfterStopId) ? night : null
      : null;
    if (target) {
      fillNightWithCamp(target, { entityId: entity.id, name: entity.name, lat: entity.lat, lng: entity.lng });
    } else {
      addEntityAsStop({
        id: entity.id,
        name: entity.name,
        addresses: [{ gps_latitude: String(entity.lat ?? ''), gps_longitude: String(entity.lng ?? '') }],
      } as unknown as Entity);
    }
    setIsTripSectionOpen(true);
  }, [tripStops, fillNightWithCamp, addEntityAsStop]);
  const applyPendingAddRef = useRef(applyPendingAdd);
  applyPendingAddRef.current = applyPendingAdd;
  useEffect(() => {
    applyPendingAddRef.current();
  }, [tripStops.length]);
  useEffect(() => {
    const run = () => applyPendingAddRef.current();
    const onStorage = (e: StorageEvent) => {
      if (e.key === PENDING_ADD_KEY && e.newValue) run();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', run);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', run);
    };
  }, []);

  // Kinds + nights follow position (Start / Overnight / Via / Destination —
  // see utils/tripRoles). Every add/remove/reorder path funnels through here,
  // so no handler can leave a stale "start" in the middle of the trip.
  // normaliseTripStops returns the same array when nothing changed, so this
  // settles after one pass.
  useEffect(() => {
    setTripStops((prev) => normaliseTripStops(prev));
  }, [tripStops]);

  // Print a day-by-day trip sheet in a new window (grey-nomad favourite:
  // distances, drive times, notes and dates on paper for the glovebox).
  const handlePrintTrip = useCallback(() => {
    if (tripStops.length < 2) return;
    const html = buildTripPrintHtml({
      stops: tripStops,
      legs: tripLegs,
      startDate: tripStartDate,
      maxDriveMinutesPerDay: maxDailyDriveHours * 60,
      routeName,
      totalDistanceKm: routeSummary?.distanceKm,
      totalDriveMinutes: routeSummary?.driveMinutes,
      mapsUrl: tripDirectionsUrl(tripStops),
    });
    const w = window.open('about:blank', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.setTimeout(() => {
      try { w.print(); } catch { /* window stays open — user can Ctrl+P */ }
    }, 300);
  }, [tripStops, tripLegs, tripStartDate, maxDailyDriveHours, routeName, routeSummary]);

  const handleAssistantSend = useCallback(
    async (rawMessage: string) => {
      const trimmed = rawMessage.trim();
      if (!trimmed) return;

      const userMessage: AssistantMessage = {
        id: uniqueId(),
        role: 'user',
        content: trimmed,
        createdAt: Date.now(),
      };

      setAssistantMessages((prev) => [...prev, userMessage]);
      setAssistantIsProcessing(true);

      const responseParts: string[] = [];
      const lower = trimmed.toLowerCase();
      let nextRadius = radius;
      let nextCorridor = routeCorridorWidth;

      const extractNumber = (pattern: RegExp): number | null => {
        const match = trimmed.match(pattern);
        if (!match) return null;
        const value = Number.parseInt(match[1], 10);
        return Number.isFinite(value) ? value : null;
      };

      const clamp = (value: number, min: number, max: number) =>
        Math.min(Math.max(value, min), max);

      try {
        const radiusValue = extractNumber(/(?:radius|search area|range)\D*(\d{1,3})/i);
        if (radiusValue !== null) {
          const bounded = clamp(radiusValue, 5, 250);
          setRadius(bounded);
          nextRadius = bounded;
          responseParts.push(`Okay, I set the location search radius to ${bounded} km.`);
        }

        const corridorValue = extractNumber(/(?:corridor|buffer|deviation)\D*(\d{1,3})/i);
        if (corridorValue !== null) {
          const bounded = clamp(corridorValue, 10, 400);
          setRouteCorridorWidth(bounded);
          nextCorridor = bounded;
          responseParts.push(`I widened the corridor to ${bounded} km.`);
        }

        const removeMatch = trimmed.match(
          /\b(?:remove|delete|drop)\b\s+(?:the\s+)?(?:stop|campsite|camp|place|location)?\s*(.+)/i
        );
        if (removeMatch) {
          let candidate = removeMatch[1].trim();
          candidate = candidate.replace(/\b(?:from|to|into|please|thanks|thank you)\b.*$/i, '').trim();
          const candidateLower = candidate.toLowerCase();
          const target = tripStops.find((stop) =>
            stop.name.toLowerCase().includes(candidateLower)
          );
          if (!target) {
            responseParts.push(`I couldn't find a stop matching "${candidate}".`);
          } else if (target.locked) {
            responseParts.push(`I can't remove ${target.name} because it anchors the trip.`);
          } else {
            setTripStops((prev) => prev.filter((stop) => stop.id !== target.id));
            const remaining = Math.max(tripStops.length - 1, 0);
            responseParts.push(
              `Removed ${target.name} from the itinerary. You now have ${remaining} stop${remaining === 1 ? '' : 's'}.`
            );
          }
        }

        let addCandidate: string | null = null;
        const addStopMatch = trimmed.match(
          /\badd\s+(?:another\s+)?(?:stop|campsite|camp|waypoint)\s+(.+)/i
        );
        if (addStopMatch) {
          addCandidate = addStopMatch[1];
        } else {
          const addTripMatch = trimmed.match(/\badd\s+(.+?)\s+(?:to|into)\s+(?:the\s+)?(?:trip|route)\b/i);
          if (addTripMatch) {
            addCandidate = addTripMatch[1];
          }
        }

        if (addCandidate) {
          let cleaned = addCandidate
            .replace(/\b(?:please|thanks|thank you)\b.*$/i, '')
            .replace(/\b(?:as|for)\b.*$/i, '')
            .trim();

          if (cleaned.length > 2) {
            responseParts.push(`Let me look for "${cleaned}"...`);
            try {
              const result = await search({
                location: cleaned,
                radius: Math.max(radius, 50),
                per_page: 5,
                search_type: 'location',
              } as EnhancedSearchParams);

              const entity = result.entities?.[0];
              if (!entity) {
                responseParts.push(`I couldn't find a campsite for "${cleaned}". Try a different name or nearby town.`);
              } else {
                let addition: { added: boolean; next: TripStop[] } | undefined;
                const stop = createTripStopFromEntity(entity);
                setTripStops((prev) => {
                  const outcome = mergeTripStopIntoList(prev, stop);
                  addition = outcome;
                  return outcome.added ? outcome.next : prev;
                });
                if (addition?.added) {
                  const count = addition.next.length;
                  responseParts.push(
                    `Added ${entity.name} just before your final stop. You now have ${count} stop${count === 1 ? '' : 's'}.`
                  );
                  if (mapViewMode !== 'trip') {
                    setMapViewMode('trip');
                  }
                } else if (addition) {
                  responseParts.push(`${entity.name} is already on your ladder.`);
                } else {
                  responseParts.push('Something went wrong while adding that stop. Please try again.');
                }
              }
            } catch (error) {
              console.error('Assistant add stop failed', error);
              responseParts.push('Something went wrong while searching for that stop. Please try again in a moment.');
            }
          }
        }

        if (/what.*stops|current.*plan|show.*(trip|stops)|summary/i.test(lower)) {
          if (tripStops.length === 0) {
            responseParts.push('The trip ladder is empty right now.');
          } else {
            const summary = tripStops
              .map((stop, index) => `${index + 1}. ${stop.name}${stop.locked ? ' (locked)' : ''}`)
              .join('\n');
            responseParts.push(`Here is your current ladder:\n${summary}`);
          }
        }

        if (!responseParts.length) {
          responseParts.push(
            'I can: add or remove stops, adjust the radius, tweak the corridor width, or recap your itinerary. Try "Add a campsite near Bright" or "Set the radius to 120 km".'
          );
        }
      } catch (error) {
        console.error('Trip assistant error', error);
        responseParts.push('I hit a snag processing that request. Please try again.');
      }

      const fallbackText =
        responseParts.length > 0
          ? responseParts.join('\n\n')
          : 'Let me know how you want to adjust your trip.';

      let handledByAgent = false;

      if (agentSessionId) {
        try {
          const payload = await sendAgentMessage({
            sessionId: agentSessionId,
            content: trimmed,
            taskType: inferAssistantTaskType(trimmed),
            agent: agentHandoff || undefined,
            contextUpdates: {
              itinerary_summary: summariseTripForAssistant(tripStops),
              action_summary: fallbackText,
              radius_km: String(nextRadius),
              corridor_width_km: String(nextCorridor),
              last_search_query: searchQuery || '',
              last_view_mode: mapViewMode,
              trip_stop_count: String(tripStops.length),
            },
          });

          if (payload.context) {
            setAgentContext(payload.context);
          }
          setAgentHandoff(payload.handoff || null);

          if (payload.messages?.length) {
            const mapped: AssistantMessage[] = payload.messages.map((msg) => ({
              id: uniqueId(),
              role: (msg.role === 'user' ? 'user' : 'assistant') as AssistantRole,
              content: msg.content ?? '',
              createdAt: Date.now(),
            }));
            setAssistantMessages((prev) => [...prev, ...mapped]);
            if (!assistantOpen) {
              setAssistantHasUnread(true);
            }
          }
          handledByAgent = true;
        } catch (error) {
          console.error('Agent assistant error', error);
          message.error('Trip assistant is unavailable, falling back to a local response.');
        }
      } else {
        message.warning('Trip assistant session is still initialising; using a local response for now.');
      }

      if (!handledByAgent) {
        setAssistantMessages((prev) => [
          ...prev,
          {
            id: uniqueId(),
            role: 'assistant',
            content: fallbackText,
            createdAt: Date.now(),
          },
        ]);
        if (!assistantOpen) {
          setAssistantHasUnread(true);
        }
      }

      setAssistantIsProcessing(false);
    },
    [
      agentHandoff,
      agentSessionId,
      assistantOpen,
      mapViewMode,
      radius,
      routeCorridorWidth,
      searchQuery,
      tripStops,
    ]
  );
  // ── Trip planner (components/planner) ─────────────────────────────────
  // HomeClient wires state into the planner's shell; the planner files own
  // how it looks. `fill` = the panel owns the results column's height and
  // its body is the only scroll container (see the sidebar below).
  const handleClearTrip = useCallback(() => {
    const previous = tripStops;
    const previousId = activeTripId;
    const previousName = tripNameInput;
    setTripStops([]);
    setActiveTripId(null);
    setTripNameInput('');
    setOpenLegKey(null);
    setIsRouteSearch(false);
    setTripStatusMessage({
      message: 'Cleared the planner. Saved trips are untouched.',
      tone: 'warning',
      action: {
        label: 'Undo',
        onClick: () => {
          setTripStops(previous);
          setActiveTripId(previousId);
          setTripNameInput(previousName);
          setTripStatusMessage(null);
        },
      },
    });
  }, [tripStops, activeTripId, tripNameInput, setTripStops, setActiveTripId, setTripStatusMessage]);

  // ── Trip-level actions (header ⋯ menu and the saved-state primary) ──────
  const handleRenameTrip = useCallback(() => {
    const el = document.getElementById('trip-name-input') as HTMLInputElement | null;
    if (!el) return;
    el.focus();
    el.select();
  }, []);

  const handleDuplicateTrip = useCallback(() => {
    if (tripStops.length < 2) return;
    const base = tripNameInput.trim() || `${tripStops[0].name} to ${tripStops[tripStops.length - 1].name}`;
    const previousId = activeTripId;
    const previousName = tripNameInput;
    setActiveTripId(null);
    setTripNameInput(`${base} (copy)`);
    setTripStatusMessage({
      message: 'Copied. Change what you like, then save to keep both.',
      tone: 'success',
      action: {
        label: 'Undo',
        onClick: () => {
          setActiveTripId(previousId);
          setTripNameInput(previousName);
          setTripStatusMessage(null);
        },
      },
    });
  }, [tripStops, tripNameInput, activeTripId, setActiveTripId, setTripStatusMessage]);

  const handleDeleteTrip = useCallback(async () => {
    const saved = activeTripId ? savedTrips.find((t) => t.id === activeTripId) : undefined;
    if (!saved) return;
    const previousStops = tripStops;
    const previousName = tripNameInput;
    try {
      const trips = await deleteTrip(saved.id);
      setSavedTrips(trips);
    } catch {
      setTripStatusMessage({ message: 'Couldn’t delete the trip just now. Try again in a sec.', tone: 'error' });
      return;
    }
    setTripStops([]);
    setActiveTripId(null);
    setTripNameInput('');
    setOpenLegKey(null);
    setTripStatusMessage({
      message: `Deleted “${saved.name}”.`,
      tone: 'warning',
      action: {
        label: 'Undo',
        onClick: () => {
          void upsertTrip({ ...saved, localOnly: undefined }).then((restored) => {
            setSavedTrips(restored);
            setActiveTripId(restored[0]?.id ?? saved.id);
          });
          setTripStops(previousStops);
          setTripNameInput(previousName);
          setTripStatusMessage(null);
        },
      },
    });
  }, [activeTripId, savedTrips, tripStops, tripNameInput, setTripStops, setActiveTripId, setTripStatusMessage]);

  const handleSearchAlongRoute = useCallback(
    (start: string, end: string, name: string, mids: string[], stops: TripStop[]) => {
      void handleRouteSearchRef.current?.(start, end, name, mids, stops, { preserveActiveTrip: true });
    },
    []
  );

  // The planner fills the results column (and its body becomes the one
  // scroll container) once there is a trip to look at in the split view.
  const plannerFills = hasTripStops && isTripSectionOpen && viewMode === 'split';

  // Re-order to the shortest drive (start and end stay put). Undo-able.
  const handleOptimiseOrder = (order: TripStop[], savedKm: number) => {
    const previous = tripStops;
    setTripStops(order);
    setTripStatusMessage({
      message: `Reordered. About ${formatKm(savedKm)} less driving.`,
      tone: 'success',
      action: {
        label: 'Undo',
        onClick: () => {
          setTripStops(previous);
          setTripStatusMessage(null);
        },
      },
    });
  };

  // The assistant lives in the planner's tool row while the planner fills
  // the column; the floating bubble steps aside (TripChat reads this flag).
  const openAssistant = useCallback(() => {
    window.dispatchEvent(new Event('trip-chat-open'));
  }, []);
  useEffect(() => {
    const root = document.documentElement;
    if (plannerFills && hasTripStops) root.setAttribute('data-trip-chat-docked', '');
    else root.removeAttribute('data-trip-chat-docked');
    return () => root.removeAttribute('data-trip-chat-docked');
  }, [plannerFills, hasTripStops]);

  // A 48px photo per stop: our own listing image for campsites and parks,
  // a free-licensed Wikipedia lead image for towns (planner/stopPhotos).
  const entityPhotoById = useMemo(() => {
    const m = new Map<number, string>();
    [...displayEntities, ...mapEntities].forEach((e) => {
      if (typeof e?.id !== 'number' || m.has(e.id)) return;
      const url = getOptimizedEntityImage(e, { width: 120, quality: 70, fallback: '' });
      if (url) m.set(e.id, url);
    });
    return m;
  }, [displayEntities, mapEntities]);
  const stopPhotos = useStopPhotos(tripStops, (id) => entityPhotoById.get(id));

  // "Find camps near here" (stop menu): browse campsites around that stop.
  // On an overnight it's the same as "Find a camp": that night's picks.
  const handleFindCampsNear = useCallback((stop: TripStop) => {
    if (slotByStopId.has(stop.id)) {
      setCampPicksStopId(stop.id);
      return;
    }
    if (typeof stop.lat !== 'number' || typeof stop.lng !== 'number') return;
    setMapViewMode('search');
    setMapRequested(true);
    setMapCenter({ lat: stop.lat, lng: stop.lng });
    setMapZoom(11);
    if (typeof window !== 'undefined' && window.innerWidth < 768) setViewMode('map');
    setTripStatusMessage({ message: `Showing camps around ${stop.name}`, tone: 'success' });
  }, [setTripStatusMessage, slotByStopId]);

  // "Explore around X" on a rest day: browse what's around that stop.
  const handleExploreNear = useCallback((stop: TripStop) => {
    if (typeof stop.lat !== 'number' || typeof stop.lng !== 'number') return;
    setMapViewMode('search');
    setMapRequested(true);
    setMapCenter({ lat: stop.lat, lng: stop.lng });
    setMapZoom(11);
    if (typeof window !== 'undefined' && window.innerWidth < 768) setViewMode('map');
    setTripStatusMessage({ message: `Showing what’s around ${stop.name}`, tone: 'success' });
  }, [setTripStatusMessage]);

  // "Find a camp" (the slot under an overnight): that night's camp picks,
  // guests included (a pick is kept on this device until they sign in).
  const handleAddCampForStop = useCallback((stop: TripStop) => setCampPicksStopId(stop.id), []);

  const renderTripPlanner = (fill: boolean, extraClassName = '', sheet = false) => {
    const empty = !hasTripStops;
    const count = tripStops.length;
    // A round trip that hasn't left home is two stops but one place.
    const places = tripPlaceCount(tripStops);
    const estimate = tripLegInfo.source === 'estimate';
    const legKm = tripLegs.reduce((sum, l) => sum + (Number.isFinite(l.distanceKm) ? (l.distanceKm as number) : 0), 0);
    const legMin = tripLegs.reduce((sum, l) => sum + (Number.isFinite(l.driveMinutes) ? (l.driveMinutes as number) : 0), 0);
    const nights = totalNights(tripStops);
    const summary = tripSummaryLine({
      // A loop's drive home isn't another stop (the shared page counts the same way).
      stopCount: places >= 2 ? places : count,
      distanceKm: routeSummary?.distanceKm ?? legKm,
      driveMinutes: routeSummary?.driveMinutes ?? legMin,
      estimate,
      nights,
    });
    const derivedName =
      count >= 2
        ? `${tripStops[0].name} to ${tripStops[count - 1].name}`
        : count === 1
          ? `From ${tripStops[0].name}`
          : 'Plan a road trip';
    const savedTrip = activeTripId ? savedTrips.find((t) => t.id === activeTripId) : undefined;
    const isSaved =
      !!savedTrip &&
      count >= 2 &&
      stopsSignature(savedTrip.stops) === stopsSignature(normaliseTripStops(tripStops)) &&
      savedTrip.name === (tripNameInput.trim() || derivedName);
    const showActions = !empty && !popularPickerOpen;
    const takenCoverActive = !!takenCover && takenCover.key === stopIdsKey;
    const coverPhotoCandidates = [
      // A trip taken from a shared page keeps that page's hero until it's edited.
      ...(takenCoverActive ? [{ url: takenCover!.url, name: takenCover!.name }] : []),
      ...[...tripStops].reverse().flatMap((st) => {
      const url = stopPhotos[st.id];
      if (!url) return [];
      const big = heroPhotoUrl(url);
      const alt = big.replace('/960px-', '/640px-');
      return [{ url: big, name: st.name }, ...(alt !== big ? [{ url: alt, name: st.name }] : [])];
      }),
    ];
    // The hero collage: this route's own places, in order. The start, the
    // first night on the way (else the middle stop), the destination.
    const collagePhotos = (() => {
      if (count < 2 || takenCoverActive) return [];
      const mid = tripStops.slice(1, -1);
      const night = mid.find((st) => (st.nights ?? 0) > 0) ?? mid[Math.floor(mid.length / 2)];
      return [tripStops[0], night, tripStops[count - 1]]
        .filter((st): st is TripStop => !!st && !!stopPhotos[st.id])
        .map((st) => ({ url: heroPhotoUrl(stopPhotos[st.id] as string), name: st.name }));
    })();
    // The trip at a glance: stops · nights · km · drive · fuel. Fuel follows
    // the vehicle (the saved rig's own L/100 km when it is the one routed).
    const stats =
      count >= 2
        ? tripStats({
            stopCount: places,
            nights,
            distanceKm: routeSummary?.distanceKm ?? legKm,
            driveMinutes: routeSummary?.driveMinutes ?? legMin,
            vehicleType: tripVehicleType,
            ownL100km: routeVehicle?.fuel_economy_l100km ?? null,
            estimate,
          })
        : undefined;
    // The plan's biggest problem, said in the header: days that drive past
    // the driver's own limit, counted from the same itinerary days the list
    // shows. "Fix" takes you to the first long day's own "Split this day".
    const longDays =
      count >= 2 && !routePending
        ? longDaySummary(
            itineraryDays(tripStops, tripLegs, maxDailyDriveHours * 60, tripStartDate).days.map((d) => ({ minutes: d.driveMinutes })),
            maxDailyDriveHours
          )
        : null;
    const showFirstLongDay = () => {
      const chip = document.querySelector<HTMLElement>('[data-testid="long-day"]');
      chip?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const fix = chip?.closest('[data-testid="day-header"]')?.querySelector<HTMLElement>('[data-testid="split-day"]');
      window.setTimeout(() => fix?.focus({ preventScroll: true }), 320);
    };
    // Share and export need the trip on the server: save it first when it
    // isn't (or has changed since). Resolves to the server id, or null.
    const ensureSavedOnServer = async (): Promise<string | null> => {
      if (isSaved && savedTrip && !savedTrip.localOnly) return savedTrip.id;
      const saved = await handleSaveTripConfirm({ quiet: true });
      return saved && !saved.localOnly ? saved.id : null;
    };
    // The add-a-stop field wears the marker of the stop it will create:
    // A (start) on an empty trip, B (destination) next, then "+".
    const nextMarker = (
      places >= 2 ? (
        // From the third stop on it is a search field, not a button: a plain
        // search glyph, no filled disc.
        <IconSearch className="h-[18px] w-[18px] text-tp-muted" />
      ) : (
        <span
          className="flex h-6 w-6 items-center justify-center rounded-full text-[12px] font-bold text-white"
          style={{ background: ROLE_HEX.end }}
        >
          B
        </span>
      )
    );
    return (
      <TripPlannerPanel
        title={empty ? '' : tripNameInput}
        titlePlaceholder={derivedName}
        onTitleChange={empty ? undefined : setTripNameInput}
        onTitleCommit={() => {
          // A saved trip re-saves under its new name; an unsaved one keeps
          // the name for when it is saved.
          if (activeTripId && count >= 2) void handleSaveTripConfirm();
        }}
        summary={summary}
        stats={stats}
        statsPending={routePending}
        // Everyone's times are the road route at the rig's pace
        // (useTripLegs), signed in or not. The line always says so, so
        // "Caravan" and the hours agree in every state.
        statsBasis={
          !estimate && rigPaceFactor(tripVehicleType) > 1
            ? `${(VEHICLE_OPTIONS.find((o) => o.value === tripVehicleType)?.label ?? tripVehicleType).toLowerCase()} pace`
            : undefined
        }
        statsWarning={
          longDays
            ? {
                text: longDays.text,
                title: `Over your ${longDays.limitH} hours a day. Add a night on the way to split it.`,
                onFix: showFirstLongDay,
              }
            : undefined
        }
        statsBasisNote={`Drive times at ${(VEHICLE_OPTIONS.find((o) => o.value === tripVehicleType)?.label ?? 'your rig').toLowerCase()} pace: about ${Math.round((rigPaceFactor(tripVehicleType) - 1) * 100)}% slower than a car on the same road.`}
        titleMenu={
          empty ? undefined : (onImage: boolean) => (
            <TripNameMenu
              savedTrips={savedTrips}
              activeTripId={activeTripId}
              onLoadTrip={handleLoadTrip}
              onRename={handleRenameTrip}
              onSeeAll={() => router.push('/saved')}
              onImage={onImage}
            />
          )
        }
        // The save state, as a solid chip under the trip name. "Saved · just
        // now" is said here, once; the action bar then leads with Share.
        badge={
          count < 2 ? undefined : (compact: boolean | 'dot') => isSaved ? (
            savedTrip?.localOnly ? (
              isLoggedIn ? (
                <SaveStatusChip state="device" compact={compact}>
                  <span aria-hidden="true">·</span>
                  <span>syncing</span>
                </SaveStatusChip>
              ) : (
                <SaveStatusChip state="device" compact={compact}>
                  <span aria-hidden="true">·</span>
                  <button
                    type="button"
                    onClick={() => setTripSignInOpen(true)}
                    className="min-h-0 rounded-sm border-0 bg-transparent p-0 text-tp-xs font-semibold text-tp-ink underline decoration-tp-ink/30 underline-offset-2 transition-colors duration-150 hover:decoration-tp-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 [font-family:inherit]"
                  >
                    Back up
                  </button>
                </SaveStatusChip>
              )
            ) : (
              <SaveStatusChip state="saved" compact={compact} savedAt={lastSavedAt && lastSavedAt.id === savedTrip?.id ? lastSavedAt.at : null} />
            )
          ) : activeTripId ? (
            <SaveStatusChip state="changed" compact={compact} />
          ) : (
            <SaveStatusChip state="unsaved" compact={compact} />
          )
        }
        // One hero photo: the destination first (where you are headed is
        // the picture), then the other stops back to the start.
        coverPhotos={count >= 2 ? coverPhotoCandidates : undefined}
        collagePhotos={collagePhotos}
        notice={
          // Just taken from a shared page: stays until the trip is saved,
          // edited or dismissed. What happened (a draft copy), what Save
          // adds, and Undo.
          takenActive && !isSaved ? (
            <TakenNotice name={takenNotice!.name || tripNameInput} onUndo={undoTake} />
          ) : undefined
        }
        // Flat in the desktop rail and in the phone sheet: the column or the
        // sheet is the surface, so no card-in-a-card.
        flat={(fill && !sheet) || mobileTripMap}
        meta={
          count >= 2 ? (
            <TripMeta
              startDate={tripStartDate}
              onChangeStartDate={setTripStartDate}
              nights={nights}
              vehicleType={tripVehicleType}
              onChangeVehicleType={setTripVehicleType}
              maxDriveHours={maxDailyDriveHours}
              // The same three controls for everyone: guests set it now and
              // it splits their days once they sign in.
              onChangeMaxDriveHours={setMaxDailyDriveHours}
              compact={sheet}
            />
          ) : undefined
        }
        open={isTripSectionOpen}
        onToggle={() => setIsTripSectionOpen((prev) => !prev)}
        fill={fill}
        sheet={sheet}
        peek={sheet && sheetSnap === 'peek'}
        onExpand={() => setSheetSnap('half')}
        // Phones, pin armed: the header says what the map is waiting for,
        // docked to the top of the sheet (not a card over the route).
        pinPrompt={
          sheet && addWaypointMode ? (
            <div role="status" className="flex min-h-[48px] items-center gap-3" data-testid="pin-drop-banner">
              <span
                aria-hidden="true"
                className="relative flex h-10 w-10 flex-none items-center justify-center rounded-full bg-tp-brand-soft text-tp-brand"
              >
                <span className="absolute inset-0 rounded-full ring-2 ring-tp-brand/30 motion-safe:animate-ping" style={{ animationDuration: '1.8s' }} />
                <IconPinPlus className="relative h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[17px] font-semibold leading-[22px] text-tp-ink">Tap the map to add a stop</span>
                <span className="flex min-w-0 items-center gap-1 truncate text-tp-sm leading-5 text-[#3a4a63]" data-testid="pin-ghost-label">
                  {pinGhost && pinGhost.label ? (
                    // The same letters as the map and the list right now.
                    <PinGhostWords stops={tripStops} index={pinGhost.index} hasNext={!!pinGhost.next} hasPrev={!!pinGhost.prev} />
                  ) : (
                    'Or move the map and place it at the crosshair'
                  )}
                </span>
              </span>
            </div>
          ) : undefined
        }
        // Full sheet: the way back to the map is one pill floating above
        // the footer (dragging the sheet down does the same).
        floatingAction={
          sheet && sheetSnap === 'full' ? (
            <button
              type="button"
              onClick={() => setSheetSnap('half')}
              className="flex h-10 min-h-0 items-center gap-1.5 rounded-full border-0 bg-tp-ink px-4 text-[14px] font-semibold text-white shadow-tp-pop transition-[transform,background-color] duration-150 ease-tp-out active:scale-[0.97] [@media(hover:hover)]:hover:bg-[#22324d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 focus-visible:ring-offset-2"
              data-testid="sheet-show-map"
            >
              <IconMap className="h-4 w-4" aria-hidden="true" />
              Map
            </button>
          ) : undefined
        }
        peekPreview={sheet && count >= 2 ? <PeekStops stops={tripStops} /> : undefined}
        className={extraClassName}
        // The "add a stop" box sits ABOVE the stop list and outside the
        // scroll body. Everything that changes late (a stop row landing once
        // its place is located, routed figures replacing estimates) happens
        // below it, so the box never moves under the cursor.
        // The empty planner leads with its own heading (PlannerEmptyState's
        // "Plan a road trip"); a second title above it said the same thing.
        hideHeader={empty && !popularPickerOpen}
        top={
          popularPickerOpen || empty ? undefined : (
            <div
              data-testid="add-next-stop"
              {...(places === 1 ? { 'data-where-next': 'true' } : {})}
            >
              <div className="flex items-end gap-2">
              <div className="min-w-0 flex-1">
              <AddStopByPlace
                onAdd={handleAddPlaceStop}
                allowMyLocation={false}
                leadingIcon={nextMarker}
                size="medium"
                near={tripAnchors}
                route={tripRoutePath}
                // Each row says what the pick adds ("+110 km", "On route");
                // the footer says where it goes.
                placement={places >= 2 ? topPlacement : undefined}
                // One search field at a time: an open "+" box greys this out.
                disabled={ladderInsertAt !== null}
                label={places === 1 ? (isUnstartedLoop(tripStops) ? 'Add a stop' : 'Where to next?') : 'Add a stop'}
                // From the third stop on, the field explains itself.
                hideLabel={places >= 2}
                placeholder={places === 1 || sheet ? 'Town, park or campsite' : 'Add a stop, e.g. Bright'}
                // Phones: the search is the way in; "add on the map" is the
                // second way, a pin at the field's right edge.
                trailingAction={
                  sheet && places >= 1 ? <PickOnMapButton onClick={() => setAddWaypointMode(true)} disabled={ladderInsertAt !== null} /> : undefined
                }
              />
              </div>
              {/* The assistant, always in reach beside the box you plan
                  with: camps, fuel and what to see, for this trip. */}
              {fill && !sheet && places >= 2 && (
                <button
                  type="button"
                  onClick={openAssistant}
                  title="Ask AI about this trip: camps, fuel, what to see on the way"
                  // A quiet ghost button (icon + word, no outline), so the
                  // stop search is the one field that leads.
                  className="-mr-1 inline-flex h-11 min-h-0 flex-none items-center gap-1.5 rounded-tp border-0 bg-transparent px-2.5 text-tp-sm font-semibold text-tp-brand transition-colors duration-150 ease-tp-out hover:bg-tp-brand-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 [font-family:inherit]"
                  data-testid="ask-ai"
                >
                  <IconSparkle className="h-[18px] w-[18px] flex-none" />
                  Ask AI
                </button>
              )}
              </div>
              {isUnstartedLoop(tripStops) && (
                <p className="m-0 mt-1.5 text-tp-sm text-tp-muted" data-testid="where-next-prompt">
                  Places you add go between here and home.
                </p>
              )}
            </div>
          )
        }
        toast={
          showActions || tripStatusMessage ? (
            <TripStatusToast message={tripStatusMessage} onDismiss={() => setTripStatusMessage(null)} floating={sheet} />
          ) : undefined
        }
        footer={
          showActions ? (
            <TripActions
              tripStops={tripStops}
              activeTripId={activeTripId}
              savedTrips={savedTrips}
              onLoadTrip={handleLoadTrip}
              isSaved={isSaved}
              tripName={tripNameInput.trim() || derivedName}
              onSave={() => void handleSaveTripConfirm()}
              saveAsync={async () => !!(await handleSaveTripConfirm())}
              ensureSaved={ensureSavedOnServer}
              isLoggedIn={isLoggedIn}
              onSignIn={() => setTripSignInOpen(true)}
              addWaypointMode={addWaypointMode}
              onToggleWaypointMode={() => setAddWaypointMode((v) => !v)}
              onSearchAlongRoute={handleSearchAlongRoute}
              routeName={routeName}
              onClearTrip={handleClearTrip}
              onOpenPopular={openPopularPicker}
              onPrint={handlePrintTrip}
              // Ask AI sits beside the add-a-stop box when the planner fills
              // the column; the menu only carries it when there's no box.
              // Phones carry it in the More menu (the floating assistant
              // stays hidden over the sheet).
              onAskAssistant={(fill && places < 2) || sheet ? openAssistant : undefined}
              sheet={sheet}
              onPlaceHere={sheet ? placeAtAim : undefined}
              placeReady={pinReady}
              setTripStatusMessage={setTripStatusMessage}
              vehicleType={tripVehicleType}
              tripSummary={stats ? stats.filter((x) => x.key === 'stops' || x.key === 'km').map((x) => `${x.value} ${x.unit}`).join(' · ') : undefined}
              sharePreview={{
                photoUrl: coverPhotoCandidates[0]?.url ?? null,
                path: tripRoutePath,
                from: tripStops[0]?.name,
                to: tripStops[count - 1]?.name,
                stopCount: count,
                // Every stop, lettered and coloured as on the main map.
                stops: tripStops.flatMap((st, i) =>
                  typeof st.lat === 'number' && typeof st.lng === 'number'
                    ? [{ lat: st.lat, lng: st.lng, name: st.name, role: roleAt(tripStops, i), letter: stopLetter(i) }]
                    : []
                ),
                // The same "2 days · 1 night" the header says.
                figures: [
                  stats?.find((x) => x.key === 'km') ? `${stats.find((x) => x.key === 'km')!.value} km` : null,
                  daysNightsLabel(nights),
                ]
                  .filter(Boolean)
                  .join(' · '),
              }}
              // The trip's ONE overflow menu, beside Share.
              overflow={{
                onDuplicate: count >= 2 ? handleDuplicateTrip : undefined,
                onDelete: savedTrip ? () => void handleDeleteTrip() : undefined,
              }}
            />
          ) : undefined
        }
      >
        {popularPickerOpen ? (
          // Takes over the panel body (empty or mid-trip). Taking a trip over
          // one with two or more places asks first.
          <PopularTripsPicker
            variant="all"
            routes={popularRoutes}
            loadingId={popularLoadingId}
            onTake={handleLoadPopularTrip}
            onRetry={retryPopularRoutes}
            onBack={() => setPopularPickerOpen(false)}
            currentStopCount={places}
          />
        ) : empty ? (
          // One field, one button, real trips to steal.
          <PlannerEmptyState
            onStart={handleStartPlanning}
            autoFocus={searchParams?.get('plan') === '1'}
            addWaypointMode={addWaypointMode}
            onToggleDropPin={() => setAddWaypointMode((v) => !v)}
            showPopular={getCountryCode() === 'au'}
            popularRoutes={popularRoutes}
            popularLoadingId={popularLoadingId}
            onTakePopular={handleLoadPopularTrip}
            onRetryPopular={retryPopularRoutes}
            onSeeAllPopular={openPopularPicker}
            onPreviewRoute={setPreviewRouteId}
            mapStrip={!mobileTripMap}
            onOpenMap={() => {
              setViewMode('map');
              setMapRequested(true);
            }}
          />
        ) : (
          <>
            {/* The itinerary, in real days: a band per day (date, where you
                sleep, sunset, weather), a clock per stop, the drive on the
                connector, and a "+" on every leg. */}
            <StopList
              stops={tripStops}
              legs={tripLegs}
              estimate={estimate}
              photos={stopPhotos}
              startDate={tripStartDate}
              onReorder={handleTripReorder}
              onRemoveStop={handleTripRemove}
              onEditNote={handleTripEditNote}
              onEditName={handleTripEditName}
              onChangeNights={handleChangeNights}
              onOptimise={handleOptimiseOrder}
              onInsertStop={handleInsertPlaceStop}
              route={tripRoutePath}
              highlightStopId={highlightStopId}
              selectedStopId={selectedStopId}
              hoveredStopId={hoveredStopId}
              onSelectStop={handleSelectStop}
              onHoverStop={setHoveredStopId}
              onHoverLeg={setHoveredLegToId}
              insertOpenAt={ladderInsertAt}
              onInsertOpenAtChange={setLadderInsertAt}
              maxDriveMinutesPerDay={maxDailyDriveHours * 60}
              onFindCamps={handleFindCampsNear}
              onAddCamp={handleAddCampForStop}
              onExploreNear={handleExploreNear}
              // The "+" box on a long leg links to every camp on that drive.
              renderLegCamps={renderLegCamps}
              // A stop's photo opens the stop up close.
              onOpenStop={setDetailStopId}
            />
            {/* A night's camp picks and a leg's camps: sheets over this
                column (the map beside them pins their rows). */}
            {renderCampPicks()}
            {renderLegSheet()}
            {detailStopId && tripStops.some((st) => st.id === detailStopId) && (
              <StopDetailSheet
                stops={tripStops}
                stopId={detailStopId}
                legs={tripLegs}
                photo={stopPhotos[detailStopId] ?? null}
                onClose={() => setDetailStopId(null)}
                onRemove={handleTripRemove}
                onOpenListing={(id) => {
                  setDetailStopId(null);
                  handleTripStopClick(id);
                }}
              />
            )}
            {/* Phones without the sheet: the pin key sits under the stops
                (the map is too small to carry it), until the days' colours
                take over. The sheet's map carries it in Layers. */}
            {!tripStopDays && !mobileTripMap && <RoleLegend stops={tripStops} variant="inline" className="mb-1 ml-5 mt-2 md:hidden" />}
            {/* Ideas for the long drives, each one added at its own leg. */}
            {count >= 2 && ladderInsertAt === null && (
              <SuggestedAlongRoute
                stops={tripStops}
                legs={tripLegInfo.source === 'estimate' ? [] : tripLegs}
                onAdd={handleInsertPlaceStop}
                route={tripRoutePath}
              />
            )}
            {/* Camps near your stops, for trips with no nights yet: once
                there are, each night's card offers them. */}
            {slotByStopId.size === 0 && <CampsNearStops stops={tripStops} onAdd={handleAddEntityToTrip} />}
          </>
        )}
      </TripPlannerPanel>
    );
  };

  // The result of a pin drop, on the map where the user is looking: a pulsing
  // dot while the spot is named, then "Added Orbost", or (in the sea) a pin
  // with a wave and a card hanging off it by a caret: "Nearest town is
  // Tamboon, 86 km north-west · Add Tamboon instead · Pick another spot".
  const renderMapPinCue = () => {
    const at = mapToast?.at ?? pinPending;
    const box = mapInstanceRef.current?.getDiv()?.getBoundingClientRect();
    const w = box?.width ?? 0;
    const h = box?.height ?? 0;
    // Keep the card inside the map: centred on the click, nudged in from the
    // edges, flipped above the pin near the bottom. The caret keeps pointing
    // at the click even when the card is nudged.
    const half = mapToast?.action || mapToast?.secondary ? 168 : 160;
    const roomy = !!(mapToast?.action || mapToast?.secondary || mapToast?.pending);
    // The zoom / layers stack owns the bottom-right corner: a card with
    // buttons keeps a clear lane (~88px) away from it.
    const rightLane = roomy ? 88 : 8;
    const toastLeft = at && w ? Math.min(Math.max(at.x, half + 8), Math.max(half + 8, w - half - rightLane)) : 0;
    const cardH = roomy ? 160 : 44;
    const flipUp = at && h ? (roomy ? at.y > cardH + 38 + 24 : at.y > h - 110) : false;
    const lifeMs = mapToast ? mapToastLifetime(mapToast) : 0;
    const tailShift = at ? Math.max(-120, Math.min(120, at.x - toastLeft)) : 0;
    const waterPin = !!mapToast?.at && mapToast.tone === 'warning';
    // The water pin is 28x38 with its tip on the click; the card clears it.
    const PIN_H = 38;
    return (
      <>
        {mapToast && !mapToast.at && (
          <div className="pointer-events-none absolute inset-x-0 top-4 z-20 flex justify-center px-16">
            <div key={mapToast.id} className="tp-fade-life" style={{ animationDuration: `${lifeMs}ms` }}>
              <MapToast toast={mapToast} />
            </div>
          </div>
        )}
        {pinPending && !mapToast && (
          <div
            className="pointer-events-none absolute z-20 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-solid border-white bg-tp-brand shadow motion-safe:animate-pulse"
            style={{ left: pinPending.x, top: pinPending.y }}
            aria-hidden="true"
          />
        )}
        {mapToast?.at && (
          <div key={mapToast.id} className="tp-fade-life pointer-events-none absolute inset-0 z-20" style={{ animationDuration: `${lifeMs}ms` }}>
            {waterPin && (
              <>
                <span
                  className="tp-ripple pointer-events-none absolute h-10 w-10 rounded-full border-[3px] border-solid border-tp-info"
                  style={{ left: mapToast.at.x, top: mapToast.at.y }}
                  aria-hidden="true"
                />
                {/* A full pin with a wave where the dot goes: "that's water",
                    said calmly. Red read as an error; this is a nudge. */}
                <svg
                  aria-hidden="true"
                  viewBox="0 0 28 38"
                  width="28"
                  height="38"
                  className="absolute text-tp-info drop-shadow-md"
                  style={{ left: mapToast.at.x - 14, top: mapToast.at.y - PIN_H }}
                >
                  <path d="M14 1C6.8 1 1 6.6 1 13.6 1 23 14 37 14 37s13-14 13-23.4C27 6.6 21.2 1 14 1z" fill="currentColor" stroke="#fff" strokeWidth="2" />
                  <path d="M7.5 12.2c1.1-1 2.1-1 3.2 0s2.1 1 3.3 0 2.1-1 3.2 0 2.1 1 3.3 0M7.5 16.2c1.1-1 2.1-1 3.2 0s2.1 1 3.3 0 2.1-1 3.2 0 2.1 1 3.3 0" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </>
            )}
            <div
              className="absolute z-30 -translate-x-1/2"
              style={{
                left: toastLeft,
                top: flipUp ? undefined : mapToast.at.y + 12,
                bottom: flipUp && h ? h - mapToast.at.y + (waterPin ? PIN_H + 10 : 12) : undefined,
                // The caret sits under the click, not the card's centre.
                ['--tail-x' as string]: `${tailShift}px`,
              }}
            >
              <MapToast toast={mapToast} tail={flipUp ? 'down' : 'up'} />
            </div>
          </div>
        )}
      </>
    );
  };

  // After an add (search, "+" between, or a pin), show the WHOLE trip, not
  // just the leg you were zoomed into: after a pin near Orbost the start and
  // destination were off-screen, so the new order couldn't be checked. Runs
  // when the stops change and again when the new road line lands.
  useEffect(() => {
    const at = fitAfterAddRef.current;
    if (!at || Date.now() - at > 8000) return;
    const map = mapInstanceRef.current;
    if (!map || typeof google === 'undefined' || tripStops.length < 2) return;
    const id = window.setTimeout(() => {
      const b = new google.maps.LatLngBounds();
      tripStops.forEach((st) => {
        if (typeof st.lat === 'number' && typeof st.lng === 'number') b.extend({ lat: st.lat, lng: st.lng });
      });
      (tripRoutePath ?? []).forEach((pt) => b.extend(pt));
      if (b.isEmpty()) return;
      // Phones: clear the chip row on top and the sheet at the bottom.
      const inset = mapBottomInsetRef.current;
      if (inset && map.getDiv().clientHeight - inset < 200) return; // sheet at full
      map.fitBounds(
        b,
        inset ? { top: 108, bottom: inset + PHONE_TOAST_LANE, left: 40, right: 60 } : { top: 72, bottom: 72, left: 64, right: 96 }
      );
    }, 450);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripStops, tripRoutePath]);

  // A new road line gets one glint: a short bright stretch runs the route
  // from start to finish, so the eye follows the trip once, end to end.
  // Once per new route; never with reduced motion.
  const routeGlintSigRef = useRef('');
  useEffect(() => {
    const map = mapInstanceRef.current;
    const path = tripRoutePath;
    if (!map || !path || path.length < 2 || typeof google === 'undefined' || mapViewMode !== 'trip') return;
    const sig = `${path.length}:${path[0].lat.toFixed(4)},${path[0].lng.toFixed(4)}:${path[path.length - 1].lat.toFixed(4)}`;
    if (sig === routeGlintSigRef.current) return;
    routeGlintSigRef.current = sig;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const line = new google.maps.Polyline({
      clickable: false,
      zIndex: 6,
      strokeColor: '#ffffff',
      strokeOpacity: 0.85,
      strokeWeight: 2.5,
      path: [],
      map,
    });
    const n = path.length;
    const span = Math.max(2, Math.round(n * 0.14));
    const DELAY = 350;
    const DURATION = 1200;
    const t0 = performance.now() + DELAY;
    let raf = 0;
    const tick = () => {
      const k = Math.max(0, Math.min(1, (performance.now() - t0) / DURATION));
      const e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
      const head = Math.round(e * (n - 1 + span));
      line.setPath(path.slice(Math.max(0, head - span), Math.min(n, head + 1)));
      if (k < 1) raf = requestAnimationFrame(tick);
      else line.setMap(null);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      line.setMap(null);
    };
  }, [tripRoutePath, mapReady, mapViewMode]);

  // Top-left of the split map: what it shows (the trip, or campsites along
  // it) as light chips; the map adds Fuel and Dump points after these while
  // a trip is shown. ONE chip system, one place: top left. The browse map
  // (no planner) keeps "Update as I move" here instead.
  // Days of driving (1 + nights before the last stop): the key's day swatches.
  const tripDayCount = useMemo(
    () => 1 + tripStops.slice(0, -1).reduce((n, st) => n + Math.max(0, st.nights ?? 0), 0),
    [tripStops]
  );
  // Phones while planning: the way back floats on the map (the page's top
  // bar is gone so the map gets the whole screen). Pinned before the chip
  // row, the same 36px height as the chips, so it never scrolls away.
  const renderMapBack = () =>
    mobileTripMap ? (
      <button
        type="button"
        onClick={backToCampsites}
        aria-label="Back to campsites"
        title="Back to campsites"
        className="flex h-9 w-9 min-h-0 min-w-0 flex-none items-center justify-center rounded-full border border-solid border-tp-line bg-white p-0 text-tp-ink shadow-tp-card transition-colors duration-150 ease-tp-out hover:bg-tp-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
        data-testid="planner-back"
      >
        <IconArrowLeft className="h-[18px] w-[18px]" />
      </button>
    ) : null;
  const renderMapTopChips = () => (
    <>
      {/* What the map shows: ONE segmented switch (the trip, or campsites
          along it). The overlay toggles after the divider add to it. */}
      {hasTripStops && (
        <div
          role="group"
          aria-label="Map shows"
          // One segmented switch at every size: the MODE (the trip, or camps
          // along it) reads apart from the overlay toggles after it.
          // Inside the map's one chip rail (its pill is the backing).
          className="flex h-8 flex-none items-center gap-0.5"
        >
          {(
            [
              { mode: 'trip', label: 'Trip', icon: <IconRoute className="h-4 w-4" /> },
              { mode: 'search', label: 'Campsites', icon: <IconTent className="h-4 w-4" /> },
            ] as const
          ).map((m) => (
            <button
              key={m.mode}
              type="button"
              aria-pressed={mapViewMode === m.mode}
              onClick={() => {
                setMapViewMode(m.mode);
                // "Campsites" on a trip means camps along it: the one entry
                // point (the planner no longer has its own "Find camps").
                if (m.mode === 'search' && tripStops.length >= 2 && !isRouteSearch) {
                  const args = corridorSearchArgs(tripStops, routeName);
                  if (args) handleSearchAlongRoute(args.start, args.end, args.name, args.mids, tripStops);
                }
              }}
              className={`flex h-8 min-h-0 flex-none items-center gap-1.5 whitespace-nowrap rounded-full border-0 px-2.5 text-[13px] font-semibold transition-colors duration-150 ease-tp-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 sm:px-3 ${
                mapViewMode === m.mode ? 'bg-tp-ink text-white' : 'bg-transparent text-tp-ink [@media(hover:hover)]:hover:bg-tp-surface'
              }`}
            >
              {m.icon}
              {m.label}
            </button>
          ))}
        </div>
      )}
      {hasTripStops && mapViewMode === 'trip' && (
        <span aria-hidden="true" className="mx-1 h-5 w-px flex-none self-center bg-tp-line-strong" />
      )}
      {/* "Update as I move" is a browsing control: never in the planner. */}
      {mapViewMode !== 'trip' && !mobileTripMap && !hasTripStops && !plannerQuiet && (
        <button
          type="button"
          role="switch"
          aria-checked={searchAsMove}
          onClick={() => setSearchAsMove((prev) => !prev)}
          className="flex h-9 min-h-0 flex-none select-none items-center gap-2.5 whitespace-nowrap rounded-full border border-solid border-white/70 bg-white/95 pl-3.5 pr-2 text-[13px] font-semibold text-tp-ink shadow-tp-card backdrop-blur-sm transition-colors duration-150 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
        >
          Update as I move
          <span
            aria-hidden="true"
            className={`relative h-5 w-9 flex-none rounded-full transition-colors duration-150 ease-tp-out ${searchAsMove ? 'bg-tp-brand' : 'bg-tp-line-strong'}`}
          >
            <span
              className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-[0_1px_2px_rgba(22,35,58,0.3)] transition-[left] duration-150 ease-tp-out ${searchAsMove ? 'left-[18px]' : 'left-0.5'}`}
            />
          </span>
        </button>
      )}
    </>
  );

  // ── Lazy map gate (see lib/explorerMapGate) ─────────────────────────────
  // Derived intent: anything that frames or edits the map loads it now.
  const mapIntent =
    mapRequested ||
    isSearching ||
    isRouteSearch ||
    hasTripStops ||
    addWaypointMode ||
    !!regionBoundary;
  const mapNearViewport = useNearViewport(mapSectionRef);
  const pageSettled = usePageSettled();
  // The split map panel is `hidden md:block` — below md it was mounting (and
  // downloading Maps JS) into a display:none box. Mobile gets the map from
  // Full Map instead, exactly as before.
  const isDesktopViewport = useMediaQuery('(min-width: 768px)');
  // Phones: while a trip is being planned, the split view is a map with a
  // bottom sheet over it (the planner), not a list with no map. The sheet
  // snaps to three heights; the map gets what is left.
  // The empty planner gets the same map peek, so the layout doesn't jump
  // between "no stops" and "stops".
  const mobileTripMap =
    !isDesktopViewport && viewMode === 'split' && (hasTripStops || addWaypointMode || (isTripSectionOpen && plannerIntent));
  // Opens at peek: the map is the first thing you see, the trip's summary
  // and actions sit under your thumb. (See PlannerBottomSheet.)
  const [sheetSnap, setSheetSnap] = useState<SheetSnap>('peek');
  // The sheet's settled height: the strip of map it covers.
  const [sheetInset, setSheetInset] = useState(PEEK_HEIGHT);
  const [mapActivated, setMapActivated] = useState(false);
  // ca_preview: the store-style hero pushes the map below the first screen,
  // where the near-viewport half of the gate left a "Loading map" box in
  // place until the visitor scrolled. In preview the map
  // mounts straight away on desktop (it is the page's main tool, the store
  // never shows a placeholder block), and Maps JS starts downloading alongside
  // the map component chunk instead of after it.
  const caPreview = useCaPreview();
  useEffect(() => {
    if (!caPreview || !(mapIntent || isDesktopViewport)) return;
    void import('./components/GoogleMapComponent');
    getGoogleMapsLoader().load().catch(() => {});
  }, [caPreview, mapIntent, isDesktopViewport]);
  // ca_preview: the map row and the page floor are sized from the window's
  // real height in pixels (--ca-vh), not 100vh. A 100vh row re-lays itself
  // out whenever something measures the page at a different height (a
  // full-page capture, a print preview), which left the map a sliver at the
  // left of a blank canvas and a white band under the footer.
  useEffect(() => {
    if (!caPreview) return;
    const root = document.documentElement;
    const set = () => root.style.setProperty('--ca-vh', `${window.innerHeight}px`);
    set();
    window.addEventListener('resize', set);
    window.addEventListener('orientationchange', set);
    return () => {
      window.removeEventListener('resize', set);
      window.removeEventListener('orientationchange', set);
      root.style.removeProperty('--ca-vh');
    };
  }, [caPreview]);
  useEffect(() => {
    if (mapActivated) return;
    if (shouldActivateExplorerMap({ userIntent: mapIntent || (caPreview && isDesktopViewport), nearViewport: mapNearViewport, pageSettled })) {
      setMapActivated(true);
    }
  }, [mapActivated, mapIntent, mapNearViewport, caPreview, isDesktopViewport, pageSettled]);
  const mountSplitMap = mapActivated && (isDesktopViewport || mobileTripMap);
  // The phone sheet at peek with nothing planned yet shows its own compact
  // start row instead of the (clipped) empty planner.
  mapBottomInsetRef.current = mobileTripMap ? sheetInset : 0;
  const emptySheetPeek = mobileTripMap && sheetSnap === 'peek' && !hasTripStops && !popularPickerOpen;

  // Tell the floating assistant where it may sit: beside the planner rail on
  // desktop, just above the sheet on phones (never over planner content).
  useEffect(() => {
    const root = document.documentElement;
    // 'rail': desktop split view (the bubble sits over the map, left of the
    // results rail, never on a card); 'sheet': the phone planner sheet.
    const mode = mobileTripMap ? 'sheet' : isDesktopViewport && viewMode === 'split' ? 'rail' : '';
    if (mode) root.setAttribute('data-trip-planner', mode);
    else root.removeAttribute('data-trip-planner');
    if (mode !== 'sheet') {
      root.style.removeProperty('--tp-fab-bottom');
      return;
    }
    let raf = 0;
    const place = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const top = sidebarRef.current?.getBoundingClientRect().top ?? window.innerHeight;
        const bottom = Math.max(24, Math.min(window.innerHeight - top + 12, window.innerHeight - 140));
        root.style.setProperty('--tp-fab-bottom', `${Math.round(bottom)}px`);
      });
    };
    place();
    const t = window.setTimeout(place, 360); // after the sheet's height transition
    window.addEventListener('scroll', place, { passive: true });
    window.addEventListener('resize', place);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t);
      window.removeEventListener('scroll', place);
      window.removeEventListener('resize', place);
    };
  }, [plannerFills, mobileTripMap, isDesktopViewport, sheetSnap, viewMode]);
  // The phone map stays full height under the sheet: once the sheet settles,
  // re-frame the trip in the part of the map you can still see.
  useEffect(() => {
    if (!mobileTripMap) return;
    const t = window.setTimeout(() => {
      const map = mapInstanceRef.current;
      // Sheet at full (or nearly): no map left to frame the trip in.
      const mapH = map?.getDiv().clientHeight ?? 0;
      if (mapH - sheetInset < 200) return;
      const pts = tripStops.filter((st) => typeof st.lat === 'number' && typeof st.lng === 'number');
      if (!map || !pts.length || typeof window === 'undefined' || !window.google) return;
      if (pts.length === 1) {
        map.panTo({ lat: pts[0].lat as number, lng: pts[0].lng as number });
        return;
      }
      const b = new google.maps.LatLngBounds();
      pts.forEach((st) => b.extend({ lat: st.lat as number, lng: st.lng as number }));
      // Top clears the chip row (36px chip + 12px inset), the pin key under
      // it, and a pin's full height; the bottom clears the sheet. Sides stay tight so the route
      // fills the map. (Same padding as GoogleMapComponent's sheet padding.)
      // The bottom also clears Google's logo (it rides the sheet's top edge)
      // and, while a pin is armed, the banner with "Place here": arming
      // drops the sheet to peek, and the trip re-fills the map it uncovers.
      map.fitBounds(b, { top: 108, right: 60, bottom: sheetInset + (addWaypointMode ? PIN_AIM_BANNER + 24 : PHONE_TOAST_LANE), left: 40 });
    }, 60);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileTripMap, sheetInset, mapReady, tripStops, addWaypointMode]);

  // Drop-a-pin on a phone: the sheet gets out of the way (peek) so there is
  // map to tap, then comes back to where it was once the stop is in.
  const snapBeforePinRef = useRef<SheetSnap | null>(null);
  useEffect(() => {
    if (!mobileTripMap) return;
    if (addWaypointMode) {
      if (snapBeforePinRef.current === null) snapBeforePinRef.current = sheetSnap;
      if (sheetSnap !== 'peek') setSheetSnap('peek');
      return;
    }
    const back = snapBeforePinRef.current;
    snapBeforePinRef.current = null;
    if (!back || back === 'peek') return;
    // A short beat, then the sheet returns (the confirmation rides above it).
    const t = window.setTimeout(() => setSheetSnap((cur) => (cur === 'peek' ? back : cur)), 350);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addWaypointMode, mobileTripMap]);
  // Phones, pin armed: where the crosshair points, and where a stop dropped
  // there would go (the same rule a real drop uses), said in the pin card.
  const [pinAim, setPinAim] = useState<{ lat: number; lng: number } | null>(null);
  const readPinAim = useCallback((): { lat: number; lng: number } | null => {
    const map = mapInstanceRef.current;
    const c = map?.getCenter();
    const z = map?.getZoom();
    if (!map || !c || typeof z !== 'number') return null;
    const div = map.getDiv();
    const { x, y } = pinAimPoint(div.clientWidth, div.clientHeight, mapBottomInsetRef.current);
    return pointToLatLng({ lat: c.lat(), lng: c.lng() }, z, div.clientWidth, div.clientHeight, x, y);
  }, []);
  const pinAimOn = addWaypointMode && mobileTripMap && mapReady;
  useEffect(() => {
    if (!pinAimOn) {
      setPinAim(null);
      return;
    }
    const map = mapInstanceRef.current;
    if (!map) return;
    let raf = 0;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setPinAim(readPinAim()));
    };
    update();
    const ls = ['center_changed', 'zoom_changed', 'idle'].map((ev) => map.addListener(ev, update));
    return () => {
      cancelAnimationFrame(raf);
      ls.forEach((l) => l.remove());
    };
  }, [pinAimOn, sheetInset, readPinAim]);
  // Pin mode is shown only on a settled map: arming drops the sheet, the
  // trip re-frames in the map it uncovers, and until that camera move and
  // its tiles finish, the crosshair and the dashed ghost stay hidden (a
  // mid-zoom map draws labels twice and lines off their pins).
  const [pinReady, setPinReady] = useState(false);
  useEffect(() => {
    setPinReady(false);
    if (!pinAimOn) return;
    let poll = 0;
    // The sheet's drop (320ms) and the re-frame it triggers come first.
    const start = window.setTimeout(() => {
      const t0 = Date.now();
      poll = window.setInterval(() => {
        const settled = !!document.querySelector('[data-testid="explorer-map"] [data-map-settled="1"]');
        if (settled || Date.now() - t0 > 2500) {
          window.clearInterval(poll);
          setPinReady(true);
        }
      }, 80);
    }, 520);
    return () => {
      window.clearTimeout(start);
      window.clearInterval(poll);
    };
  }, [pinAimOn, sheetInset]);
  const pinGhost = useMemo(
    () => (pinAim && pinReady ? pinGhostFor(tripStops, pinAim) : null),
    [pinAim, pinReady, tripStops]
  );
  const [pinDrop, setPinDrop] = useState<{ key: number; x: number; y: number } | null>(null);
  useEffect(() => {
    if (!pinDrop) return;
    const t = window.setTimeout(() => setPinDrop(null), 700);
    return () => window.clearTimeout(t);
  }, [pinDrop]);
  // No dashed ghost line on the map while armed: a second, straight route
  // drawn through the crosshair read as "already placed" and competed with
  // the real road. The card under the crosshair says where the stop goes.
  const placeAtAim = useCallback(() => {
    const p = readPinAim();
    const map = mapInstanceRef.current;
    if (!p || !map) return;
    const div = map.getDiv();
    const at = pinAimPoint(div.clientWidth, div.clientHeight, mapBottomInsetRef.current);
    setPinDrop({ key: Date.now(), ...at });
    void handleAddWaypointAt(p.lat, p.lng, at);
  }, [readPinAim, handleAddWaypointAt]);

  // The empty planner is the task: the campsite clusters behind it step back
  // (smaller, faded) so the map reads as Australia, not a wall of bubbles.
  const plannerQuiet = !hasTripStops && isTripSectionOpen && plannerIntent && viewMode === 'split';
  // Phones: once the popular trips are drawn, frame THEM (not the whole
  // continent with its empty sea) in the map the sheet leaves uncovered.
  const previewFrameKey = Object.keys(previewPaths).sort().join('|');
  useEffect(() => {
    if (!plannerQuiet || !mapReady || !mapInstanceRef.current) return;
    if (mobileTripMap && sheetSnap === 'full') return; // no map to frame
    const map = mapInstanceRef.current;
    const paths = Object.values(previewPaths).filter((pth) => pth.length >= 2);
    if (mobileTripMap && paths.length >= 2 && typeof google !== 'undefined') {
      const b = new google.maps.LatLngBounds();
      paths.forEach((pth) => pth.forEach((pt) => b.extend(pt)));
      // Frame the mainland north to south (Cape York to Tasmania), not just
      // the routes: a raster map's whole-level zoom leaves slack, and on a
      // route-only box it fell to open sea above the sheet with Papua New
      // Guinea at the top. The routes stay inside it.
      if (getCountryCode() === 'au') {
        b.extend({ lat: -10.6, lng: b.getCenter().lng() });
        b.extend({ lat: -43.7, lng: b.getCenter().lng() });
      }
      // Top clears the back button and the route labels hanging above
      // their lines; the bottom clears the sheet and Google's logo. The
      // routes hang from the top (spare room falls to the sea above the
      // sheet, not over Papua New Guinea).
      const pad = { top: 100, right: 20, bottom: sheetInset + 36, left: 20 };
      const ne = b.getNorthEast();
      const sw = b.getSouthWest();
      const div = map.getDiv();
      const view = fitTopAnchored(
        { north: ne.lat(), south: sw.lat(), east: ne.lng(), west: sw.lng() },
        { width: div.clientWidth, height: div.clientHeight },
        pad,
        { minZoom: 3 }
      );
      if (view) {
        map.setZoom(view.zoom);
        map.setCenter(view.center);
      } else {
        map.fitBounds(b, pad);
      }
      return;
    }
    applyInitialMapFrame(map, 16);
    // Phones: re-frame the country in what the sheet leaves uncovered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plannerQuiet, mapReady, applyInitialMapFrame, sheetInset, previewFrameKey]);
  useEffect(() => {
    const root = document.documentElement;
    if (plannerQuiet) root.setAttribute('data-trip-planner-quiet', '');
    else root.removeAttribute('data-trip-planner-quiet');
    return () => root.removeAttribute('data-trip-planner-quiet');
  }, [plannerQuiet]);
  useEffect(
    () => () => {
      document.documentElement.removeAttribute('data-trip-planner');
      document.documentElement.style.removeProperty('--tp-fab-bottom');
    },
    []
  );

  // Popular-trip shapes for the empty planner's map (desktop: the map is
  // beside the shelf). Six trips = the shelf's first screenful.
  useEffect(() => {
    if (!plannerQuiet || !mountSplitMap || !popularRoutes?.length) return;
    const todo = popularRoutes.slice(0, 6).filter((r) => !previewFetchRef.current.has(r.id));
    if (!todo.length) return;
    todo.forEach((r) => previewFetchRef.current.add(r.id));
    // Not cancelled on re-run: the ids are marked fetched, so the shapes must
    // land whenever they arrive.
    void Promise.all(
      todo.map((r) =>
        fetchSystemTrip(r.id).then((trip) => {
          const path = (trip?.stops ?? [])
            .filter((st) => typeof st.lat === 'number' && typeof st.lng === 'number')
            .map((st) => ({ lat: st.lat as number, lng: st.lng as number }));
          return [r.id, path] as const;
        })
      )
    ).then((pairs) => {
      setPreviewPaths((prev) => {
        const next = { ...prev };
        pairs.forEach(([id, path]) => {
          if (path.length >= 2) next[id] = path;
        });
        return next;
      });
    });
  }, [plannerQuiet, mountSplitMap, popularRoutes]);
  const previewRoutes = useMemo(
    () =>
      plannerQuiet
        ? Object.entries(previewPaths).map(([id, path]) => ({
            id,
            path,
            label: popularRoutes?.find((r) => r.id === id)?.name,
          }))
        : undefined,
    [plannerQuiet, previewPaths, popularRoutes]
  );

  // Road distances for everyone, on every screen. Where the map is drawing
  // the route it reports Google's legs (handleRouteDrawn); phones show the
  // list instead, so ask the same Directions service directly. Without this
  // a popular trip read "971 km · 12h" on desktop and "≈ 782 km — sign in to
  // route it by road" on a phone.
  const tripLegSource = tripLegInfo.source;
  useEffect(() => {
    if (mountSplitMap || viewMode === 'map') return; // the map reports legs
    // Signed in too: the figures come from the road route for everyone.
    if (tripLegSource === 'google' || tripPlaceCount(tripStops) < 2) return;
    const points = tripStops.map((st) =>
      typeof st.lat === 'number' && typeof st.lng === 'number' ? { lat: st.lat, lng: st.lng } : null
    );
    if (points.some((pt) => !pt)) return;
    const key = stopIdsKey;
    let cancelled = false;
    const timer = setTimeout(() => {
      // The road too, not just its figures: leg camp searches follow it.
      fetchRoadRoute(points as { lat: number; lng: number }[]).then((r) => {
        if (cancelled) return;
        if (r) setGoogleLegs({ key, legs: r.legs, paths: r.paths });
      });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [mountSplitMap, viewMode, tripLegSource, tripStops, stopIdsKey]);

  // The "N results" label in the browse view comes from the map's all-points
  // load (onPointsLoaded). Phones no longer mount the hidden split map, so
  // fetch that same (server-cached) list once the page settles to keep the
  // label showing the real total rather than the featured page size.
  useEffect(() => {
    if (!pageSettled || allPointsTotal > 0 || !showAllPoints) return;
    if (isDesktopViewport || (typeof window !== 'undefined' && window.matchMedia?.('(min-width: 768px)').matches)) return;
    let cancelled = false;
    fetchAllMapPoints()
      .then((points) => {
        if (cancelled) return;
        if (points.length > 0) setAllPointsTotal(points.length);
        else setAllPointsGaveUp(true); // fetchAllMapPoints returns [] on error
      })
      .catch(() => {
        if (!cancelled) setAllPointsGaveUp(true);
      });
    return () => {
      cancelled = true;
    };
  }, [pageSettled, allPointsTotal, showAllPoints, isDesktopViewport]);

  // Desktop gets the total from the map's own all-points load. If that never
  // reports (API down), stop showing "Loading…" and fall back to the page size.
  useEffect(() => {
    if (allPointsTotal > 0 || allPointsGaveUp) return;
    if (!mountSplitMap && viewMode !== 'map') return;
    const timer = setTimeout(() => setAllPointsGaveUp(true), 15000);
    return () => clearTimeout(timer);
  }, [allPointsTotal, allPointsGaveUp, mountSplitMap, viewMode]);

  // The bar above the map says "Trip planner" (not a result count) whenever
  // the planner is the task, empty or not.
  const planningBar = (mapViewMode === 'trip' && hasTripStops) || plannerQuiet;
  const backToCampsites = () => {
    if (hasTripStops) setMapViewMode('search');
    else setPlannerIntent(false);
  };
  // ca_preview: the site breadcrumb carries the task (Home > Campsite
  // Explorer > Trip planner), and its Campsite Explorer crumb steps back.
  const crumbTail = caPreview && planningBar ? (takenTrail ? 'Your copy' : 'Trip planner') : null;
  useEffect(() => {
    if (!caPreview) return;
    (window as unknown as { __caCrumbTail?: string | null }).__caCrumbTail = crumbTail;
    window.dispatchEvent(new CustomEvent(CRUMB_TAIL_EVENT, { detail: crumbTail }));
  }, [caPreview, crumbTail]);
  const backToCampsitesRef = useRef(backToCampsites);
  backToCampsitesRef.current = backToCampsites;
  useEffect(() => {
    if (!caPreview) return;
    const on = () => backToCampsitesRef.current();
    window.addEventListener(CRUMB_BACK_EVENT, on);
    return () => window.removeEventListener(CRUMB_BACK_EVENT, on);
  }, [caPreview]);

  // Search from the hero: close the Filters panel (left open, the results
  // updated off-screen under it) and, when the results sit below the fold
  // (phones; a tall hero), bring them into view.
  const runHeroSearch = () => {
    const hadFilters = showFilters;
    setShowFilters(false);
    void Promise.resolve(handleSearch()).finally(() => {
      if (!hadFilters && !caPreview) return;
      requestAnimationFrame(() => {
        if (typeof window === 'undefined') return;
        // Anchor on the hero's bottom edge, where the results begin: the
        // results bar is sticky, so its own rect can read as already "at the
        // top" and scrollIntoView on it overshot past the first cards.
        const hero = document.querySelector('.ca-xh, .fg-hero');
        const bar = resultsBarRef.current;
        const top = hero ? hero.getBoundingClientRect().bottom : bar?.getBoundingClientRect().top;
        if (typeof top !== 'number') return;
        if (top > window.innerHeight * 0.6 || top < 0) {
          window.scrollTo({ top: Math.max(0, window.scrollY + top), behavior: 'smooth' });
        }
      });
    });
  };

  const handlePlanTripCta = useCallback(() => {
    setIsTripSectionOpen(true);
    setPlannerIntent(true);
    setMapRequested(true);
    setViewMode((mode) => (mode === 'map' ? 'split' : mode));
    // Let the panel render open before scrolling to it.
    requestAnimationFrame(() => scrollPlannerIntoViewRef.current?.());
  }, []);

  return (
    <div className={`${caPreview ? 'ca-explorer-root ' : ''}flex min-h-screen flex-col bg-gray-50`}>
      {/* Phones while planning: the planner layer covers the page; the hero
          (and its trust strip, tabs and chips) leaves the page entirely so
          nothing behind the map is read out or tabbed to. */}
      {/* ca_preview, desktop: the planner (?plan=1, "Plan a road trip", a
          trip in progress) opens straight onto the map and planner, as it
          does on phones: one IA for the same URL, no marketing hero above. */}
      <div className={mobileTripMap || (caPreview && isDesktopViewport && planningBar) ? 'hidden' : 'contents'}>
      <HeroSearch
        onPlanTrip={handlePlanTripCta}
        searchQuery={searchQuery}
        selectedType={selectedType}
        showFilters={showFilters}
        selectedFeatures={selectedFeatures}
        radius={radius}
        options={options}
        isSearching={isSearching}
        onLocationSearch={handleLocationSearch}
        onLocationSelect={handleLocationSelect}
        onLocationChange={setSearchQuery}
        onTypeChange={setSelectedType}
        onToggleFilters={() => setShowFilters(!showFilters)}
        onToggleFeature={toggleFeature}
        onClearFilters={clearFilters}
        onRadiusChange={setRadius}
        onSearch={runHeroSearch}
        onPressEnter={runHeroSearch}
        onPickRegion={(name) => {
          setSearchQuery(`region:${name}`);
          handleSearch({ location: `region:${name}` });
        }}
        onPickLocation={(location) => {
          // escapeto region chips — plain geocoded location search (no Region
          // nodes outside AU, so region:<name> would come back empty).
          setSearchQuery(location);
          handleSearch({ location });
        }}
        onPickRoute={(route) => {
          const m = route.match(/^(.+?)\s+to\s+(.+)$/i);
          if (m) {
            setSearchQuery(route);
            handleRouteSearch(m[1].trim(), m[2].trim(), route);
          }
        }}
        features={features}
        assistantMessages={assistantMessages}
        assistantSuggestions={assistantSuggestions}
        assistantIsOpen={assistantOpen}
        assistantHasUnread={assistantHasUnread}
        assistantIsProcessing={assistantBooting || assistantIsProcessing}
        onToggleAssistant={toggleAssistant}
        onSendAssistant={handleAssistantSend}
      />
      </div>

      {/* ══════════════════════════════════════════════════════════════════
           VIEW 1 — SPLIT (default): Map left + List right
           VIEW 2 — LIST ONLY: Scrollable card grid, no map
           Both share the same list rendering; the map is simply hidden in list mode.
           ══════════════════════════════════════════════════════════════════ */}
      {viewMode !== 'map' && (
        // Full-bleed: the map runs edge to edge like a real map app, not
        // inside the page's 1280px column (body has overflow-x: hidden).
        <div
          className={
            mobileTripMap
              ? // Phones while planning: the planner owns the screen. A
                // fixed layer (top bar, then the map with the sheet over it)
                // covers the marketing page, which stops scrolling underneath
                // (html[data-planner-sheet] in globals.css).
                'tp-plan-layer fixed inset-0 z-40 flex flex-col bg-white'
              : 'relative ml-[calc(50%-50vw)] w-screen max-w-[100vw]'
          }
        >
          {/* View mode toggle bar */}
          {/* Sand-toned to continue the hero's palette — this bar sits directly
              under the search console, so a white/blue strip re-introduced the
              generic chrome the hero was redesigned away from.
              The map row below is h-[calc(100vh-48px)], so this bar must stay
              48px: its h-8 buttons carry min-h-0 to opt out of the global
              44px button min-height, which made the bar 60px and pushed the
              map's bottom edge below the fold. */}
          <div ref={resultsBarRef} className={`${caPreview ? 'ca-results-bar ' : ''}sticky top-0 z-20 box-border h-12 flex-none items-center justify-between gap-2 border-0 border-b border-solid border-[#16233a]/[.12] bg-[#f5f8fc] px-3 ${mobileTripMap ? 'hidden' : 'flex'}`}>
            <div className="flex min-w-0 items-center gap-2">
              {/* One line, always — this bar must stay 48px (see above). */}
              <span className="truncate whitespace-nowrap text-sm font-semibold text-[#16233a]">
                {(mapViewMode === 'trip' && hasTripStops) || plannerQuiet ? (
                  // Breadcrumb, not a second title: the trip's name is the
                  // planner's own heading.
                  <nav aria-label="Breadcrumb" className={`${caPreview ? 'ca-bar-crumb ' : ''}inline-flex items-center gap-1 text-[13px]`}>
                    {/* Phones: a back arrow, not a breadcrumb trail. */}
                    <button
                      type="button"
                      onClick={backToCampsites}
                      aria-label="Back to campsites"
                      title="Back to campsites"
                      className="-ml-1.5 flex h-9 w-9 min-h-0 items-center justify-center rounded-tp-sm border-0 bg-transparent text-[#3a4a63] transition-colors duration-150 hover:bg-white hover:text-tp-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 sm:hidden"
                    >
                      <IconArrowLeft className="h-[18px] w-[18px]" />
                    </button>
                    {takenTrail && takenFrom?.path ? (
                      // Taken from a shared page: the trail says where you came from and what this is.
                      <a
                        href={withBase(takenFrom.path)}
                        className="hidden min-h-0 rounded-sm font-medium text-[#3a4a63] no-underline transition-colors duration-150 hover:text-tp-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 sm:inline"
                      >
                        Shared trip
                      </a>
                    ) : (
                      <button
                        type="button"
                        onClick={backToCampsites}
                        className="hidden min-h-0 rounded-sm border-0 bg-transparent p-0 font-medium text-[#3a4a63] transition-colors duration-150 hover:text-tp-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 sm:inline"
                      >
                        {takenTrail ? 'Shared trip' : 'Campsites'}
                      </button>
                    )}
                    <IconChevronRight className="hidden h-3.5 w-3.5 text-tp-faint sm:block" />
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[14px] font-semibold text-tp-ink sm:text-[13px]" aria-current="page">
                      <IconRoute className="hidden h-4 w-4 text-tp-brand sm:block" />
                      {takenTrail ? 'Your copy' : 'Trip planner'}
                    </span>
                  </nav>
                ) : (typeFilterActive ? typedCount === null : resultsCount === null) ? (
                  <span className="font-medium text-[#3a4a63]">Loading campsites…</span>
                ) : (
                  <>
                    <span className={`${caPreview ? 'ca-results-count ' : ''}text-[#1a68d1] tabular-nums`}>{shownMapCount.toLocaleString()}</span> {mapCountLabel}
                    {/* Phones: no room beside the view toggle (it ran under
                        it at 390px); the list header carries "Showing N of M". */}
                    {countNote && <span className={`${caPreview ? 'ca-results-note ' : ''}hidden font-medium text-[#3a4a63] sm:inline`} data-testid="results-count-note"> · {countNote}</span>}
                  </>
                )}
              </span>
              {isSearching && <Spin size="small" />}
              {/* ca_preview: the assistant opens from here, not a pill over the page. */}
              {caPreview && !mobileTripMap && (
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent('trip-chat-open'))}
                  aria-label="Ask the camping assistant"
                  className="ca-ask-link hidden md:inline-flex"
                >
                  <IconSparkle className="ca-ask-link__icon" aria-hidden="true" />
                  {/* Desktop only: at 360-390px there is no room beside the
                      Map + list toggle. Phones get the store chat control
                      floating bottom-right instead (data-chat-fab below). */}
                  Ask the camping assistant
                </button>
              )}
              {isRouteSearch && routeVehicle && (
                <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 text-teal-700 text-[11px] font-semibold px-2 py-0.5 border border-teal-200" title="Route is calculated for your vehicle (avoids unsuitable roads)">
                  🚐 {routeVehicle.name} · {routeVehicle.type}
                </span>
              )}
              {isRouteSearch && canRoute && !routeVehicle && tripVehicleType !== 'car' && (
                <span
                  className="hidden sm:inline-flex items-center gap-1 rounded-full bg-teal-50 text-teal-700 text-[11px] font-semibold px-2 py-0.5 border border-teal-200"
                  title="Route and campsite picks are planned for this vehicle"
                >
                  🚐 Planned for a {tripVehicleType}
                </span>
              )}
              {isRouteSearch && !routeVehicle && (tripVehicleType === 'car' || !canRoute) && !isSearching && (
                <a
                  href={withBase("/account/vehicles")}
                  className="hidden sm:inline-flex items-center gap-1 rounded-full bg-gray-100 text-gray-600 text-[11px] font-medium px-2 py-0.5 border border-gray-200 hover:border-teal-300 hover:text-teal-700 transition-colors"
                  title="Sign in and add a vehicle to get caravan/RV-safe routing that avoids unsuitable roads"
                >
                  🚐 Add a vehicle for caravan/RV-safe routing →
                </a>
              )}
            </div>
            {/* Phones while planning: the sheet already trades map for list,
                so the bar carries one icon toggle (show the list / show the
                map), not a three-way switch. */}
            {planningBar && mobileTripMap && (
              <button
                type="button"
                onClick={() => setSheetSnap(sheetSnap === 'full' ? 'half' : 'full')}
                aria-label={sheetSnap === 'full' ? 'Show the map' : 'Show the list'}
                title={sheetSnap === 'full' ? 'Show the map' : 'Show the list'}
                className="flex h-9 w-9 min-h-0 flex-none items-center justify-center rounded-tp border border-solid border-[#16233a]/[.12] bg-white text-tp-ink shadow-[0_1px_2px_rgba(22,35,58,0.08)] transition-colors duration-150 ease-tp-out hover:bg-tp-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 sm:hidden"
                data-testid="sheet-view-toggle"
              >
                {sheetSnap === 'full' ? <IconMap className="h-[18px] w-[18px]" /> : <IconList className="h-[18px] w-[18px]" />}
              </button>
            )}
            {/* The ONE view switch. Same three labels and icons at every size;
                the map's own control only chooses what the map shows. */}
            <div
              className={`${caPreview ? 'ca-view-toggle ' : ''}${planningBar && mobileTripMap ? 'hidden sm:flex' : 'flex'} flex-none items-center gap-0.5 rounded-tp bg-[#16233a]/[.06] p-0.5`}
              role="group"
              aria-label="View"
            >
              {(
                [
                  { mode: 'split', label: 'Map + list', icon: <IconSplit className="h-4 w-4" /> },
                  { mode: 'list', label: 'List', icon: <IconList className="h-4 w-4" /> },
                  { mode: 'map', label: 'Map', icon: <IconMap className="h-4 w-4" /> },
                ] as const
              ).map((v) => (
                <button
                  key={v.mode}
                  type="button"
                  aria-pressed={viewMode === v.mode}
                  aria-label={v.label}
                  title={v.label}
                  onClick={() => {
                    setViewMode(v.mode);
                    if (v.mode !== 'list') setMapRequested(true);
                  }}
                  // A quiet segmented control: the selected view is a white
                  // tab, not a dark fill, so it never outweighs the trip.
                  className={`flex h-8 min-h-0 items-center gap-1.5 rounded-[8px] border-0 text-[13px] font-semibold transition-[background-color,color,box-shadow] duration-150 ease-tp-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 ${
                    planningBar ? 'px-2 sm:px-2.5' : 'px-2.5'
                  } ${
                    viewMode === v.mode
                      ? 'bg-white text-tp-ink shadow-[0_1px_2px_rgba(22,35,58,0.16)]'
                      : 'bg-transparent text-tp-muted hover:text-tp-ink'
                  }`}
                >
                  {/* While planning on a phone the bar is tight: words, no
                      icons (icons alone read as a puzzle). */}
                  <span className={planningBar ? 'hidden sm:inline-flex' : 'inline-flex'}>{v.icon}</span>
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          <div
            ref={mapSectionRef}
            className={
              mobileTripMap
                ? 'relative min-h-0 flex-1 overflow-hidden'
                : `${caPreview && viewMode === 'split' ? 'ca-map-row ' : ''}flex overflow-hidden ${viewMode === 'split' ? 'h-[calc(100vh-48px)] flex-col md:flex-row' : ''}`
            }
          >
            {/* Map panel — split view. Desktop: left of the rail. Phones: on
                top, above the planner sheet, while a trip is being planned. */}
            {viewMode === 'split' && (
              <div
                className={
                  mobileTripMap
                    ? 'absolute inset-0 overflow-hidden bg-[#dfe6ee]'
                    : 'relative hidden flex-none overflow-hidden md:block md:h-auto md:flex-1'
                }
                data-testid="explorer-map"
              >
                {/* Phones: full height, behind the sheet. */}
                <div className="absolute inset-0">
                  {takenRoute && takenRoute.key === stopIdsKey && (
                    <TakenRouteCover stops={tripStops} route={takenRoute.route} ready={mapDrewKey === stopIdsKey} />
                  )}
                  {!mountSplitMap ? (
                    <MapPlaceholder onActivate={() => setMapRequested(true)} />
                  ) : (
                  <GoogleMapComponent
                    key="map-split"
                    entities={nearStartPins && isTripView ? nearStartPins : mapEntities}
                    allPoints={showAllPoints}
                    onPointsLoaded={setAllPointsTotal}
                    center={shouldShowRouteOnMap ? undefined : mapCenter}
                    zoom={shouldShowRouteOnMap ? undefined : mapZoom}
                    routeConfig={shouldShowRouteOnMap ? activeRouteConfig : null}
                    onRouteDrawn={handleRouteDrawn}
                    onEntitySelected={(entity) => {
                      handleEntityClick(entity);
                      focusEntityInList(entity);
                    }}
                    onMapChange={handleMapChange}
                    onMapReady={(map) => {
                      mapInstanceRef.current = map;
                      setMapReady(true);
                      // Region search waiting for a map, else fit the country.
                      applyInitialMapFrame(map, 40);
                    }}
                    enableClustering={mapViewMode !== 'trip'}
                    useBoundsLoading={searchAsMove && mapViewMode !== 'trip'}
                    boundsSearchFilters={boundsSearchFilters}
                    showCampsites={true}
                    onAddTripStop={handleAddEntityToTrip}
                    tripStopEntityIds={hasTripStops ? tripEntityIds : undefined}
                    onEntitiesLoaded={handleBoundsEntitiesLoaded}
                    availabilityData={availabilityActive ? availabilityData : undefined}
                    regionBoundary={regionBoundary}
                    regionBoundarySource={regionBoundarySource}
                    routePathOverride={routePathOverride}
                    routeAnchors={nightMarkers}
                    tripStops={tripStopsForMap}
                    highlightStopId={highlightStopId}
                    routeDragEnabled={mapViewMode === 'trip'}
                    legPins={legPinProps}
                    legPath={openLegPath}
                    hoveredLegPinId={hoveredLegCampId}
                    onLegPinHover={setHoveredLegCampId}
                    selectedLegPinId={selectedLegCampId}
                    onLegPinClick={setSelectedLegCampId}
                    onStopDragged={handleStopDragged}
                    onRouteViaDragged={handleRouteViaDragged}
                    onTripStopClick={handleTripStopClick}
                    onRouteLegs={handleRouteLegs}
                    dropPinMode={addWaypointMode}
                    routeSummary={mapRouteSummary}
                    // The phone's small map leaves the totals to the sheet.
                    showSummaryChip={!mobileTripMap}
                    // The planner's header beside this map prints the totals;
                    // the chip keeps the roads and the day key.
                    summaryChipTotals={false}
                    selectedStopId={selectedStopId}
                    highlightedStopId={hoveredStopId}
                    highlightedLegToId={hoveredLegToId}
                    onStopSelect={handleSelectStop}
                    onStopHover={setHoveredStopId}
                    minimalChrome={mobileTripMap}
                    dropPin={hasTripStops && mapViewMode === 'trip' ? { active: addWaypointMode, onToggle: () => setAddWaypointMode((v) => !v) } : undefined}
                    // Phones: the sheet covers the bottom of the map. Controls
                    // ride its top edge (--tp-sheet-h) and the trip is framed
                    // in what is left.
                    bottomInset={mobileTripMap ? sheetInset : undefined}
                    // The wide corridor only while camps along the route are
                    // on show; the trip view gets a crisp line.
                    showCorridor={mapViewMode === 'search' && isRouteSearch}
                    // The view switch's "Map" is the full-screen map.
                    fullscreenControl={false}
                    topLeftSlot={renderMapTopChips()}
                    topLeftLead={renderMapBack()}
                    routeChips={hasTripStops && mapViewMode === 'trip' ? ['fuel', 'dumppoints'] : undefined}
                    layersLegend={
                      mobileTripMap && hasTripStops && mapViewMode === 'trip' ? (
                        <RoleLegend stops={tripStops} variant="inline" days={tripDayCount} />
                      ) : undefined
                    }
                    // The browse filter (All / Campsites / Free camps…) is not
                    // a planner control; the planner's chips sit top left.
                    typeChips={!plannerQuiet && !hasTripStops}
                    typeFilter={mapTypeFilter}
                    onTypeFilterChange={setMapTypeFilter}
                    // The planner's map: a quieter basemap so the route and
                    // pins carry the colour.
                    mutedBasemap={(hasTripStops && mapViewMode === 'trip') || plannerQuiet}
                    // Empty planner: the popular trips drawn on the map; the
                    // card under the pointer lights its line up.
                    quiet={plannerQuiet}
                    previewRoutes={previewRoutes}
                    highlightPreviewId={plannerQuiet ? previewRouteId : null}
                  />
                  )}
                </div>

                {/* The result count is said once, in the bar above the map. */}

                {/* Desktop: the key to the pin colours, bottom left over the
                    map, clear of Google's logo. Once the trip is in days the
                    pins take their day's colour instead, and the itinerary's
                    day bands are the key. */}
                {hasTripStops && mapViewMode === 'trip' && !mobileTripMap && !tripStopDays && (
                  <RoleLegend
                    stops={tripStops}
                    className="pointer-events-auto absolute bottom-[5.25rem] left-3 z-10 hidden md:flex"
                  />
                )}
                {/* Phones: the key lives in the Layers panel (layersLegend),
                    not on the map, where it covered labels and only repeated
                    what the letters and the list say. */}

                {/* Drop-a-pin guidance — the mode is armed from the side panel,
                    so without an on-map cue users don't know the map is now a
                    target ("how do I drop a pin?"). */}
                {addWaypointMode && !mobileTripMap && (
                  <div className="pointer-events-none absolute bottom-16 left-1/2 z-20 -translate-x-1/2 whitespace-nowrap rounded-full bg-tp-ok px-4 py-2 text-[13px] font-semibold text-white shadow-tp-pop animate-tp-enter motion-reduce:animate-none md:bottom-auto md:top-16">
                    {isDesktopViewport ? 'Click anywhere on the map to add a stop · Esc to cancel' : 'Tap the map to add a stop'}
                  </div>
                )}
                {/* Phones, pin armed: a crosshair in the middle of the map you
                    can see (move the map under it, then "Place at the
                    crosshair": one thumb, exact), or just tap the map. The
                    sheet's header says where the stop would go. */}
                {addWaypointMode && mobileTripMap && (
                  <>
                    {/* The crosshair shows only once the map has settled
                        after the sheet drops (never over a half-drawn,
                        mid-zoom map). White halo and a soft shadow, so it
                        reads on any basemap label. */}
                    <div
                      aria-hidden="true"
                      className={`pointer-events-none absolute left-1/2 z-20 transition-opacity duration-150 ease-tp-out ${pinReady ? 'opacity-100' : 'opacity-0'}`}
                      style={{ top: `calc(${PIN_AIM_TOP / 2}px + (100% - var(--tp-sheet-h, 0px) - ${PIN_AIM_BANNER}px) / 2)` }}
                      data-testid="pin-aim"
                      data-ready={pinReady ? 'true' : 'false'}
                    >
                      {/* A slow ripple: the map is waiting for a tap, nothing
                          is placed yet. */}
                      <span
                        className="absolute -ml-5 -mt-5 h-10 w-10 rounded-full border-2 border-solid border-tp-brand/60 motion-safe:animate-ping"
                        style={{ animationDuration: '1.6s' }}
                      />
                      <svg viewBox="0 0 48 48" className="relative -ml-6 -mt-6 block h-12 w-12 [filter:drop-shadow(0_2px_3px_rgba(22,35,58,0.35))_drop-shadow(0_0_1px_rgba(22,35,58,0.4))]">
                        <circle cx="24" cy="24" r="13" fill="rgba(255,255,255,0.28)" stroke="#fff" strokeWidth="5" />
                        <circle cx="24" cy="24" r="13" fill="none" stroke="#1558b0" strokeWidth="2.2" />
                        <path d="M24 3v9M24 36v9M3 24h9M36 24h9" stroke="#fff" strokeWidth="5" strokeLinecap="round" />
                        <path d="M24 4v8M24 36v8M4 24h8M36 24h8" stroke="#1558b0" strokeWidth="2.2" strokeLinecap="round" />
                        <circle cx="24" cy="24" r="3.4" fill="#1558b0" stroke="#fff" strokeWidth="2" />
                      </svg>
                    </div>
                  </>
                )}
                {/* "Place here": a pin drops into the crosshair's spot. */}
                {pinDrop && (
                  <svg
                    key={pinDrop.key}
                    aria-hidden="true"
                    viewBox="0 0 30 40"
                    width="30"
                    height="40"
                    className="tp-pin-drop pointer-events-none absolute z-30 [filter:drop-shadow(0_3px_4px_rgba(22,35,58,0.35))]"
                    style={{ left: pinDrop.x - 15, top: pinDrop.y - 40 }}
                  >
                    <path d="M15 39c-.6 0-1.1-.3-1.4-.8C9 31.6 2 23.4 2 14.5 2 7 7.8 1.5 15 1.5S28 7 28 14.5c0 8.9-7 17.1-11.6 23.7-.3.5-.8.8-1.4.8z" fill="#1558b0" stroke="#fff" strokeWidth="2.5" />
                    <circle cx="15" cy="14.5" r="4" fill="#fff" />
                  </svg>
                )}
                {renderMapPinCue()}
                {/* Empty planner on a phone: a soft fog over the top of the
                    map. A raster map frames the continent with a strip to
                    spare, and that strip (the Arafura Sea, PNG's names)
                    fades under the chips instead of competing with the
                    routes. */}
                {emptySheetPeek && (
                  <div
                    aria-hidden="true"
                    className="tp-map-top-fog pointer-events-none absolute inset-x-0 top-0 z-[5] h-[100px] bg-[linear-gradient(180deg,rgba(238,242,246,0.96)_0%,rgba(238,242,246,0.75)_50%,rgba(238,242,246,0)_100%)]"
                    data-testid="map-top-fog"
                  />
                )}
              </div>
            )}

            {/* List panel — right side in split, full width in list mode.
                Phones while planning: inside the bottom sheet. */}
            <MaybeSheet
              on={mobileTripMap}
              snap={sheetSnap}
              onSnap={setSheetSnap}
              onInsetChange={setSheetInset}
              // The empty planner's peek carries the start field and a row
              // of real trips to take.
              peekHeight={!hasTripStops && popularRoutes && popularRoutes.length > 0 ? EMPTY_PEEK_HEIGHT : PEEK_HEIGHT}
            >
            {/* Empty planner at peek: the task in one line and one field.
                Tapping it lifts the sheet and puts the cursor in the real
                start field (no keyboard over the map on arrival). */}
            {emptySheetPeek && (
              <div className="flex-none pb-4 pt-0.5" data-testid="planner-sheet-peek-empty">
                <div className="px-5">
                {/* The task, then ONE field: the hero. Picking the start on
                    the map is the field's trailing action, the same "Pick on
                    map" as in every other state. */}
                <p className="ca-sheet-title m-0 text-[18px] font-bold leading-6 tracking-[-0.01em] text-tp-ink">Plan a road trip</p>
                <div className="mt-3 flex h-12 items-center gap-2 rounded-tp-lg border border-solid border-tp-line-strong bg-white pl-4 pr-1.5 shadow-[0_1px_2px_rgba(22,35,58,0.06)] transition-colors duration-150 ease-tp-out focus-within:border-tp-brand focus-within:ring-2 focus-within:ring-tp-brand/20">
                  <button
                    type="button"
                    onClick={() => {
                      setSheetSnap('half');
                      window.setTimeout(() => {
                        document.querySelector<HTMLInputElement>('[data-testid="planner-start"] input')?.focus();
                      }, 340);
                    }}
                    className="flex h-full min-h-0 min-w-0 flex-1 items-center gap-3 border-0 bg-transparent p-0 text-left text-[16px] text-tp-muted focus-visible:outline-none"
                    data-testid="planner-sheet-start"
                  >
                    <IconSearch className="h-[18px] w-[18px] flex-none text-tp-brand" aria-hidden="true" />
                    <span className="truncate">Where from?</span>
                  </button>
                  <PickOnMapButton onClick={() => setAddWaypointMode(true)} />
                </div>
                </div>
                {/* Or take a real trip: the ones drawn on the map above, one
                    tap each. The row scrolls sideways and fades at the edge. */}
                {popularRoutes && popularRoutes.length > 0 && (
                  <div className="mt-3 flex items-start gap-2 px-5">
                  {/* Only whole chips: the row wraps and shows its first line,
                      so no trip name is ever cut. "All trips" opens the rest. */}
                  <ul
                    aria-label="Popular road trips"
                    className="m-0 flex h-9 min-w-0 flex-1 list-none flex-wrap gap-2 overflow-hidden p-0"
                    data-testid="planner-sheet-popular"
                  >
                    {/* Back to the last trip you saved, first in the row. */}
                    {(() => {
                      const last = [...savedTrips]
                        .filter((t) => (t.stops?.length ?? 0) >= 2)
                        .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))[0];
                      return last ? (
                        <li className="flex-none">
                          <button
                            type="button"
                            onClick={() => void handleLoadTrip(last.id)}
                            aria-label={`Continue your trip: ${last.name}`}
                            className="flex h-9 min-h-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-solid border-tp-brand/25 bg-tp-brand-soft px-3 text-[13px] font-semibold text-tp-ink transition-colors duration-150 ease-tp-out hover:border-tp-brand/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
                            data-testid="planner-sheet-continue"
                          >
                            <IconRoute className="h-4 w-4 flex-none text-tp-brand" aria-hidden="true" />
                            <span className="font-medium text-[#3a4a63]">Continue</span>
                            <span className="max-w-[180px] truncate">{last.name}</span>
                          </button>
                        </li>
                      ) : null;
                    })()}
                    {popularRoutes.slice(0, 6).map((r) => {
                      const f = routeTripFacts(r);
                      const days = f.days === null ? '' : f.days <= 1 ? 'Day trip' : `${f.days} days`;
                      return (
                        <li key={r.id} className="flex-none">
                          <button
                            type="button"
                            onClick={() => void handleLoadPopularTrip(r)}
                            disabled={popularLoadingId === r.id}
                            aria-label={`Take the ${r.name} trip${days ? `, ${days}` : ''}`}
                            className="flex h-9 min-h-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-solid border-tp-line bg-white px-3 text-[13px] font-semibold text-tp-ink transition-colors duration-150 ease-tp-out hover:border-tp-brand/40 hover:bg-tp-surface disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
                          >
                            <IconRoute className="h-4 w-4 flex-none text-tp-brand" aria-hidden="true" />
                            {r.name}
                            {days && <span className="font-medium text-tp-muted">· {days}</span>}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <button
                    type="button"
                    onClick={() => {
                      openPopularPicker();
                      setSheetSnap('full');
                    }}
                    className="flex h-9 min-h-0 flex-none items-center gap-0.5 whitespace-nowrap rounded-full border-0 bg-transparent pl-1 pr-0 text-[13px] font-semibold text-tp-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
                    data-testid="planner-sheet-all-trips"
                  >
                    All trips
                    <IconChevronRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                  </div>
                )}
              </div>
            )}
            <div
              ref={sidebarRef}
              // The Swap sheet lays itself over this column on desktop.
              data-planner-column
              className={`${plannerFills ? 'flex flex-col overflow-hidden bg-white' : 'overflow-y-auto bg-gray-50'} ${
                viewMode === 'split'
                  ? 'min-h-0 w-full flex-1 md:w-[440px] md:flex-none md:border-0 md:border-l md:border-solid md:border-gray-200'
                  : 'w-full'
              } ${mobileTripMap ? '!bg-white' : ''} ${emptySheetPeek ? 'hidden' : ''}`}
            >
              {/* Trip open with stops: the planner owns the top of the
                  column and its body is the ONE scroll container (header,
                  add-stop box and actions stay put). Results scroll in their
                  own strip below, so loading results can never slide a card
                  under the cursor mid-click. */}
              {plannerFills && (
                <div
                  // Fills the rail when the results strip is hidden, so the
                  // footer sits on the bottom edge like an app's toolbar.
                  className={`flex min-h-0 flex-col ${
                    mobileTripMap || (mapViewMode === 'trip' && !isRouteSearch) ? 'flex-1' : 'flex-[0_1_auto]'
                  }`}
                  data-testid="trip-planner-panel"
                >
                  {renderTripPlanner(true, mobileTripMap ? 'h-full min-h-0' : 'min-h-0 flex-1', mobileTripMap)}
                </div>
              )}
              <div
                ref={resultsScrollRef}
                // With a trip open, the planner owns the column. Results only
                // share it when they are about the trip (campsites along the
                // route) or the map is showing campsites.
                className={
                  plannerFills
                    ? mobileTripMap || (mapViewMode === 'trip' && !isRouteSearch)
                      ? 'hidden'
                      : 'min-h-[128px] flex-1 overflow-y-auto border-0 border-t border-solid border-tp-line'
                    : ''
                }
                data-testid="explorer-results"
              >
              <div className={`p-3 space-y-2 ${viewMode === 'list' ? 'max-w-7xl mx-auto px-4' : ''}`}>
                {/* Collapsed or empty: content-height, pinned to the top of
                    the results column on desktop. */}
                {/* ca_preview, desktop browsing: the hero's "Plan a road trip"
                    is the way in; no empty planner accordion above results. */}
                {!plannerFills && !(caPreview && isDesktopViewport && !mobileTripMap && !hasTripStops && !planningBar) && (
                  <div
                    className={
                      mobileTripMap
                        ? '-mx-3 -mt-3 mb-1 border-0 border-b border-solid border-tp-line'
                        : 'md:sticky md:top-0 md:z-10 md:-mx-3 md:-mt-3 md:bg-gray-50 md:px-3 md:pt-3'
                    }
                    data-testid="trip-planner-panel"
                  >
                    {renderTripPlanner(false, mobileTripMap ? '' : 'mb-2')}
                  </div>
                )}
                {/* Campervan-hire cross-sell — AU operators only (brand flag).
                    Without the gate the "across Australia" panel leaked onto
                    escapeto's non-AU pages. */}
                {/* Clear air between the planner and the hire card, so a
                    click a few px off the planner can't land on an ad that
                    navigates away. */}
                {/* Campervan hire sits below the first few results (see the
                    list), never between the planner and the campsites. */}
                {viewMode === 'list' && !listIsLoading && displayEntities.length > 0 && (
                  <div className="flex items-center justify-between py-2">
                    <h2 className="text-sm font-semibold text-gray-800">{searchPanelTitle}</h2>
                    <span className="text-xs text-gray-500">{matchingTotal != null && !typeFilterActive
                      ? `Showing ${displayEntities.length.toLocaleString('en-AU')} of ${matchingTotal.toLocaleString('en-AU')}`
                      : <>{displayEntities.length} {(typeFilterActive && mapTypeNoun(mapTypeFilter, displayEntities.length)) || (selectedType === 'Accommodation' ? 'accommodation' : selectedType !== 'All' ? selectedType.toLowerCase() : 'results')}{matchingTotal != null && countNote ? ` ${countNote}` : ''}</>}</span>
                  </div>
                )}
                {/* Under the empty planner the results are a labelled shelf
                    ("Campsites in view"), and nothing at all while they load:
                    unlabelled shimmer there read as the planner loading. */}
                {plannerQuiet && !listIsLoading && displayEntities.length > 0 && (
                  <div className="flex items-baseline justify-between px-1 pt-2" data-testid="planner-camps-heading">
                    <h2 className="m-0 text-tp-base font-semibold text-tp-ink">Campsites in view</h2>
                    <span className="text-tp-xs text-tp-muted">Tap + Trip to start from one</span>
                  </div>
                )}
                {plannerQuiet && (listIsLoading || displayEntities.length === 0) ? null : listIsLoading ? (
                  <div className={`grid ${viewMode === 'list' ? 'gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'gap-2 grid-cols-1'}`}>
                    {Array.from({ length: 8 }).map((_, i) =>
                      viewMode === 'list' ? (
                        <div key={i} className="animate-pulse rounded-lg border border-gray-200 bg-white overflow-hidden">
                          <div className="aspect-[16/9] bg-gray-200" />
                          <div className="p-2.5 space-y-2">
                            <div className="h-3 bg-gray-200 rounded w-3/4" />
                            <div className="h-2.5 bg-gray-100 rounded w-1/2" />
                          </div>
                        </div>
                      ) : (
                        <div key={i} className="flex h-[124px] animate-pulse overflow-hidden rounded-xl border border-gray-200 bg-white">
                          <div className="w-[124px] flex-none bg-gray-200 sm:w-[136px]" />
                          <div className="flex-1 space-y-2 p-2.5">
                            <div className="h-3 w-3/4 rounded bg-gray-200" />
                            <div className="h-2.5 w-1/2 rounded bg-gray-100" />
                            <div className="h-2.5 w-2/3 rounded bg-gray-100" />
                          </div>
                        </div>
                      )
                    )}
                  </div>
                ) : displayEntities.length === 0 && typeFilterActive && unfilteredDisplayEntities.length > 0 ? (
                  <div className="p-6 text-center bg-white border border-gray-200 rounded-lg" data-testid="type-filter-empty">
                    <p className="text-gray-800 font-semibold text-sm">No {mapTypeNoun(mapTypeFilter, 0)} in these results</p>
                    <p className="text-gray-500 text-xs mt-1 mb-3">Try another type, or search a different area.</p>
                    <button
                      type="button"
                      onClick={() => setMapTypeFilter('all')}
                      className="rounded-full border border-solid border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      Show all types
                    </button>
                  </div>
                ) : displayEntities.length === 0 ? (
                  <div className="p-6 text-center bg-white border border-gray-200 rounded-lg">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-3">
                      <EnvironmentOutlined style={{ fontSize: '20px', color: '#9ca3af' }} />
                    </div>
                    <p className="text-gray-800 font-semibold text-sm">No {selectedType === 'Accommodation' ? 'accommodation' : selectedType !== 'All' ? selectedType.toLowerCase() : 'results'} found</p>
                    <p className="text-gray-500 text-xs mt-1 mb-3">Try a different location or broaden your filters</p>
                    <div className="flex flex-wrap justify-center gap-1.5">
                      {getHomeContent().emptySuggestions.map((place) => (
                        <button
                          key={place}
                          type="button"
                          onClick={() => {
                            setSearchQuery(place);
                            handleSearchRef.current?.({ location: place });
                          }}
                          className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-medium text-gray-600 transition hover:border-[#e3f2fd] hover:bg-[#e3f2fd] hover:text-[#1558b0]"
                        >
                          {place}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className={`grid ${viewMode === 'list' ? 'gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'gap-2 grid-cols-1'}`}>
                      {listSections.map((section) => (
                        <React.Fragment key={section.key}>
                          {section.title && (
                            <div
                              className={`${viewMode === 'list' ? 'col-span-full' : ''} pt-2`}
                              data-testid="route-day-section"
                            >
                              <p className="m-0 text-tp-lg font-semibold text-tp-ink">{section.title}</p>
                              {section.subtitle && (
                                <p className="m-0 text-tp-sm text-tp-muted">{section.subtitle}</p>
                              )}
                            </div>
                          )}
                          {section.entities.map((entity, entityIdx) => (
                            <div
                              key={entity.id}
                              className="relative"
                              ref={(el) => {
                                if (el) entityRefs.current.set(entity.id, el);
                                else entityRefs.current.delete(entity.id);
                              }}
                            >
                              {section.nearTonight.has(entity.id) && (
                                <span className="pointer-events-none absolute left-2 top-2 z-10 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-semibold text-white shadow">
                                  Near tonight’s stop
                                </span>
                              )}
                              <EntityCard
                                entity={entity}
                                // ca_preview: the first screenful of photos is
                                // fetched straight away, the rest on scroll.
                                eagerImage={section.key === listSections[0]?.key && entityIdx < 5}
                                isSelected={selectedEntity?.id === entity.id}
                                onClick={() => handleEntityClick(entity)}
                                onViewDetails={() => handleViewEntityDetails(entity)}
                                // Beside the map: compact image-left rows so ~4
                                // results fit in the column instead of ~1.5.
                                layout={viewMode === 'list' ? 'grid' : 'list'}
                                onAddToTrip={handleAddEntityToTrip}
                                isInTrip={tripStops.some((stop) => stop.entityId === entity.id)}
                              />
                            </div>
                          )).flatMap((card, idx) =>
                            // Campervan hire (AU brand only), after the third
                            // result: under what was searched for, not over it.
                            BRAND.showVanHire && !hasTripStops && section.key === listSections[0]?.key &&
                            idx === Math.min(2, section.entities.length - 1)
                              ? [
                                  card,
                                  <div key="van-hire" className={viewMode === 'list' ? 'col-span-full' : ''}>
                                    <VanHirePromo query={hireQuery} />
                                  </div>,
                                ]
                              : [card]
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    {!tripHasNoRouteYet && visibleCardCount < displayEntities.length && (
                      <div className="flex justify-center pb-4">
                        <button
                          type="button"
                          onClick={() =>
                            setVisibleCardCount((count) =>
                              Math.min(count + 40, displayEntities.length)
                            )
                          }
                          className="rounded-full border border-gray-300 bg-white px-4 py-1.5 text-xs font-semibold text-gray-700 shadow-sm transition hover:border-accent hover:text-[#1558b0]"
                        >
                          Show more
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              </div>
            </div>
            </MaybeSheet>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
           VIEW 3 — FULLSCREEN MAP: Map fills entire viewport, toolbar overlay
           No hero, no footer — true fullscreen like realestate.com.au
           ══════════════════════════════════════════════════════════════════ */}
      {viewMode === 'map' && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col">
          {/* Fullscreen map fills everything */}
          <div className="relative flex-1">
            <div className="absolute inset-0">
              <GoogleMapComponent
                key="map-fullscreen"
                entities={mapEntities}
                allPoints={showAllPoints}
                onPointsLoaded={setAllPointsTotal}
                typeChipsTopClass="top-20"
                typeFilter={mapTypeFilter}
                onTypeFilterChange={setMapTypeFilter}
                gestureHandling="greedy"
                center={shouldShowRouteOnMap ? undefined : mapCenter}
                zoom={shouldShowRouteOnMap ? undefined : mapZoom}
                routeConfig={shouldShowRouteOnMap ? activeRouteConfig : null}
                onRouteDrawn={handleRouteDrawn}
                onEntitySelected={(entity) => handleEntityClick(entity)}
                onMapChange={handleMapChange}
                onMapReady={(map) => {
                  mapInstanceRef.current = map;
                  setMapReady(true);
                  // An active region search outranks the country-wide default
                  // frame — this is how mobile (no split map) gets its zoom.
                  // Otherwise, if nothing has moved the camera yet, fit
                  // mainland AU + Tasmania (escapeto: the active country).
                  // Shared with the split map; see applyInitialMapFrame.
                  applyInitialMapFrame(map, 24);
                }}
                enableClustering={mapViewMode !== 'trip'}
                useBoundsLoading={searchAsMove && mapViewMode !== 'trip'}
                boundsSearchFilters={boundsSearchFilters}
                showCampsites={true}
                onAddTripStop={handleAddEntityToTrip}
                tripStopEntityIds={hasTripStops ? tripEntityIds : undefined}
                onEntitiesLoaded={handleBoundsEntitiesLoaded}
                availabilityData={availabilityActive ? availabilityData : undefined}
                regionBoundary={regionBoundary}
                regionBoundarySource={regionBoundarySource}
                routePathOverride={routePathOverride}
                routeAnchors={nightMarkers}
                tripStops={tripStopsForMap}
                highlightStopId={highlightStopId}
                routeDragEnabled={mapViewMode === 'trip'}
                legPins={legPinProps}
                legPath={openLegPath}
                hoveredLegPinId={hoveredLegCampId}
                onLegPinHover={setHoveredLegCampId}
                selectedLegPinId={selectedLegCampId}
                onLegPinClick={setSelectedLegCampId}
                onStopDragged={handleStopDragged}
                onRouteViaDragged={handleRouteViaDragged}
                onTripStopClick={handleTripStopClick}
                onRouteLegs={handleRouteLegs}
                dropPinMode={addWaypointMode}
                routeSummary={mapRouteSummary}
                selectedStopId={selectedStopId}
                highlightedStopId={hoveredStopId}
                highlightedLegToId={hoveredLegToId}
                onStopSelect={handleSelectStop}
                onStopHover={setHoveredStopId}
              />
            </div>

            {/* MapToolbar overlay — fullscreen map only */}
            <MapToolbar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onSearch={handleSearch}
              onLocationSearch={handleLocationSearch}
              locationSuggestions={options}
              selectedType={selectedType}
              onTypeChange={setSelectedType}
              selectedFeatures={selectedFeatures}
              onToggleFeature={toggleFeature}
              onClearFilters={clearFilters}
              showList={false}
              onToggleList={() => setViewMode('split')}
              searchAsMove={searchAsMove}
              onToggleSearchAsMove={() => setSearchAsMove(prev => !prev)}
              entityCount={mapBoundsEntities.length || activeMapCount}
              isSearching={isSearching}
              checkInDate={checkInDate}
              checkOutDate={checkOutDate}
              onDateChange={handleDateChange}
              availabilityActive={availabilityActive}
            />

            {/* "Update as I move" checkbox — campsites view only. Phones: it
                sits above the result count (side by side, the count pill ran
                over it and cut the label off). */}
            {/* ca_preview: the same switch pill the split map's corner uses
                (phones never mount the split map, so this is their only
                "Update as I move"). EscapeTo keeps its checkbox. */}
            {mapViewMode !== 'trip' && caPreview && (
              <button
                type="button"
                role="switch"
                aria-checked={searchAsMove}
                onClick={() => setSearchAsMove((prev) => !prev)}
                data-testid="update-as-i-move"
                className="absolute bottom-[68px] left-3 z-10 flex h-9 min-h-0 select-none items-center gap-2.5 whitespace-nowrap rounded-full border border-solid border-white/70 bg-white/95 pl-3.5 pr-2 text-[13px] font-semibold text-tp-ink shadow-tp-card backdrop-blur-sm transition-colors duration-150 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 sm:bottom-4"
              >
                Update as I move
                <span
                  aria-hidden="true"
                  className={`relative h-5 w-9 flex-none rounded-full transition-colors duration-150 ease-tp-out ${searchAsMove ? 'bg-tp-brand' : 'bg-tp-line-strong'}`}
                >
                  <span
                    className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-[0_1px_2px_rgba(22,35,58,0.3)] transition-[left] duration-150 ease-tp-out ${searchAsMove ? 'left-[18px]' : 'left-0.5'}`}
                  />
                </span>
              </button>
            )}
            {mapViewMode !== 'trip' && !caPreview && (
            <label className="absolute bottom-[68px] left-3 z-10 flex items-center gap-2 whitespace-nowrap rounded-lg border border-white/60 bg-white/95 px-3 py-2 shadow-lg backdrop-blur-sm cursor-pointer select-none sm:bottom-4">
              <input
                type="checkbox"
                checked={searchAsMove}
                onChange={() => setSearchAsMove(prev => !prev)}
                className="w-4 h-4 rounded border-gray-300 text-[#1558b0] focus:ring-accent"
              />
              <span className="text-xs font-medium text-gray-700">Update as I move</span>
            </label>
            )}

            {/* Drop-a-pin guidance (fullscreen map) — same cue as split view. */}
            {addWaypointMode && (
              <div className="pointer-events-none absolute top-3 left-1/2 z-20 -translate-x-1/2 whitespace-nowrap rounded-full bg-emerald-600/95 px-4 py-2 text-xs font-semibold text-white shadow-lg">
                Click anywhere on the map to add a stop · Esc to cancel
              </div>
            )}
            {renderMapPinCue()}

            {/* Counter — bottom center, campsites view only */}
            <div className={`absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 ${mapViewMode === 'trip' ? 'hidden' : ''}`}>
              <div className="rounded-full border border-white/60 bg-white/90 px-4 py-2 shadow-lg backdrop-blur-sm">
                <p className="text-sm font-semibold text-gray-900">
                  {!mapCountKnown ? (
                    <span className="font-medium text-gray-600">Loading campsites…</span>
                  ) : (
                    <>
                      <span className="text-[#1558b0] text-base font-bold">{shownMapCount.toLocaleString()}</span>
                      <span className="text-gray-600 ml-1">{mapCountLabel}</span>
                      {countNote && <span className="font-medium text-gray-500"> · {countNote}</span>}
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* Exit fullscreen / List button — top right area */}
            <button
              onClick={() => setViewMode('split')}
              className="absolute top-2 right-3 z-30 h-9 px-4 flex items-center gap-1.5 text-[13px] font-semibold rounded-lg bg-white shadow-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <IconSplit className="h-4 w-4" />
              Map + list
            </button>

            {/* Trip mode toggle */}
            {hasTripStops && (
              <div
                role="group"
                aria-label="Show on map"
                className="absolute top-14 left-2 z-10 flex items-center gap-0.5 rounded-full border border-solid border-white/70 bg-white/95 p-1 shadow-tp-card backdrop-blur-sm"
              >
                {(
                  [
                    { mode: 'trip', label: 'Trip', icon: <IconRoute className="h-4 w-4" /> },
                    { mode: 'search', label: 'Campsites', icon: <IconTent className="h-4 w-4" /> },
                  ] as const
                ).map((m) => (
                  <button
                    key={m.mode}
                    type="button"
                    aria-pressed={mapViewMode === m.mode}
                    onClick={() => setMapViewMode(m.mode)}
                    className={`flex h-8 min-h-0 items-center gap-1.5 rounded-full border-0 px-3 text-[13px] font-semibold transition-colors duration-150 ${
                      mapViewMode === m.mode ? 'bg-tp-brand-soft text-tp-brand' : 'bg-transparent text-tp-muted hover:text-tp-ink'
                    }`}
                  >
                    {m.icon}
                    {m.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ca_preview: the page keeps going below the map, as the store's do. */}
      {caPreview && !mobileTripMap && <ExplorerMore />}

      {/* "Which night?" — + Trip on a card or pin while a trip is under way. */}
      {nightChooserEntity && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-black/30 p-3 sm:items-center"
          onClick={() => setNightChooserEntity(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Add to your trip"
            className="w-full max-w-sm rounded-xl bg-white p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-[11px] font-bold uppercase tracking-wide text-amber-700">Add to your trip</p>
            <p className="mt-0.5 text-sm font-semibold text-stone-900">{nightChooserEntity.name}</p>
            {nightPlan && nightPlan.slots.length > 0 ? (
              <>
                <p className="mt-2 text-[12px] text-stone-500">Which night is it for?</p>
                <ul className="mt-1.5 space-y-1.5">
                  {nightPlan.slots.map((slot) => {
                    const already = slot.entityId === nightChooserEntity.id;
                    // How far this campsite is from that night's stop — so a
                    // Grampians park doesn't quietly become the Mount Gambier night.
                    const here = createTripStopFromEntity(nightChooserEntity);
                    const awayKm =
                      typeof here.lat === 'number' && typeof here.lng === 'number' &&
                      typeof slot.lat === 'number' && typeof slot.lng === 'number'
                        ? Math.round(haversineKm(here.lat, here.lng, slot.lat, slot.lng))
                        : null;
                    const date = slot.date
                      ? new Date(`${slot.date}T12:00:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })
                      : null;
                    return (
                      <li key={slot.key}>
                        <button
                          type="button"
                          disabled={already}
                          onClick={() => {
                            const s = createTripStopFromEntity(nightChooserEntity);
                            fillNightWithCamp(slot, {
                              entityId: nightChooserEntity.id,
                              name: s.name,
                              lat: s.lat,
                              lng: s.lng,
                            });
                            setNightChooserEntity(null);
                          }}
                          className="flex w-full items-center justify-between gap-2 rounded-lg border border-stone-200 px-3 py-2 text-left text-[12.5px] hover:border-amber-400 hover:bg-amber-50 disabled:cursor-default disabled:opacity-60"
                        >
                          <span className="min-w-0">
                            <span className="font-semibold text-stone-800">
                              {slot.nights > 1 ? `Nights ${slot.night}–${slot.night + slot.nights - 1}` : `Night ${slot.night}`}
                            </span>
                            {date && <span className="ml-1 text-stone-500">{date}</span>}
                            <span className="block truncate text-[11px] text-stone-500">
                              {slot.suggested ? suggestedSlotLabel(slot.name) : slot.name}
                              {awayKm !== null && (
                                <span className={awayKm > 60 ? 'text-amber-700' : ''}>
                                  {' '}· {awayKm < 1 ? '<1' : awayKm} km away
                                </span>
                              )}
                            </span>
                          </span>
                          <span className="flex-none text-[11px] font-semibold text-amber-700">
                            {already ? '✓ Booked in' : 'Stay here'}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : (
              <p className="mt-2 text-[12px] text-stone-500">
                No nights on this trip yet — add it as a stop and set its nights.
              </p>
            )}
            <div className="mt-3 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => {
                  addEntityAsStop(nightChooserEntity);
                  setNightChooserEntity(null);
                }}
                className="h-8 min-h-0 rounded-md border border-stone-200 px-3 text-[12px] font-semibold text-stone-700 hover:bg-stone-50"
              >
                Add as an extra stop
              </button>
              <button
                type="button"
                onClick={() => setNightChooserEntity(null)}
                className="h-8 min-h-0 px-2 text-[12px] font-medium text-stone-500 hover:text-stone-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trip-planner sign-in — opened from the panel's "Sign in" button and
          from any save/export attempt while logged out. */}
      <ReviewerLoginPrompt
        isOpen={tripSignInOpen}
        onClose={() => setTripSignInOpen(false)}
        title="Sign in to sync your trips"
        subtitle="Trips you’ve saved on this device move to your account and show up in the app. You can also download them as GPX or KML."
      />
    </div>
  );
}

export default function HomePage() {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <Spin size="large" />
      </div>
    }>
      <HomePageContent />
    </Suspense>
  );
}

/** One stop letter in its role colour, inline with text (20px). */
function LetterChip({ stops, index }: { stops: TripStop[]; index: number }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-flex h-5 w-5 flex-none items-center justify-center rounded-full text-[11px] font-bold ${ROLE_BADGE[roleAt(stops, index)]}`}
    >
      {stopLetter(index)}
    </span>
  );
}

/** "Goes between (B) Lakes Entrance and (C) Sydney", in today's letters. */
function PinGhostWords({ stops, index, hasPrev, hasNext }: { stops: TripStop[]; index: number; hasPrev: boolean; hasNext: boolean }) {
  const prev = hasPrev ? stops[index - 1] : null;
  const next = hasNext ? stops[index] : null;
  const name = (s: TripStop) => <span className="min-w-0 truncate font-medium text-tp-ink">{s.name}</span>;
  if (prev && next) {
    return (
      <>
        <span className="flex-none">Goes between</span>
        <LetterChip stops={stops} index={index - 1} />
        {name(prev)}
        <span className="flex-none">and</span>
        <LetterChip stops={stops} index={index} />
        {name(next)}
      </>
    );
  }
  if (prev) {
    return (
      <>
        <span className="flex-none">New end, after</span>
        <LetterChip stops={stops} index={index - 1} />
        {name(prev)}
      </>
    );
  }
  if (next) {
    return (
      <>
        <span className="flex-none">New start, before</span>
        <LetterChip stops={stops} index={index} />
        {name(next)}
      </>
    );
  }
  return null;
}

/**
 * Peek with a trip: the stops in order, each with its letter, so the lowest
 * snap previews the trip ("A Melbourne › B Lakes Entrance › C Sydney").
 * Middle stops fold into "+2" when the line runs out.
 */
function PeekStops({ stops }: { stops: TripStop[] }) {
  const shown = stops.length <= 4 ? stops.map((_, i) => i) : [0, 1, stops.length - 1];
  const hidden = stops.length - shown.length;
  return (
    <ol className="m-0 flex min-w-0 list-none items-center gap-1 overflow-hidden p-0 text-tp-sm text-tp-ink" aria-label="Stops" data-testid="trip-peek-stops">
      {shown.map((i, k) => (
        <li key={stops[i].id} className={`flex items-center gap-1 ${k === shown.length - 1 ? 'min-w-0' : 'min-w-0 flex-shrink'}`}>
          {k > 0 && (
            <span aria-hidden="true" className="flex-none text-tp-faint">
              ›
            </span>
          )}
          {k === shown.length - 1 && hidden > 0 && (
            <span className="flex-none rounded-full bg-tp-surface px-1.5 text-[11px] font-semibold tabular-nums text-[#3a4a63]">+{hidden}</span>
          )}
          {k === shown.length - 1 && hidden > 0 && (
            <span aria-hidden="true" className="flex-none text-tp-faint">
              ›
            </span>
          )}
          <LetterChip stops={stops} index={i} />
          <span className="min-w-0 truncate font-medium">{stops[i].name}</span>
        </li>
      ))}
    </ol>
  );
}

